import React, { useState, useEffect, useRef } from 'react';
import { 
  Trophy, 
  Activity, 
  Database, 
  Mail, 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  AlertCircle,
  RefreshCw,
  LogOut,
  Calendar,
  Grid,
  ChevronRight,
  ChevronLeft,
  UserCheck,
  Check,
  XCircle,
  Clock,
  ExternalLink,
  Users,
  Search,
  Filter,
  BarChart3,
  Settings,
  DollarSign,
  CreditCard,
  Eye,
  Repeat,
  Download,
  Bell,
  Globe,
  RotateCcw,
  Sliders,
  Plus,
  FileText,
  Zap,
  Edit2,
  Trash2,
  Key,
  Layers,
  ListFilter,
  Table,
  Menu,
  X,
  UserPlus,
  Shield,
  User,
  Save,
  CheckSquare,
  Send,
  AlertTriangle,
  Code,
  Unlock,
  Tag,
  Rocket
} from 'lucide-react';

interface AuthUser {
  email: string;
  name: string;
  global_role: 'site_admin' | 'user';
  isRegistered?: boolean;
}

interface PoolMembership {
  id: string;
  name: string;
  season: number;
  pool_type?: 'live' | 'simulation';
  pool_status?: 'draft' | 'active' | 'archived';
  invite_status?: 'active' | 'pending' | 'declined';
  role_in_pool: 'pool_admin' | 'pool_user';
}

interface NFLTeam {
  id: string;
  name: string;
  shortName: string;
  abbrev: string;
  logo: string;
  score?: string;
  record?: string;
  winner?: boolean;
}

interface NFLGame {
  id: string;
  week: number;
  name: string;
  shortName: string;
  date: string;
  kickoffDate?: string;
  kickoffFormatted: string;
  status: 'scheduled' | 'in_progress' | 'final';
  statusDetail: string;
  oddsDetails?: string;
  venue?: string;
  weatherDisplay?: string;
  homeScore?: number;
  awayScore?: number;
  homeTeam: NFLTeam;
  awayTeam: NFLTeam;
}

interface GridUserPick {
  user: { id: string; name: string; email: string };
  isAlive: boolean;
  eliminatedWeek: number | null;
  buybackUsedWeek?: number | null;
  buybacksUsed?: number;
  record: string;
  weeklyPicks: Record<number, { team: string; status: 'win' | 'loss' | 'pending'; buybackUsed?: boolean; isAutoPick?: boolean }>;
}

interface GridData {
  currentWeek?: number;
  usersCount: number;
  aliveCount: number;
  eliminatedCount: number;
  poolConfig?: {
    buyInCost: number;
    buyBackCost: number;
    maxBuybacks: number;
    buybackMaxWeek: number;
    pickLockRule: string;
    pickRevealRule: string;
    poolStatus: string;
    paymentInstructions?: string;
  };
  financials?: {
    totalPotValue: number;
    totalBuyInsValue: number;
    totalBuybacksValue: number;
    approvedBuybacksCount: number;
  };
  grid: GridUserPick[];
}

interface PoolSettings {
  pool_id: string;
  pool_name?: string;
  name?: string;
  pool_status: 'draft' | 'active';
  season_year: number;
  pick_lock_rule: 'thursday_kickoff' | 'saturday_kickoff' | 'sunday_early' | 'individual_kickoff';
  registration_lock_week: number;
  buy_in_cost: number;
  max_buy_backs: number;
  buy_back_max_week: number;
  buy_back_cost: number;
  pick_reveal_rule: 'pick_lock' | 'game_start' | 'tuesday_settlement' | 'as_submitted' | 'game_kickoff';
  pick_reuse_rule: 'single' | 'double' | 'double_late_season';
  reminder_email_rule: 'all_on' | 'all_off' | 'user_choice';
  unmade_pick_policy: 'policy_a_ghost' | 'policy_b_buyback' | 'policy_b_favorite';
  buy_back_scope?: 'losses_only' | 'losses_and_unmade';
  season_start_week: number;
  pick_opening_rule: 'weekly_rolling' | 'advance_multi_week';
  payment_instructions?: string;
  updated_at: number;
}

interface UserPayment {
  user_id: string;
  pool_id: string;
  name: string;
  email: string;
  buy_in_paid: number;
  buy_backs_used: number;
  buy_backs_paid: number;
  total_paid?: number;
  reminder_opt_in: number;
  timezone?: string;
  notes: string;
}

interface AllowlistUser {
  email: string;
  name: string;
  global_role?: 'site_admin' | 'user';
  account_status?: 'active' | 'invited';
}

interface AuthUser {
  id?: string;
  email: string;
  name: string;
  global_role: 'site_admin' | 'user';
}

// 3-Letter NFL Team Abbreviation Helper
const getTeamAbbrev = (teamName: string): string => {
  if (!teamName) return '—';
  const name = teamName.toLowerCase();
  if (name.includes('chiefs') || name.includes('kansas')) return 'KC';
  if (name.includes('bills') || name.includes('buffalo')) return 'BUF';
  if (name.includes('eagles') || name.includes('philadelphia')) return 'PHI';
  if (name.includes('49ers') || name.includes('san francisco')) return 'SF';
  if (name.includes('cowboys') || name.includes('dallas')) return 'DAL';
  if (name.includes('packers') || name.includes('green bay')) return 'GB';
  if (name.includes('ravens') || name.includes('baltimore')) return 'BAL';
  if (name.includes('bengals') || name.includes('cincinnati')) return 'CIN';
  if (name.includes('dolphins') || name.includes('miami')) return 'MIA';
  if (name.includes('lions') || name.includes('detroit')) return 'DET';
  if (name.includes('texans') || name.includes('houston')) return 'HOU';
  if (name.includes('jaguars') || name.includes('jacksonville')) return 'JAX';
  if (name.includes('chargers')) return 'LAC';
  if (name.includes('rams')) return 'LAR';
  if (name.includes('raiders') || name.includes('las vegas')) return 'LV';
  if (name.includes('broncos') || name.includes('denver')) return 'DEN';
  if (name.includes('jets')) return 'NYJ';
  if (name.includes('giants')) return 'NYG';
  if (name.includes('patriots') || name.includes('new england')) return 'NE';
  if (name.includes('steelers') || name.includes('pittsburgh')) return 'PIT';
  if (name.includes('browns') || name.includes('cleveland')) return 'CLE';
  if (name.includes('colts') || name.includes('indianapolis')) return 'IND';
  if (name.includes('titans') || name.includes('tennessee')) return 'TEN';
  if (name.includes('commanders') || name.includes('washington')) return 'WAS';
  if (name.includes('bears') || name.includes('chicago')) return 'CHI';
  if (name.includes('panthers') || name.includes('carolina')) return 'CAR';
  if (name.includes('saints') || name.includes('new orleans')) return 'NO';
  if (name.includes('falcons') || name.includes('atlanta')) return 'ATL';
  if (name.includes('buccaneers') || name.includes('bucs') || name.includes('tampa')) return 'TB';
  if (name.includes('cardinals') || name.includes('arizona')) return 'ARI';
  if (name.includes('seahawks') || name.includes('seattle')) return 'SEA';
  if (name.includes('vikings') || name.includes('minnesota')) return 'MIN';
  return teamName.slice(0, 3).toUpperCase();
};

interface TeamColorTheme {
  primary: string;
  bgTint: string;
  border: string;
  text: string;
}

const NFL_TEAM_THEMES: Record<string, TeamColorTheme> = {
  // NFC North
  'DET': { primary: '#0076b6', bgTint: '#ecfeff', border: '#06b6d4', text: '#0e7490' },
  'GB':  { primary: '#203731', bgTint: '#f0fdf4', border: '#22c55e', text: '#15803d' },
  'MIN': { primary: '#4f2683', bgTint: '#faf5ff', border: '#a855f7', text: '#6b21a8' },
  'CHI': { primary: '#0b162a', bgTint: '#f0f4fe', border: '#1e3a8a', text: '#1e40af' },

  // NFC East
  'DAL': { primary: '#003594', bgTint: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  'PHI': { primary: '#004c54', bgTint: '#f0fdf4', border: '#10b981', text: '#047857' },
  'NYG': { primary: '#0b2265', bgTint: '#eff6ff', border: '#2563eb', text: '#1e40af' },
  'WAS': { primary: '#5a1414', bgTint: '#fef2f2', border: '#ef4444', text: '#991b1b' },

  // NFC South
  'ATL': { primary: '#a71930', bgTint: '#fef2f2', border: '#f87171', text: '#991b1b' },
  'CAR': { primary: '#0085ca', bgTint: '#f0f9ff', border: '#38bdf8', text: '#0369a1' },
  'NO':  { primary: '#d3bc8d', bgTint: '#fffbeb', border: '#f59e0b', text: '#78350f' },
  'TB':  { primary: '#d50a0a', bgTint: '#fef2f2', border: '#ef4444', text: '#b91c1c' },

  // NFC West
  'ARI': { primary: '#97233f', bgTint: '#fef2f2', border: '#f87171', text: '#991b1b' },
  'LAR': { primary: '#003594', bgTint: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  'SF':  { primary: '#aa0000', bgTint: '#fef2f2', border: '#ef4444', text: '#991b1b' },
  'SEA': { primary: '#002244', bgTint: '#f0fdf4', border: '#84cc16', text: '#3f6212' },

  // AFC North
  'BAL': { primary: '#241773', bgTint: '#faf5ff', border: '#a855f7', text: '#6b21a8' },
  'CIN': { primary: '#fb4f14', bgTint: '#fff7ed', border: '#f97316', text: '#c2410c' },
  'CLE': { primary: '#311d00', bgTint: '#fff7ed', border: '#f97316', text: '#9a3412' },
  'PIT': { primary: '#ffb612', bgTint: '#fffbeb', border: '#f59e0b', text: '#92400e' },

  // AFC East
  'BUF': { primary: '#00338d', bgTint: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  'MIA': { primary: '#008e97', bgTint: '#f0fdfa', border: '#14b8a6', text: '#0f766e' },
  'NE':  { primary: '#002244', bgTint: '#f0f4fe', border: '#3b82f6', text: '#1e40af' },
  'NYJ': { primary: '#125740', bgTint: '#f0fdf4', border: '#16a34a', text: '#15803d' },

  // AFC South
  'HOU': { primary: '#03202f', bgTint: '#f0f4fe', border: '#3b82f6', text: '#1e40af' },
  'IND': { primary: '#002c5f', bgTint: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  'JAX': { primary: '#006778', bgTint: '#f0fdfa', border: '#06b6d4', text: '#0e7490' },
  'TEN': { primary: '#4b92db', bgTint: '#f0f9ff', border: '#38bdf8', text: '#0369a1' },

  // AFC West
  'DEN': { primary: '#fb4f14', bgTint: '#fff7ed', border: '#f97316', text: '#c2410c' },
  'KC':  { primary: '#e31837', bgTint: '#fef2f2', border: '#ef4444', text: '#b91c1c' },
  'LV':  { primary: '#000000', bgTint: '#f8fafc', border: '#64748b', text: '#0f172a' },
  'LAC': { primary: '#0080c6', bgTint: '#f0f9ff', border: '#38bdf8', text: '#0369a1' },
};

const formatCountdownTimer = (diffMs: number): string => {
  if (diffMs <= 0) return '0s';
  const d = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const h = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diffMs / (1000 * 60)) % 60);
  const s = Math.floor((diffMs / 1000) % 60);

  if (d > 0) {
    return `${d}d ${h}h ${m}m ${s}s`;
  }
  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
};

const getWeatherEmoji = (weatherStr?: string, venueStr?: string): string => {
  const w = (weatherStr || '').toLowerCase();
  const v = (venueStr || '').toLowerCase();
  if (w.includes('dome') || v.includes('dome') || v.includes('ford') || v.includes('sofi') || v.includes('at&t') || v.includes('allegiant') || v.includes('lucas oil') || v.includes('u.s. bank') || v.includes('mercedes') || v.includes('caesars') || v.includes('nrg')) {
    return '🏟️';
  }
  if (w.includes('snow') || w.includes('flurry') || w.includes('blizzard')) return '❄️';
  if (w.includes('rain') || w.includes('shower') || w.includes('drizzle') || w.includes('storm')) return '🌧️';
  if (w.includes('sun') || w.includes('clear') || w.includes('fair')) return '☀️';
  if (w.includes('cloud') || w.includes('overcast')) return '☁️';
  if (w.includes('wind')) return '💨';
  return '☀️';
};

const shortenVenueName = (venueName?: string): string => {
  if (!venueName) return 'Stadium';
  let v = venueName.trim();
  v = v.replace(/GEHA Field at /i, '')
       .replace(/Empower Field at /i, '')
       .replace(/Bank of America Stadium/i, 'Bank of America')
       .replace(/Lincoln Financial Field/i, 'Lincoln Financial')
       .replace(/Mercedes-Benz Superdome/i, 'Superdome')
       .replace(/Caesars Superdome/i, 'Superdome')
       .replace(/Mercedes-Benz Stadium/i, 'Mercedes-Benz')
       .replace(/Melbourne Cricket Ground/i, 'MCG Stadium');
  return v;
};

const getWeatherDisplayCompact = (weatherStr?: string, venueStr?: string): string => {
  const w = (weatherStr || '').trim();
  if (!w || w.includes('72° Clear')) return '';
  const match = w.match(/(\d+°(?:F|C)?)/i);
  const emoji = getWeatherEmoji(w, venueStr);
  if (match) return `${emoji} ${match[1]}`;
  if (w.toLowerCase().includes('dome')) return '🏟️ Dome';
  return `${emoji} ${w}`;
};

const formatKickoffWithTimeZone = (dateIsoOrFormatted: string, timeZone: string = 'America/New_York'): string => {
  if (!dateIsoOrFormatted) return 'TBD Kickoff';
  try {
    const dateObj = new Date(dateIsoOrFormatted);
    if (isNaN(dateObj.getTime())) return dateIsoOrFormatted;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    const parts = formatter.format(dateObj).replace(/,/g, '');
    const tzAbbrev = timeZone === 'America/Chicago' ? 'CT' : timeZone === 'America/Denver' ? 'MT' : timeZone === 'America/Los_Angeles' ? 'PT' : 'ET';
    return `${parts} ${tzAbbrev}`;
  } catch (e) {
    return dateIsoOrFormatted;
  }
};

export function formatDisplayName(fullName?: string, email?: string): string {
  if (!fullName && !email) return 'Player';

  let name = (fullName || '').trim();
  if (!name || name.includes('@')) {
    const prefix = (email || name).split('@')[0];
    const cleanPrefix = prefix.replace(/[^a-zA-Z]/g, ' ').trim();
    name = cleanPrefix || (email ? email.split('@')[0] : 'Player');
  }

  const alphaParts = name.split(/\s+/).map((p) => p.replace(/[^a-zA-Z]/g, '')).filter(Boolean);
  if (alphaParts.length === 0) {
    return (fullName || email || 'Player').trim();
  }

  if (alphaParts.length === 1) {
    return alphaParts[0].charAt(0).toUpperCase() + alphaParts[0].slice(1).toLowerCase();
  }

  const firstName = alphaParts[0].charAt(0).toUpperCase() + alphaParts[0].slice(1).toLowerCase();
  const lastInitial = alphaParts[alphaParts.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
}

export default function App() {
  // Authentication & Auth Gating States
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [userPools, setUserPools] = useState<PoolMembership[]>([]);
  const [activePool, setActivePool] = useState<PoolMembership | null>(null);

  // Login Form & Telemetry States
  const [loginEmail, setLoginEmail] = useState<string>('');
  const [sendingMagicLink, setSendingMagicLink] = useState<boolean>(false);
  const [linkSentStatus, setLinkSentStatus] = useState<boolean>(false);

  // Verify Magic Link State
  const [verifyingToken, setVerifyingToken] = useState<boolean>(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Pending Pool Invites State
  const [pendingInvitePools, setPendingInvitePools] = useState<any[]>([]);
  const [respondingInvite, setRespondingInvite] = useState<boolean>(false);

  // ── 3 Primary Portals Navigation Architecture ──────────────────────────────
  const [primaryPortal, setPrimaryPortal] = useState<'user' | 'pool_admin' | 'site_admin'>('user');
  const [userSubTab, setUserSubTab] = useState<'pick_selection' | 'group_picks' | 'user_settings'>('pick_selection');
  const [poolAdminSubTab, setPoolAdminSubTab] = useState<'rules_setup' | 'payment_ledger' | 'schedule_control'>('rules_setup');
  const [siteAdminSubTab, setSiteAdminSubTab] = useState<'user_directory' | 'pool_management' | 'schedule_engine' | 'db_explorer' | 'diagnostics'>('user_directory');

  // DB Explorer & SQL Console State
  const [dbTables, setDbTables] = useState<Array<{ name: string; rowCount: number }>>([]);
  const [selectedDbTable, setSelectedDbTable] = useState<string>('user_payments');
  const [dbTableData, setDbTableData] = useState<{ table: string; totalRows: number; columns: string[]; rows: any[] }>({
    table: 'user_payments',
    totalRows: 0,
    columns: [],
    rows: [],
  });
  const [loadingTableData, setLoadingTableData] = useState<boolean>(false);
  const [dbTableFilter, setDbTableFilter] = useState<string>('');
  const [dbTablePage, setDbTablePage] = useState<number>(1);
  const [dbTableLimit, setDbTableLimit] = useState<number>(50);

  // Read-Only SQL Console State
  const [sqlQuery, setSqlQuery] = useState<string>('SELECT * FROM user_payments LIMIT 50;');
  const [executingSql, setExecutingSql] = useState<boolean>(false);
  const [sqlResult, setSqlResult] = useState<{ columns: string[]; rows: any[]; rowCount: number; durationMs: number; error?: string } | null>(null);
  
  // Hamburger Drawer Menu State
  const [hamburgerOpen, setHamburgerOpen] = useState<boolean>(false);

  // Create New Pool Modal & Simulator States
  const [showCreatePoolModal, setShowCreatePoolModal] = useState<boolean>(false);
  const [showScheduleSyncModal, setShowScheduleSyncModal] = useState<boolean>(false);
  const [showPoolLaunchModal, setShowPoolLaunchModal] = useState<boolean>(false);
  const [syncNotifyEmail, setSyncNotifyEmail] = useState<boolean>(true);
  const [simTimelineIndex, setSimTimelineIndex] = useState<number>(0);
  const [simEngineMode, setSimEngineMode] = useState<'scripted' | 'random'>('scripted');
  const [showDeletePoolModal, setShowDeletePoolModal] = useState<boolean>(false);
  const [deleteFailsafeChecked, setDeleteFailsafeChecked] = useState<boolean>(false);
  const [isDeletingPool, setIsDeletingPool] = useState<boolean>(false);
  const [newPoolName, setNewPoolName] = useState<string>('');
  const [newPoolSeason, setNewPoolSeason] = useState<number>(() => {
    const d = new Date();
    return d.getUTCMonth() < 2 ? d.getUTCFullYear() - 1 : d.getUTCFullYear();
  });
  const [newPoolType, setNewPoolType] = useState<'live' | 'simulation'>('live');
  const [newPoolBuyIn, setNewPoolBuyIn] = useState<number>(100);
  const [addJasonJohn, setAddJasonJohn] = useState<boolean>(true);
  const [isCreatingPool, setIsCreatingPool] = useState<boolean>(false);

  const [selectedRulePreset, setSelectedRulePreset] = useState<'strict_traditional' | 'flexible_late' | 'auto_favorite'>('strict_traditional');
  const [newPoolBuyBackCost, setNewPoolBuyBackCost] = useState<number>(100);
  const [newPoolMaxBuybacks, setNewPoolMaxBuybacks] = useState<number>(1);
  const [newPoolBuybackMaxWeek, setNewPoolBuybackMaxWeek] = useState<number>(3);
  const [newPoolPickLockRule, setNewPoolPickLockRule] = useState<'individual_kickoff' | 'thursday_kickoff'>('thursday_kickoff');
  const [newPoolPickOpeningRule, setNewPoolPickOpeningRule] = useState<'all_weeks_open' | 'weekly_rolling'>('weekly_rolling');
  const [newPoolUnmadePickPolicy, setNewPoolUnmadePickPolicy] = useState<string>('policy_a_ghost');
  const [newPoolRegLockWeek, setNewPoolRegLockWeek] = useState<number>(1);
  const [newPoolBuyBackScope, setNewPoolBuyBackScope] = useState<string>('losses_only');
  const [newPoolPaymentInstructions, setNewPoolPaymentInstructions] = useState<string>('');
  const [sendingNudge, setSendingNudge] = useState<boolean>(false);

  const handleSendNudge = async (targetEmail?: string) => {
    setSendingNudge(true);
    try {
      const res = await fetch('/api/admin/payments/nudge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId: activePool?.id, targetEmail })
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: data.message || `Payment reminders sent successfully!` });
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to send payment reminders.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: e.message });
    } finally {
      setSendingNudge(false);
    }
  };

  const applyRulePreset = (preset: 'strict_traditional' | 'flexible_late' | 'auto_favorite') => {
    setSelectedRulePreset(preset);
    if (preset === 'strict_traditional') {
      setNewPoolBuyIn(100);
      setNewPoolBuyBackCost(100);
      setNewPoolMaxBuybacks(1);
      setNewPoolBuybackMaxWeek(3);
      setNewPoolPickLockRule('thursday_kickoff');
      setNewPoolPickOpeningRule('weekly_rolling');
      setNewPoolUnmadePickPolicy('policy_a_ghost');
      setNewPoolRegLockWeek(1);
      setNewPoolBuyBackScope('losses_only');
    } else if (preset === 'flexible_late') {
      setNewPoolBuyIn(100);
      setNewPoolBuyBackCost(100);
      setNewPoolMaxBuybacks(1);
      setNewPoolBuybackMaxWeek(3);
      setNewPoolPickLockRule('individual_kickoff');
      setNewPoolPickOpeningRule('all_weeks_open');
      setNewPoolUnmadePickPolicy('policy_b_buyback');
      setNewPoolRegLockWeek(2);
      setNewPoolBuyBackScope('losses_and_unmade');
    } else if (preset === 'auto_favorite') {
      setNewPoolBuyIn(100);
      setNewPoolBuyBackCost(100);
      setNewPoolMaxBuybacks(1);
      setNewPoolBuybackMaxWeek(3);
      setNewPoolPickLockRule('individual_kickoff');
      setNewPoolPickOpeningRule('all_weeks_open');
      setNewPoolUnmadePickPolicy('policy_b_favorite');
      setNewPoolRegLockWeek(1);
      setNewPoolBuyBackScope('losses_only');
    }
  };

  const [simTargetWeek, setSimTargetWeek] = useState<number>(1);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);
  const [simMessage, setSimMessage] = useState<string>('');

  const [activeWeek, setActiveWeek] = useState<number>(1);
  const [standingsViewMode, setStandingsViewMode] = useState<'summary' | 'detailed'>('detailed');

  // Pool Data States
  const [games, setGames] = useState<NFLGame[]>([]);
  const [allSchedulesMap, setAllSchedulesMap] = useState<Record<number, NFLGame[]>>({});
  const [cardOrderIds, setCardOrderIds] = useState<string[]>([]);
  const [loadingGames, setLoadingGames] = useState<boolean>(false);
  const [gridData, setGridData] = useState<GridData | null>(null);
  const [loadingGrid, setLoadingGrid] = useState<boolean>(false);
  const [espnSource, setEspnSource] = useState<string>('espn_live');
  const [currentTime, setCurrentTime] = useState<number>(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // User Settings State
  const [userReminderOptIn, setUserReminderOptIn] = useState<boolean>(true);
  const [userBuybackNotifyOptIn, setUserBuybackNotifyOptIn] = useState<boolean>(true);
  const [userRevealNotifyOptIn, setUserRevealNotifyOptIn] = useState<boolean>(true);
  const [userTimeZone, setUserTimeZone] = useState<string>('America/New_York');
  const [savingUserSettings, setSavingUserSettings] = useState<boolean>(false);

  // Admin Name Editing State
  const [editingUserName, setEditingUserName] = useState<{ userId: string; email: string; name: string } | null>(null);
  const [newNameInput, setNewNameInput] = useState<string>('');
  const [savingNewName, setSavingNewName] = useState<boolean>(false);

  // Admin Settings & Payments
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [adminSettings, setAdminSettings] = useState<PoolSettings>({
    pool_id: 'pool_2026_cote',
    pool_status: 'draft',
    season_year: 2026,
    pick_lock_rule: 'thursday_kickoff',
    registration_lock_week: 1,
    buy_in_cost: 100.0,
    max_buy_backs: 1,
    buy_back_max_week: 3,
    buy_back_cost: 100.0,
    pick_reveal_rule: 'pick_lock',
    pick_reuse_rule: 'single',
    reminder_email_rule: 'user_choice',
    unmade_pick_policy: 'policy_a_ghost',
    buy_back_scope: 'losses_only',
    season_start_week: 1,
    pick_opening_rule: 'weekly_rolling',
    updated_at: Date.now(),
  });

  const handleRegLockWeekChange = (newRegWeek: number) => {
    if (newRegWeek > 1) {
      const newPolicy = adminSettings.unmade_pick_policy === 'policy_a_ghost' ? 'policy_b_buyback' : adminSettings.unmade_pick_policy;
      setAdminSettings({
        ...adminSettings,
        registration_lock_week: newRegWeek,
        unmade_pick_policy: newPolicy,
        buy_back_scope: 'losses_and_unmade'
      });
    } else {
      setAdminSettings({ ...adminSettings, registration_lock_week: newRegWeek });
    }
  };

  const handleUnmadePolicyChange = (newPolicy: any) => {
    if (newPolicy === 'policy_a_ghost') {
      setAdminSettings({
        ...adminSettings,
        unmade_pick_policy: newPolicy,
        registration_lock_week: 1,
        buy_back_scope: 'losses_only'
      });
    } else if (newPolicy === 'policy_b_buyback') {
      setAdminSettings({
        ...adminSettings,
        unmade_pick_policy: newPolicy,
        buy_back_scope: 'losses_and_unmade'
      });
    } else {
      setAdminSettings({
        ...adminSettings,
        unmade_pick_policy: newPolicy
      });
    }
  };
  const [userPayments, setUserPayments] = useState<UserPayment[]>([]);
  const [savingAdminSettings, setSavingAdminSettings] = useState<boolean>(false);
  const [launchingPool, setLaunchingPool] = useState<boolean>(false);

  // Batch Invites & Buyback Request States
  const [inviteInputText, setInviteInputText] = useState<string>('');
  const [sendingInvites, setSendingInvites] = useState<boolean>(false);
  const [invitedList, setInvitedList] = useState<Array<{ email: string; name: string; magicLink: string; emailSent: boolean }>>([]);
  const [requestingBuyback, setRequestingBuyback] = useState<boolean>(false);

  // Site Admin Allowlist State
  const [allowlistUsers, setAllowlistUsers] = useState<AllowlistUser[]>([]);
  const [newAllowlistEmail, setNewAllowlistEmail] = useState<string>('');
  const [newAllowlistName, setNewAllowlistName] = useState<string>('');
  const [newAllowlistRole, setNewAllowlistRole] = useState<'site_admin' | 'user'>('user');
  const [addingAllowlist, setAddingAllowlist] = useState<boolean>(false);
  const [deletingUser, setDeletingUser] = useState<{ email: string; name: string } | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState<boolean>(false);
  const [notifyUserOnDelete, setNotifyUserOnDelete] = useState<boolean>(true);
  const [adminBuybacks, setAdminBuybacks] = useState<any[]>([]);

  // UI Toast
  const [submittingPick, setSubmittingPick] = useState<boolean>(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Grid Filter
  const [gridFilterStatus, setGridFilterStatus] = useState<'all' | 'alive' | 'eliminated'>('all');
  const [gridSearch, setGridSearch] = useState<string>('');

  // Diagnostics & Schedule Sync
  const [health, setHealth] = useState<any>(null);
  const [loadingHealth, setLoadingHealth] = useState<boolean>(false);
  const [emailTestStatus, setEmailTestStatus] = useState<string | null>(null);
  const [emailTestLoading, setEmailTestLoading] = useState<boolean>(false);
  const [dbData, setDbData] = useState<any>(null);
  const [loadingDb, setLoadingDb] = useState<boolean>(false);

  const [discrepancyData, setDiscrepancyData] = useState<{ hasDiscrepancy: boolean; count: number; list: string[] } | null>(null);
  const [checkingDiscrepancies, setCheckingDiscrepancies] = useState<boolean>(false);
  const [syncingSchedule, setSyncingSchedule] = useState<boolean>(false);

  // Admin Settings Lock & Email Diff Modal State
  const [isSettingsLocked, setIsSettingsLocked] = useState<boolean>(true);
  const [baselineSettings, setBaselineSettings] = useState<PoolSettings | null>(null);
  const [showSettingsChangeModal, setShowSettingsChangeModal] = useState<boolean>(false);
  const [settingsDiffs, setSettingsDiffs] = useState<Array<{ field: string; oldVal: string; newVal: string }>>([]);
  const [sendingDiffEmail, setSendingDiffEmail] = useState<boolean>(false);

  // Player Registration Screen State
  const [showRegistrationScreen, setShowRegistrationScreen] = useState<boolean>(false);
  const [invitesCollapsed, setInvitesCollapsed] = useState<boolean>(false);
  const [registrationForm, setRegistrationForm] = useState<{
    firstName: string;
    lastName: string;
    timeZone: string;
    paymentNote: string;
  }>({
    firstName: '',
    lastName: '',
    timeZone: 'America/New_York',
    paymentNote: '',
  });
  const [submittingRegistration, setSubmittingRegistration] = useState<boolean>(false);
  const [registrationDeclined, setRegistrationDeclined] = useState<boolean>(false);

  const [showPaymentNoteModal, setShowPaymentNoteModal] = useState<boolean>(false);
  const [paymentNoteInput, setPaymentNoteInput] = useState<string>('');
  const [savingPaymentNote, setSavingPaymentNote] = useState<boolean>(false);

  const handleSavePaymentNote = async () => {
    if (!activePool) return;
    setSavingPaymentNote(true);
    try {
      const res = await fetch('/api/user/payment-note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poolId: activePool.id,
          paymentNote: paymentNoteInput,
        }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: 'Payment note submitted to Pool Administrator!' });
        setShowPaymentNoteModal(false);
        fetchAdminData(activePool.id);
        fetchGrid(activePool.id);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to update payment note.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: 'Error connecting to server.' });
    } finally {
      setSavingPaymentNote(false);
    }
  };

  // Check Auth Status on Load
  const checkAuth = async () => {
    setCheckingAuth(true);
    try {
      const res = await fetch('/api/auth/me');
      const data: any = await res.json();

      if (data.authenticated && data.user) {
        setAuthUser(data.user);
        setUserPools(data.pools || []);
        if (data.pendingInvites) {
          setPendingInvitePools(data.pendingInvites);
        }
        if (data.pools && data.pools.length > 0) {
          setActivePool(data.pools[0]);
        }
        const isSiteAdminUser = data.user?.global_role === 'site_admin' || (data.user?.email && (data.user.email.toLowerCase() === 'jasonc1201@gmail.com' || data.user.email.toLowerCase() === 'johnrbressler@gmail.com'));
        const isAlreadyRegistered = data.user?.isRegistered || data.needsRegistration === false || isSiteAdminUser;
        if (!isAlreadyRegistered && !isSiteAdminUser) {
          setShowRegistrationScreen(true);
        } else {
          setShowRegistrationScreen(false);
        }
      } else {
        setAuthUser(null);
      }
    } catch (e) {
      console.error('Failed auth check', e);
    } finally {
      setCheckingAuth(false);
    }
  };

  // Magic Link Token Verification Handler
  const verifyMagicToken = async (token: string, email: string = '') => {
    setVerifyingToken(true);
    setVerifyError(null);
    try {
      const res = await fetch('/api/auth/magic/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setAuthUser(data.user);
        setUserPools(data.pools || []);
        if (data.pendingInvites) {
          setPendingInvitePools(data.pendingInvites);
        }
        setActivePool(data.activePool || data.pools[0]);
        window.history.replaceState({}, document.title, window.location.pathname);

        // Hardened User Lifecycle Router (5 Scenarios)
        const isSiteAdminUser = data.user?.global_role === 'site_admin' || email.toLowerCase() === 'jasonc1201@gmail.com' || email.toLowerCase() === 'johnrbressler@gmail.com';
        const isAlreadyRegistered = data.user?.isRegistered || !data.needsRegistration || isSiteAdminUser;

        if (!isAlreadyRegistered && !isSiteAdminUser) {
          setShowRegistrationScreen(true);
        } else {
          setShowRegistrationScreen(false);
          if (isSiteAdminUser && (!data.pools || data.pools.length === 0)) {
            setPrimaryPortal('site_admin');
          }
        }
      } else {
        setVerifyError(data.error || 'Magic link verification failed.');
      }
    } catch (e: any) {
      setVerifyError(`Error: ${e.message}`);
    } finally {
      setVerifyingToken(false);
      setCheckingAuth(false);
    }
  };

  // Respond to Pending Pool Invitation (Accept Commitment or Decline)
  const handleRespondToInvite = async (poolId: string, action: 'accept' | 'decline') => {
    setRespondingInvite(true);
    setToast(null);
    try {
      const res = await fetch('/api/pool/invite/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId, action }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: data.message });
        setPendingInvitePools((prev) => prev.filter((p) => p.id !== poolId));
        await checkAuth();
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to update invitation status.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setRespondingInvite(false);
    }
  };

  useEffect(() => {
    // Client-side error telemetry listener
    const sendTelemetry = (errorData: any) => {
      try {
        const payload = JSON.stringify({
          ...errorData,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        });
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/client-logs', payload);
        } else {
          fetch('/api/client-logs', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload });
        }
      } catch (e) {}
    };

    const onError = (evt: ErrorEvent) => {
      sendTelemetry({ type: 'window_error', message: evt.message, filename: evt.filename, lineno: evt.lineno, colno: evt.colno, errorStack: evt.error?.stack });
    };

    const onUnhandledRejection = (evt: PromiseRejectionEvent) => {
      sendTelemetry({ type: 'unhandled_rejection', reason: evt.reason?.message || String(evt.reason), errorStack: evt.reason?.stack });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const emailParam = urlParams.get('email');

    if (tokenParam || emailParam) {
      verifyMagicToken(tokenParam || '', emailParam || '');
    } else {
      checkAuth();
    }

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  // Fetch Scoreboard & Grid when Active Pool / Week changes
  useEffect(() => {
    if (authUser && activePool) {
      fetchScoreboard(activeWeek, true);
      fetchGrid(activePool.id);
      fetchAdminData(activePool.id);
      checkDiscrepancies(activeWeek, false);
      fetchAllowlist();
    }
  }, [authUser, activePool, activeWeek]);

  const checkDiscrepancies = async (week: number, notifyAdmins: boolean = false) => {
    setCheckingDiscrepancies(true);
    try {
      const res = await fetch(`/api/admin/schedule/discrepancies?week=${week}&notify=${notifyAdmins ? '1' : '0'}`);
      const data: any = await res.json();
      setDiscrepancyData({
        hasDiscrepancy: data.hasDiscrepancy,
        count: data.discrepanciesCount,
        list: data.discrepancies || [],
      });
      if (notifyAdmins && data.hasDiscrepancy) {
        setToast({ type: 'success', text: 'Discrepancies detected! Alert email sent to pool admins.' });
      }
    } catch (e) {
      console.error('Failed discrepancy check', e);
    } finally {
      setCheckingDiscrepancies(false);
    }
  };

  const handleSyncSchedule = async (syncAll: boolean = true, notifyMembers: boolean = true) => {
    if (syncAll) setLaunchingPool(true);
    else setSyncingSchedule(true);
    setToast(null);
    try {
      // 1. First save current settings with pool_status = 'active'
      const targetPoolId = activePool?.id || adminSettings.pool_id || 'pool_2026_cote';
      await fetch('/api/admin/settings/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...adminSettings, pool_id: targetPoolId, pool_status: 'active' }),
      });

      // 2. Launch pool and sync schedule
      const res = await fetch('/api/admin/schedule/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId: targetPoolId, week: activeWeek, syncAllWeeks: syncAll, notifyMembers }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        if (syncAll) {
          setAdminSettings((prev) => ({ ...prev, pool_status: 'active' }));
          setIsSettingsLocked(true);
        }
        fetchScoreboard(activeWeek);
        checkDiscrepancies(activeWeek, false);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to sync schedule.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSyncingSchedule(false);
      setLaunchingPool(false);
      setShowScheduleSyncModal(false);
    }
  };

  const handleSendBatchInvites = async () => {
    if (!inviteInputText.trim()) return;
    setSendingInvites(true);
    setToast(null);
    try {
      const res = await fetch('/api/admin/invite/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailsText: inviteInputText, poolId: activePool?.id }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        setInvitedList((prev) => [...data.results, ...prev]);
        setInviteInputText('');
        if (activePool?.id) {
          fetchAdminData(activePool.id);
          fetchGrid(activePool.id);
        }
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to send invites.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSendingInvites(false);
    }
  };

  const handleResendInvite = async (email: string) => {
    setToast(null);
    try {
      const res = await fetch('/api/admin/invite/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, poolId: activePool?.id }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: data.message });
        setInvitedList((prev) =>
          prev.map((item) => (item.email === email ? { ...item, magicLink: data.magicLink, emailSent: data.emailSent } : item))
        );
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to resend invite.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    }
  };

  const handleDeletePendingInvite = async (email: string) => {
    setToast(null);
    try {
      const res = await fetch('/api/admin/invite/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, poolId: activePool?.id }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: data.message });
        setInvitedList((prev) => prev.filter((item) => item.email !== email));
        fetchAdminData(activePool?.id || 'pool_2026_cote');
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to delete pending invite.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    }
  };



  const handleCreatePoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPoolName.trim()) return;
    setIsCreatingPool(true);
    try {
      const res = await fetch('/api/pools/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newPoolName.trim(),
          season: newPoolSeason,
          poolType: newPoolType,
          buyInCost: newPoolBuyIn,
          buyBackCost: newPoolBuyBackCost,
          maxBuybacks: newPoolMaxBuybacks,
          buybackMaxWeek: newPoolBuybackMaxWeek,
          pickLockRule: newPoolPickLockRule,
          pickOpeningRule: newPoolPickOpeningRule,
          unmadePickPolicy: newPoolUnmadePickPolicy,
          regLockWeek: newPoolRegLockWeek,
          buyBackScope: newPoolBuyBackScope,
          paymentInstructions: newPoolPaymentInstructions,
          adminEmails: addJasonJohn ? ['jasonc1201@gmail.com', 'johnrbressler@gmail.com'] : []
        })
      });
      const data: any = await res.json();
      if (res.ok && data.success && data.pool) {
        const poolNameVal = data.pool.name || data.pool.pool_name || newPoolName.trim();
        const freshPool = { ...data.pool, name: poolNameVal, pool_name: poolNameVal };
        const freshPools = [...userPools, freshPool];
        setUserPools(freshPools);
        setActivePool(freshPool);
        setIsSettingsLocked(false);
        const newSettingsObj: PoolSettings = {
          pool_id: data.pool.id,
          pool_name: poolNameVal,
          name: poolNameVal,
          pool_status: 'draft',
          season_year: newPoolSeason,
          season_start_week: 1,
          registration_lock_week: newPoolRegLockWeek || 1,
          pick_opening_rule: (newPoolPickOpeningRule as any) || 'weekly_rolling',
          pick_lock_rule: (newPoolPickLockRule as any) || 'thursday_kickoff',
          unmade_pick_policy: (newPoolUnmadePickPolicy as any) || 'policy_a_ghost',
          buy_in_cost: typeof newPoolBuyIn === 'number' ? newPoolBuyIn : parseFloat(String(newPoolBuyIn || '100')),
          buy_back_cost: typeof newPoolBuyBackCost === 'number' ? newPoolBuyBackCost : parseFloat(String(newPoolBuyBackCost || newPoolBuyIn || '100')),
          max_buy_backs: typeof newPoolMaxBuybacks === 'number' ? newPoolMaxBuybacks : parseInt(String(newPoolMaxBuybacks || '1'), 10),
          buy_back_max_week: typeof newPoolBuybackMaxWeek === 'number' ? newPoolBuybackMaxWeek : parseInt(String(newPoolBuybackMaxWeek || '3'), 10),
          buy_back_scope: (newPoolBuyBackScope as any) || 'losses_only',
          pick_reveal_rule: 'pick_lock',
          pick_reuse_rule: 'single',
          reminder_email_rule: 'user_choice',
          payment_instructions: newPoolPaymentInstructions.trim(),
          updated_at: Date.now()
        };
        setAdminSettings(newSettingsObj);
        setBaselineSettings(newSettingsObj);
        setShowCreatePoolModal(false);
        setNewPoolName('');
        setToast({ type: 'success', text: `🎉 ${data.message || 'Pool created successfully!'}` });
        fetchAdminData(data.pool.id);
        fetchScoreboard(activeWeek);
      } else {
        alert(`❌ Failed to create pool: ${data.error || 'Unknown error'}`);
      }
    } catch (err: any) {
      alert(`❌ Error creating pool: ${err.message}`);
    } finally {
      setIsCreatingPool(false);
    }
  };

  const handleSimulateState = async (preset?: 'wed_prepick' | 'fri_post_tnf' | 'sun_in_progress' | 'tue_concluded', targetIdx?: number, mode?: 'scripted' | 'random') => {
    setIsSimulating(true);
    setSimMessage('');
    const idxToUse = targetIdx !== undefined ? targetIdx : simTimelineIndex;
    const modeToUse = mode !== undefined ? mode : simEngineMode;
    try {
      const res = await fetch('/api/admin/sim/set-state', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          poolId: activePool?.id || 'pool_2026_test',
          timelineIndex: idxToUse,
          statePreset: preset,
          engineMode: modeToUse
        })
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        if (data.timelineIndex !== undefined) setSimTimelineIndex(data.timelineIndex);
        const w = data.targetWeek || Math.floor(idxToUse / 4) + 1;
        setSimTargetWeek(w);
        setActiveWeek(w);
        setSimMessage(`✅ ${data.message}`);
        fetchAdminData(activePool?.id || 'pool_2026_test');
        fetchScoreboard(w);
      } else {
        setSimMessage(`❌ ${data.error || 'Failed to simulate state'}`);
      }
    } catch (err: any) {
      setSimMessage(`❌ ${err.message}`);
    } finally {
      setIsSimulating(false);
    }
  };

  const handleRespondPoolInvite = async (poolId: string, action: 'accept' | 'decline') => {
    try {
      const res = await fetch('/api/pool/invite/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId, action })
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: data.message });
        if (action === 'accept') {
          setUserPools((prev) => prev.map((p) => p.id === poolId ? { ...p, invite_status: 'active' } : p));
          if (activePool?.id === poolId) {
            setActivePool((prev) => prev ? { ...prev, invite_status: 'active' } : null);
          }
          fetchAdminData(poolId);
        } else {
          const remaining = userPools.filter((p) => p.id !== poolId);
          setUserPools(remaining);
          if (remaining.length > 0) setActivePool(remaining[0]);
        }
      } else {
        alert(`❌ ${data.error || 'Failed to respond to invitation.'}`);
      }
    } catch (err: any) {
      alert(`❌ Error: ${err.message}`);
    }
  };

  const handleDeletePoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activePool?.id) return;
    if (!deleteFailsafeChecked) {
      alert('Please check the failsafe confirmation checkbox before deleting.');
      return;
    }
    setIsDeletingPool(true);
    try {
      const res = await fetch('/api/pools/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId: activePool.id }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: `Pool "${activePool.name}" was permanently deleted.` });
        setShowDeletePoolModal(false);
        setDeleteFailsafeChecked(false);
        const remainingPools = userPools.filter((p) => p.id !== activePool.id);
        setUserPools(remainingPools);
        if (remainingPools.length > 0) {
          setActivePool(remainingPools[0]);
        } else {
          setActivePool(null);
        }
      } else {
        alert(`❌ ${data.error || 'Failed to delete pool.'}`);
      }
    } catch (err: any) {
      alert(`❌ Error: ${err.message}`);
    } finally {
      setIsDeletingPool(false);
    }
  };

  const [resettingPool, setResettingPool] = useState<boolean>(false);

  const handleResetPool = async () => {
    if (!window.confirm('Are you sure you want to reset Test Survivor Pool to new draft pool status? All test picks and users will be cleared except Jason & John.')) return;
    setResettingPool(true);
    setToast(null);
    try {
      const res = await fetch('/api/admin/pool/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId: activePool?.id || 'pool_2026_test' }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        setAdminSettings((prev) => ({ ...prev, pool_status: 'draft' }));
        setIsSettingsLocked(false);
        fetchAllowlist();
        fetchAdminData(activePool?.id || 'pool_2026_test');
        fetchScoreboard(activeWeek);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to reset pool.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setResettingPool(false);
    }
  };

  // Request Magic Link Handler
  const handleRequestMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginEmail.includes('@')) {
      setToast({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setSendingMagicLink(true);

    try {
      const res = await fetch('/api/auth/magic/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setLinkSentStatus(true);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to request magic link.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSendingMagicLink(false);
    }
  };

  // Logout Handler
  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {
      console.error(e);
    }
    setAuthUser(null);
    setUserPools([]);
    setActivePool(null);
    setHamburgerOpen(false);
  };

  // Helper: 3-Tier Dynamic Game Sorting (Tier 1: Live, Tier 2: Upcoming, Tier 3: Final)
  const sortGamesBy3Tiers = (gameList: NFLGame[]): NFLGame[] => {
    return [...gameList].sort((a, b) => {
      const getTier = (g: NFLGame) => {
        const statusStr = (g.status || '').toLowerCase().trim();
        const isFinal = statusStr === 'final' || statusStr === 'status_final' || statusStr === 'post';
        const isInProgress = !isFinal && (statusStr === 'in' || statusStr === 'in_progress' || statusStr === 'status_in_progress');
        if (isInProgress) return 1; // Tier 1: LIVE (Top Priority)
        if (!isFinal) return 2;     // Tier 2: UPCOMING / SCHEDULED
        return 3;                   // Tier 3: FINAL / COMPLETED (Bottom Priority)
      };

      const tierA = getTier(a);
      const tierB = getTier(b);

      if (tierA !== tierB) {
        return tierA - tierB;
      }

      const timeA = a.date ? new Date(a.date).getTime() : 0;
      const timeB = b.date ? new Date(b.date).getTime() : 0;
      return timeA - timeB;
    });
  };

  // Fetch Scoreboard (ESPN API or D1 - Supports 18-week pre-loading)
  const fetchScoreboard = async (week: number | 'all' = 'all', forceResort: boolean = false) => {
    setLoadingGames(true);
    try {
      const targetPoolId = activePool?.id || 'pool_2026_cote';
      const res = await fetch(`/api/nfl/scoreboard?week=${week}&poolId=${targetPoolId}`);
      const data: any = await res.json();

      if (data.gamesByWeek) {
        const mapByW: Record<number, NFLGame[]> = {};
        Object.keys(data.gamesByWeek).forEach((wKey) => {
          const wNum = parseInt(wKey, 10);
          const rawWGames: NFLGame[] = data.gamesByWeek[wKey] || [];
          mapByW[wNum] = sortGamesBy3Tiers(rawWGames);
        });
        setAllSchedulesMap((prev) => ({ ...prev, ...mapByW }));

        const targetW = typeof week === 'number' ? week : activeWeek;
        const currentWeekGames = mapByW[targetW] || (data.games ? sortGamesBy3Tiers(data.games) : []);
        setGames(currentWeekGames);
        if (forceResort || cardOrderIds.length === 0) {
          setCardOrderIds(currentWeekGames.map((g) => String(g.id)));
        }
      } else {
        const rawGames: NFLGame[] = data.games || [];
        const sortedGames = sortGamesBy3Tiers(rawGames);
        setGames(sortedGames);
        if (forceResort || cardOrderIds.length === 0) {
          setCardOrderIds(sortedGames.map((g) => String(g.id)));
        }
      }

      setEspnSource(data.source || 'espn_live');
    } catch (e) {
      console.error('Failed to fetch scoreboard', e);
    } finally {
      setLoadingGames(false);
    }
  };

  const lastPoolIdRef = useRef<string | null>(null);
  const lastAdminPortalPoolRef = useRef<string | null>(null);

  // Auto-default Pool Admin view to Financial/Payment Ledger for active pools, or Rules Setup for draft pools
  useEffect(() => {
    if (primaryPortal === 'pool_admin') {
      const poolKey = `${activePool?.id || ''}_${adminSettings?.pool_status || ''}`;
      if (lastAdminPortalPoolRef.current !== poolKey) {
        lastAdminPortalPoolRef.current = poolKey;
        const isPoolActive = (adminSettings?.pool_status === 'active' || activePool?.pool_status === 'active');
        setPoolAdminSubTab(isPoolActive ? 'payment_ledger' : 'rules_setup');
      }
    } else {
      lastAdminPortalPoolRef.current = null;
    }
  }, [primaryPortal, activePool?.id, activePool?.pool_status, adminSettings?.pool_status]);

  // Instant zero-latency week switching from pre-loaded 18-week schedule map
  useEffect(() => {
    if (allSchedulesMap[activeWeek] && allSchedulesMap[activeWeek].length > 0) {
      const sortedGames = allSchedulesMap[activeWeek];
      setGames(sortedGames);
      setCardOrderIds(sortedGames.map((g) => String(g.id)));
    }
  }, [activeWeek, allSchedulesMap]);

  // Fetch Grid Data (Strictly scoped by poolId)
  const fetchGrid = async (poolId?: string) => {
    const targetPoolId = poolId || activePool?.id || 'pool_2026_cote';
    setLoadingGrid(true);
    try {
      const res = await fetch(`/api/picks/grid?poolId=${targetPoolId}`);
      const data: any = await res.json();
      setGridData(data);

      if (data.poolConfig) {
        setAdminSettings((prev) => ({
          ...prev,
          buy_in_cost: data.poolConfig.buyInCost,
          buy_back_cost: data.poolConfig.buyBackCost,
          max_buy_backs: data.poolConfig.maxBuybacks,
          buy_back_max_week: data.poolConfig.buybackMaxWeek,
          pick_lock_rule: data.poolConfig.pickLockRule as any,
          pick_opening_rule: (data.poolConfig.pickOpeningRule || 'weekly_rolling') as any,
          pick_reveal_rule: data.poolConfig.pickRevealRule as any,
          pool_status: data.poolConfig.poolStatus as any,
          payment_instructions: data.poolConfig.paymentInstructions !== undefined ? data.poolConfig.paymentInstructions : (prev.payment_instructions || '')
        }));
      }

      if (data.currentWeek && lastPoolIdRef.current !== targetPoolId) {
        lastPoolIdRef.current = targetPoolId;
        setActiveWeek(data.currentWeek);

        if (authUser && data.grid) {
          const myRow = data.grid.find((g: any) => g.user.email === authUser.email);
          const isEliminated = myRow ? !myRow.isAlive : false;
          const isPendingBuyback = adminBuybacks.some((b) => (b.email === authUser?.email || b.user_id === authUser?.email) && b.status === 'pending');
          if (isEliminated && !isPendingBuyback) {
            setUserSubTab('group_picks');
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch grid data', e);
    } finally {
      setLoadingGrid(false);
    }
  };

  // Fetch Admin Settings & Payments
  const fetchAdminData = async (poolId: string) => {
    try {
      const res = await fetch(`/api/admin/settings?poolId=${poolId}`);
      const data: any = await res.json();
      if (data.settings) {
        const poolNameVal = data.settings.pool_name || data.settings.name || (activePool as any)?.name || (activePool as any)?.pool_name || '';
        const normalized = {
          ...data.settings,
          pool_name: poolNameVal,
          name: poolNameVal,
        };
        setAdminSettings(normalized);
        setBaselineSettings(normalized);
        setIsSettingsLocked(data.settings.pool_status === 'active');
      }
      if (data.userPayments) {
        setUserPayments(data.userPayments);
        const myPay = data.userPayments.find((p: UserPayment) => p.email === authUser?.email);
        if (myPay) {
          setUserReminderOptIn(myPay.reminder_opt_in === 1);
          if (myPay.timezone) setUserTimeZone(myPay.timezone);
        }
      }
      if (data.invitedList) {
        setInvitedList(data.invitedList);
      }
      fetchAdminBuybacks();
    } catch (e) {
      console.error('Failed to fetch admin settings', e);
    } finally {
      setSettingsLoaded(true);
    }
  };

  const fetchAdminBuybacks = async () => {
    try {
      const res = await fetch(`/api/admin/buybacks?poolId=${activePool?.id || 'pool_2026_cote'}`);
      const data: any = await res.json();
      setAdminBuybacks(data.buybacks || []);
    } catch (e) {
      console.error('Failed to fetch admin buybacks', e);
    }
  };

  const handleRequestBuyback = async (weekNum?: number) => {
    const targetWeek = weekNum || currentUserGrid?.eliminatedWeek || activeWeek;
    setRequestingBuyback(true);
    try {
      const res = await fetch('/api/user/buyback/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authUser?.email, poolId: activePool?.id, week: targetWeek, amount: adminSettings.buy_back_cost }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: 'Buy-back request submitted! You can lock in a Provisional Pick below while awaiting admin payment confirmation.' });
        fetchAdminData(activePool?.id || 'pool_2026_cote');
        fetchGrid(activePool?.id);
      } else {
        setToast({ type: 'error', text: data.error || 'Buy-back request failed' });
      }
    } catch (e) {
      setToast({ type: 'error', text: 'Failed to request buy-back' });
    } finally {
      setRequestingBuyback(false);
    }
  };

  const handleApproveBuyback = async (buybackId: string) => {
    try {
      const res = await fetch('/api/admin/buyback/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buybackId, poolId: activePool?.id }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: 'Buy-Back approved & marked paid! Player status restored.' });
        fetchAdminBuybacks();
        fetchAdminData(activePool?.id || 'pool_2026_cote');
        fetchGrid(activePool?.id);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to approve buy-back' });
      }
    } catch (e) {
      setToast({ type: 'error', text: 'Network error approving buy-back' });
    }
  };

  const handleRejectBuyback = async (buybackId: string) => {
    try {
      const res = await fetch('/api/admin/buyback/reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buybackId }),
      });
      if (res.ok) {
        setToast({ type: 'info', text: 'Buy-Back request rejected.' });
        fetchAdminBuybacks();
      }
    } catch (e) {}
  };

  // Fetch Site Admin Allowlist
  const fetchAllowlist = async () => {
    try {
      const res = await fetch('/api/admin/allowlist');
      const data: any = await res.json();
      if (data.allowlist) setAllowlistUsers(data.allowlist);
    } catch (e) {
      console.error('Failed to fetch allowlist', e);
    }
  };

  // Add User to Allowlist Handler
  const handleAddAllowlistUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAllowlistEmail || !newAllowlistEmail.includes('@')) {
      setToast({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setAddingAllowlist(true);
    setToast(null);

    try {
      const res = await fetch('/api/admin/allowlist/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newAllowlistEmail,
          name: newAllowlistName || 'Pool Member',
          role: newAllowlistRole,
        }),
      });

      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        setNewAllowlistEmail('');
        setNewAllowlistName('');
        fetchAllowlist();
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to add user.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setAddingAllowlist(false);
    }
  };

  const handleDeleteUserSubmit = async () => {
    if (!deletingUser) return;
    setIsDeletingUser(true);
    try {
      const res = await fetch('/api/admin/users/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: deletingUser.email, notifyUser: notifyUserOnDelete }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: `🗑️ ${data.message}` });
        setDeletingUser(null);
        fetchAllowlist();
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to delete user record.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setIsDeletingUser(false);
    }
  };

  // Fetch DB Explorer Tables
  const fetchDbTables = async () => {
    try {
      const res = await fetch('/api/admin/db/tables');
      const data: any = await res.json();
      if (data.tables) setDbTables(data.tables);
    } catch (e) {}
  };

  const fetchDbTableData = async (tableName: string, page: number = 1, limit: number = 50) => {
    setLoadingTableData(true);
    try {
      const res = await fetch(`/api/admin/db/table-data?table=${tableName}&page=${page}&limit=${limit}`);
      const data: any = await res.json();
      setDbTableData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTableData(false);
    }
  };

  const handleExecuteReadonlySql = async () => {
    if (!sqlQuery.trim()) return;
    setExecutingSql(true);
    setSqlResult(null);
    try {
      const res = await fetch('/api/admin/db/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: sqlQuery }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setSqlResult(data);
      } else {
        setSqlResult({ columns: [], rows: [], rowCount: 0, durationMs: 0, error: data.error || 'SQL query failed.' });
      }
    } catch (e: any) {
      setSqlResult({ columns: [], rows: [], rowCount: 0, durationMs: 0, error: `Error: ${e.message}` });
    } finally {
      setExecutingSql(false);
    }
  };

  useEffect(() => {
    if (primaryPortal === 'site_admin') {
      fetchAllowlist();
      if (siteAdminSubTab === 'db_explorer') {
        fetchDbTables();
        fetchDbTableData(selectedDbTable, dbTablePage, dbTableLimit);
      }
    }
  }, [primaryPortal, siteAdminSubTab, selectedDbTable, dbTablePage, dbTableLimit]);

  // Save User Settings Handler
  const handleSaveUserSettings = async () => {
    if (!authUser) return;
    setSavingUserSettings(true);
    setToast(null);
    try {
      const res = await fetch('/api/user/settings/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authUser.email,
          poolId: activePool?.id,
          reminderOptIn: userReminderOptIn,
          timezone: userTimeZone,
        }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        if (data.user && data.user.name) {
          setAuthUser((prev) => prev ? { ...prev, name: data.user.name } : null);
        }
        fetchAdminData(activePool?.id || 'pool_2026_cote');
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to save settings.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSavingUserSettings(false);
    }
  };

  // Save Admin Settings Handler with Diff Detection & Member Email Modal
  const handleSaveSettings = async (skipDiffCheck = false) => {
    // Detect setting diffs if pool is active and participants are registered
    if (!skipDiffCheck && adminSettings.pool_status === 'active' && userPayments.length > 0 && baselineSettings) {
      const diffs: Array<{ field: string; oldVal: string; newVal: string }> = [];

      if (adminSettings.buy_in_cost !== baselineSettings.buy_in_cost) {
        diffs.push({ field: 'Entry Buy-In Cost', oldVal: `$${baselineSettings.buy_in_cost}`, newVal: `$${adminSettings.buy_in_cost}` });
      }
      if (adminSettings.pick_lock_rule !== baselineSettings.pick_lock_rule) {
        diffs.push({ field: 'Pick Lock Deadline Rule', oldVal: baselineSettings.pick_lock_rule, newVal: adminSettings.pick_lock_rule });
      }
      if (adminSettings.pick_reveal_rule !== baselineSettings.pick_reveal_rule) {
        diffs.push({ field: 'Pick Reveal Rule', oldVal: baselineSettings.pick_reveal_rule, newVal: adminSettings.pick_reveal_rule });
      }
      if (adminSettings.unmade_pick_policy !== baselineSettings.unmade_pick_policy) {
        diffs.push({ field: 'Unmade Pick Policy', oldVal: baselineSettings.unmade_pick_policy, newVal: adminSettings.unmade_pick_policy });
      }
      if (adminSettings.buy_back_cost !== baselineSettings.buy_back_cost) {
        diffs.push({ field: 'Buy-Back Cost', oldVal: `$${baselineSettings.buy_back_cost}`, newVal: `$${adminSettings.buy_back_cost}` });
      }
      if (adminSettings.max_buy_backs !== baselineSettings.max_buy_backs) {
        diffs.push({ field: 'Max Buy-Backs', oldVal: `${baselineSettings.max_buy_backs}`, newVal: `${adminSettings.max_buy_backs}` });
      }
      if (adminSettings.season_start_week !== baselineSettings.season_start_week) {
        diffs.push({ field: 'Pool Start Week', oldVal: `Week ${baselineSettings.season_start_week}`, newVal: `Week ${adminSettings.season_start_week}` });
      }
      if (adminSettings.registration_lock_week !== baselineSettings.registration_lock_week) {
        diffs.push({ field: 'Registration Lock Week', oldVal: `Week ${baselineSettings.registration_lock_week}`, newVal: `Week ${adminSettings.registration_lock_week}` });
      }

      if (diffs.length > 0) {
        setSettingsDiffs(diffs);
        setShowSettingsChangeModal(true);
        return;
      }
    }

    setSavingAdminSettings(true);
    setToast(null);
    try {
      const targetPoolId = activePool?.id || adminSettings.pool_id || 'pool_2026_cote';
      const res = await fetch('/api/admin/settings/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...adminSettings, pool_id: targetPoolId }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: data.message });
        setBaselineSettings(adminSettings);
        setIsSettingsLocked(true);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to save settings.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSavingAdminSettings(false);
    }
  };

  const handleSendDiffEmailAndSave = async () => {
    setSendingDiffEmail(true);
    try {
      await fetch('/api/admin/settings/notify-changes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ poolId: activePool?.id, diffs: settingsDiffs }),
      });
      setToast({ type: 'success', text: 'Rule change summary email dispatched to all pool members!' });
    } catch (e: any) {
      console.error('Failed sending rule change email', e);
    } finally {
      setSendingDiffEmail(false);
      setShowSettingsChangeModal(false);
      await handleSaveSettings(true);
    }
  };

  // Submit Player Registration Handler
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingRegistration(true);
    setToast(null);
    try {
      const res = await fetch('/api/pool/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authUser?.email,
          firstName: registrationForm.firstName,
          lastName: registrationForm.lastName,
          timeZone: registrationForm.timeZone,
          paymentNote: registrationForm.paymentNote,
          poolId: activePool?.id,
        }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        if (authUser) {
          setAuthUser({ ...authUser, name: data.fullName, isRegistered: true });
        }
        setShowRegistrationScreen(false);
        setToast({ type: 'success', text: `Welcome to ${activePool?.name || 'SurvivorPool.app'}!` });
        if (activePool) {
          fetchGrid(activePool.id);
          fetchAdminData(activePool.id);
        }
      } else {
        setToast({ type: 'error', text: data.error || 'Registration failed.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Registration error: ${e.message}` });
    } finally {
      setSubmittingRegistration(false);
    }
  };

  // Submit Decline Invitation Handler
  const handleDeclineSubmit = async () => {
    setSubmittingRegistration(true);
    setToast(null);
    try {
      const res = await fetch('/api/pool/decline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authUser?.email,
          poolId: activePool?.id || 'pool_2026_cote',
        }),
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setRegistrationDeclined(true);
        setToast({ type: 'info', text: 'You have declined the pool invitation.' });
        if (activePool) {
          fetchAdminData(activePool.id);
        }
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to decline invitation.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSubmittingRegistration(false);
    }
  };

  // Update Payment Status Handler
  const handleUpdateUserPayment = async (updatedPayment: UserPayment) => {
    setToast(null);
    setUserPayments((prev) => prev.map((p) => (p.user_id === updatedPayment.user_id ? updatedPayment : p)));
    try {
      const res = await fetch('/api/admin/payments/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updatedPayment, pool_id: activePool?.id }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setToast({ type: 'success', text: `Updated payment status for ${updatedPayment.name}!` });
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to update payment.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    }
  };

  // Submit Pick Handler
  const handleMakePick = async (teamInput: string) => {
    if (!authUser) return;
    const teamAbbrev = getTeamAbbrev(teamInput);
    const targetPoolId = activePool?.id || 'pool_2026_test';

    // ⚡ Optimistic UI Update (0ms Latency)
    setGridData((prev) => {
      if (!prev || !prev.grid) return prev;
      const updatedGrid = prev.grid.map((g) => {
        const isMe = (g.user.id && authUser.id && g.user.id === authUser.id) ||
                     (g.user.email && authUser.email && g.user.email.toLowerCase() === authUser.email.toLowerCase());
        if (!isMe) return g;
        const newPicks = { ...(g.weeklyPicks || {}) };
        newPicks[activeWeek] = { team: teamAbbrev, status: 'pending' };
        return { ...g, weeklyPicks: newPicks };
      });
      return { ...prev, grid: updatedGrid };
    });

    setSubmittingPick(true);
    setToast(null);
    try {
      const res = await fetch('/api/picks/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: authUser.email,
          poolId: targetPoolId,
          week: activeWeek,
          teamPicked: teamAbbrev,
        }),
      });

      const data: any = await res.json();
      if (res.ok) {
        fetchGrid(targetPoolId);
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to submit pick.' });
        fetchGrid(targetPoolId);
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
      fetchGrid(targetPoolId);
    } finally {
      setSubmittingPick(false);
    }
  };

  const fetchHealth = async () => {
    setLoadingHealth(true);
    try {
      const res = await fetch('/api/health');
      const data = await res.json();
      setHealth(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHealth(false);
    }
  };

  const handleTestEmail = async () => {
    setEmailTestLoading(true);
    setEmailTestStatus(null);
    try {
      const target = authUser?.email || 'user@example.com';
      const res = await fetch('/api/email/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: target }),
      });
      const data: any = await res.json();
      if (res.ok) {
        setEmailTestStatus(`Test email dispatched to ${data.targetEmail} via ${data.gateway}!`);
      } else {
        setEmailTestStatus('Failed to send test email.');
      }
    } catch (e: any) {
      setEmailTestStatus(`Error: ${e.message}`);
    } finally {
      setEmailTestLoading(false);
    }
  };

  const handleInspectDb = async () => {
    setLoadingDb(true);
    try {
      const res = await fetch('/api/picks/sample');
      const data = await res.json();
      setDbData(data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoadingDb(false);
    }
  };

  const currentUserGrid = (gridData?.grid || []).find((g) => 
    (g?.user?.id && authUser?.id && g.user.id === authUser.id) ||
    (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) ||
    (g?.user?.name && authUser?.name && g.user.name.toLowerCase() === authUser.name.toLowerCase())
  );
  const usedTeamsMap: Record<number, string> = {};
  if (currentUserGrid?.weeklyPicks) {
    Object.entries(currentUserGrid.weeklyPicks).forEach(([wStr, p]) => {
      if (p && p.team) {
        usedTeamsMap[parseInt(wStr, 10)] = getTeamAbbrev(p.team);
      }
    });
  }

  // Financial Calculations for Admin Dashboard
  const totalBuyInsOwed = (userPayments || []).length * (adminSettings.buy_in_cost || 10);
  const totalBuyInsCollected = (userPayments || []).filter((p) => p.buy_in_paid === 1).length * (adminSettings.buy_in_cost || 10);
  const totalBuyBacksCount = (userPayments || []).reduce((acc, p) => acc + (p.buy_backs_used || 0), 0);
  const totalBuyBacksOwed = totalBuyBacksCount * (adminSettings.buy_back_cost || 10);
  const totalBuyBacksCollected = (userPayments || []).filter((p) => p.buy_backs_paid === 1).reduce((acc, p) => acc + (p.buy_backs_used || 0) * (adminSettings.buy_back_cost || 10), 0);
  const totalPotValue = totalBuyInsOwed + totalBuyBacksOwed;
  const totalPotCollected = totalBuyInsCollected + totalBuyBacksCollected;
  const totalUnpaidBalance = totalPotValue - totalPotCollected;

  // Filtered Grid User list
  const filteredGrid = ((gridData?.grid || []).filter((g) => {
    if (!g?.user) return false;
    const matchesSearch = (g.user.name || '').toLowerCase().includes(gridSearch.toLowerCase()) || (g.user.email || '').toLowerCase().includes(gridSearch.toLowerCase());
    if (gridFilterStatus === 'alive') return matchesSearch && g.isAlive;
    if (gridFilterStatus === 'eliminated') return matchesSearch && !g.isAlive;
    return matchesSearch;
  }) || []).sort((a, b) => {
    if (a.isAlive && !b.isAlive) return -1;
    if (!a.isAlive && b.isAlive) return 1;

    if (a.isAlive && b.isAlive) {
      const winsA = parseInt((a.record || '0-0').split('-')[0], 10) || 0;
      const winsB = parseInt((b.record || '0-0').split('-')[0], 10) || 0;
      if (winsB !== winsA) return winsB - winsA;
      return (a.user.name || '').localeCompare(b.user.name || '');
    }

    const elimA = a.eliminatedWeek || 0;
    const elimB = b.eliminatedWeek || 0;
    if (elimB !== elimA) return elimB - elimA;
    return (a.user.name || '').localeCompare(b.user.name || '');
  });

  const handleSaveNewName = async () => {
    if (!editingUserName || !newNameInput.trim()) return;
    setSavingNewName(true);
    try {
      const res = await fetch('/api/admin/users/update-name', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: editingUserName.userId,
          email: editingUserName.email,
          name: newNameInput.trim(),
          poolId: activePool?.id || adminSettings.pool_id || 'pool_2026_cote'
        })
      });
      const data: any = await res.json();
      if (res.ok && data.success) {
        setToast({ type: 'success', text: `Updated display name to "${newNameInput.trim()}"!` });
        setEditingUserName(null);
        setNewNameInput('');
        fetchAllowlist();
        fetchGrid(activePool?.id || 'pool_2026_cote');
        fetchAdminData(activePool?.id || 'pool_2026_cote');
      } else {
        setToast({ type: 'error', text: data.error || 'Failed to update user name.' });
      }
    } catch (e: any) {
      setToast({ type: 'error', text: `Error: ${e.message}` });
    } finally {
      setSavingNewName(false);
    }
  };

  const handleExportLedgerCSV = () => {
    if (!userPayments || userPayments.length === 0) return;
    const headers = ['Member Name', 'Email', 'Status', 'Buy-In Paid', 'Buy-Backs Used', 'Buy-Backs Paid', 'Total Paid', 'Total Owed', 'Settled Status', 'Notes'];
    const rows = userPayments.map((p) => {
      const uGrid = (gridData?.grid || []).find((g) => (g?.user?.email && p.email && g.user.email.toLowerCase() === p.email.toLowerCase()) || (g?.user?.id && p.user_id && g.user.id === p.user_id));
      const statusText = uGrid ? (uGrid.isAlive ? 'Active' : (!uGrid.weeklyPicks[uGrid.eliminatedWeek!] ? 'Ghost Eliminated' : `Eliminated W${uGrid.eliminatedWeek}`)) : 'Active';
      const usedCount = uGrid?.buybacksUsed ?? (p.buy_backs_used || 0);
      const buyInCost = adminSettings.buy_in_cost || 10;
      const buyBackCost = adminSettings.buy_back_cost || 10;
      const totalOwed = buyInCost + (usedCount * buyBackCost);
      const totalPaid = (p.buy_in_paid ? buyInCost : 0) + (p.buy_backs_paid ? usedCount * buyBackCost : 0);
      const isSettled = totalPaid >= totalOwed && totalOwed > 0 ? 'Settled' : 'Pending';

      return [
        `"${(p.name || formatDisplayName(p.name, p.email)).replace(/"/g, '""')}"`,
        `"${p.email}"`,
        `"${statusText}"`,
        p.buy_in_paid ? 'PAID' : 'UNPAID',
        usedCount,
        p.buy_backs_paid ? 'PAID' : 'UNPAID',
        `$${totalPaid}`,
        `$${totalOwed}`,
        isSettled,
        `"${(p.notes || '').replace(/"/g, '""')}"`
      ].join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `financial_ledger_${activePool?.id || 'pool'}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSettleAccount = async (p: any) => {
    const uGrid = (gridData?.grid || []).find((g) => (g?.user?.email && p.email && g.user.email.toLowerCase() === p.email.toLowerCase()) || (g?.user?.id && p.user_id && g.user.id === p.user_id));
    const usedCount = uGrid?.buybacksUsed ?? (p.buy_backs_used || 0);
    const buyInCost = adminSettings.buy_in_cost || 10;
    const buyBackCost = adminSettings.buy_back_cost || 10;
    const totalOwed = buyInCost + (usedCount * buyBackCost);

    await handleUpdateUserPayment({
      ...p,
      buy_in_paid: 1,
      buy_backs_paid: usedCount > 0 ? 1 : 0,
      total_paid: totalOwed
    });
    setToast({ type: 'success', text: `Marked account settled for ${formatDisplayName(p.name, p.email)} ($${totalOwed})!` });
  };

  // Check roles for navigation & deterministic pool state hydration
  const effectiveActivePool = activePool || (userPools && userPools.length > 0 ? userPools[0] : null);
  const isSiteAdmin = authUser?.global_role === 'site_admin' || authUser?.email?.toLowerCase() === 'jasonc1201@gmail.com' || authUser?.email?.toLowerCase() === 'johnrbressler@gmail.com';
  const isPoolAdmin = isSiteAdmin || effectiveActivePool?.role_in_pool === 'pool_admin';

  // ══════════════════════════════════════════════════════════════════════════
  // VIEW 1: LOADING / CHECKING AUTH STATE
  // ══════════════════════════════════════════════════════════════════════════
  if (checkingAuth || verifyingToken) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center', padding: '32px' }}>
          <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '16px', backgroundColor: '#eff6ff', marginBottom: '16px' }}>
            <RefreshCw size={32} color="#2563eb" className="spin" />
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
            {verifyingToken ? 'Verifying Magic Login Link...' : 'Authenticating Session...'}
          </h2>
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '6px' }}>
            Securing access to SurvivorPool.app
          </p>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VIEW 2: MAGIC LINK VERIFICATION ERROR SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (verifyError) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ maxWidth: '440px', width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '14px', backgroundColor: '#fef2f2', marginBottom: '16px' }}>
            <AlertCircle size={32} color="#dc2626" />
          </div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
            Magic Link Expired or Invalid
          </h2>
          <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.5, marginBottom: '24px' }}>
            {verifyError}
          </p>
          <button
            onClick={() => { setVerifyError(null); window.history.replaceState({}, document.title, window.location.pathname); }}
            style={{ width: '100%', backgroundColor: '#2563eb', color: '#ffffff', padding: '12px', borderRadius: '10px', fontWeight: 700, border: 'none', cursor: 'pointer' }}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VIEW 3: LOGIN SCREEN (GATED AUTHENTICATION)
  // ══════════════════════════════════════════════════════════════════════════
  if (!authUser) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ maxWidth: '440px', width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '18px', padding: '36px', boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }}>
          
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <img src="/logo_full.png" alt="SurvivorPool.app Logo" style={{ height: '90px', borderRadius: '12px', marginBottom: '12px', objectFit: 'contain' }} />
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
              The Modern Platform for NFL Survivor Leagues
            </p>
          </div>

          <form onSubmit={handleRequestMagicLink}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                Enter Email
              </label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 14px', backgroundColor: '#ffffff' }}>
                <Mail size={18} color="#64748b" style={{ marginRight: '10px' }} />
                <input
                  type="email"
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.92rem', color: '#0f172a', fontWeight: 500 }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sendingMagicLink}
              style={{
                width: '100%',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                padding: '14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '0.95rem',
                border: 'none',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Key size={18} />
              {sendingMagicLink ? 'Sending Link...' : linkSentStatus ? 'Resend Link' : 'Send Login Link'}
            </button>

            {linkSentStatus && (
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: '#16a34a', fontWeight: 700, fontSize: '0.88rem' }}>
                <CheckCircle2 size={18} color="#16a34a" />
                <span>Link Sent! Check your email inbox.</span>
              </div>
            )}

            <div style={{ marginTop: '16px', fontSize: '0.78rem', color: '#64748b', textAlign: 'center' }}>
              Links expire in 60 seconds
            </div>
          </form>

        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VIEW 3.5: PLAYER POOL REGISTRATION SCREEN (INVITATION LINK FLOW)
  // ══════════════════════════════════════════════════════════════════════════
  if (showRegistrationScreen && authUser) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', color: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, system-ui, sans-serif' }}>
        <div style={{ maxWidth: '580px', width: '100%', backgroundColor: '#1e293b', borderRadius: '16px', border: '1px solid #334155', padding: '32px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)' }}>
          
          {/* Header with Pool Name */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <img src="/logo_full.png" alt="SurvivorPool.app Logo" style={{ height: '75px', borderRadius: '10px', marginBottom: '10px', objectFit: 'contain' }} />
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#60a5fa', margin: 0 }}>
              {activePool?.name || 'SurvivorPool.app 2026'}
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginTop: '6px' }}>
              Official Player Registration & Terms Agreement
            </p>
          </div>

          {registrationDeclined ? (
            <div style={{ backgroundColor: '#450a0a', border: '1px solid #991b1b', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
              <h3 style={{ color: '#f87171', margin: 0 }}>Invitation Declined</h3>
              <p style={{ color: '#fca5a5', fontSize: '0.88rem', marginTop: '8px' }}>
                You have opted out of joining {activePool?.name || 'SurvivorPool.app'}. If this was a mistake, please contact your pool administrator.
              </p>
              <button
                onClick={() => { setRegistrationDeclined(false); setShowRegistrationScreen(false); }}
                style={{ marginTop: '16px', backgroundColor: '#334155', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              >
                Return to Dashboard
              </button>
            </div>
          ) : (
            <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Prepopulated Email */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Invited Email Address:
                </label>
                <input
                  type="email"
                  value={authUser.email}
                  disabled
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#94a3b8', fontWeight: 600 }}
                />
              </div>

              {/* First Name & Last Name Input Boxes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: '6px' }}>
                    First Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Jason"
                    value={registrationForm.firstName}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, firstName: e.target.value })}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 600 }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: '6px' }}>
                    Last Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Cote"
                    value={registrationForm.lastName}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, lastName: e.target.value })}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 600 }}
                  />
                </div>
              </div>

              {/* Time Zone Selection */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: '6px' }}>
                  Preferred Time Zone:
                </label>
                <select
                  value={registrationForm.timeZone}
                  onChange={(e) => setRegistrationForm({ ...registrationForm, timeZone: e.target.value })}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 600 }}
                >
                  <option value="America/New_York">Eastern Time (ET - America/New_York)</option>
                  <option value="America/Chicago">Central Time (CT - America/Chicago)</option>
                  <option value="America/Denver">Mountain Time (MT - America/Denver)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT - America/Los_Angeles)</option>
                  <option value="America/Anchorage">Alaska Time (AKT - America/Anchorage)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HAT - Pacific/Honolulu)</option>
                </select>
              </div>

              {/* Payment Note / Handle Input */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', display: 'block', marginBottom: '6px' }}>
                  Payment Memo / Handle (Optional):
                </label>
                <input
                  type="text"
                  placeholder="e.g. Sent $100 via Venmo @JasonCote or Zelle 555-123-4567"
                  value={registrationForm.paymentNote}
                  onChange={(e) => setRegistrationForm({ ...registrationForm, paymentNote: e.target.value })}
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #475569', backgroundColor: '#0f172a', color: '#ffffff', fontWeight: 600 }}
                />
              </div>

              {/* Pool Settings Transparency Card */}
              <div style={{ backgroundColor: '#0f172a', borderRadius: '12px', border: '1px solid #334155', padding: '18px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#fbbf24', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ShieldCheck size={18} />
                  <span>Official Pool Rules & Settings</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#cbd5e1', fontSize: '0.84rem', lineHeight: 1.6 }}>
                  <li>💵 <strong>Entry Fee:</strong> ${adminSettings?.buy_in_cost || 100}.00 (Due prior to Week 1 Kickoff)</li>
                  <li>🔁 <strong>Buy-Back Policy:</strong> Up to {adminSettings?.max_buy_backs || 1} buy-back allowed through Week {adminSettings?.buy_back_max_week || 3}</li>
                  <li>🔒 <strong>Pick Deadline:</strong> Locks at {adminSettings?.pick_lock_rule === 'individual_kickoff' ? 'Individual Game Kickoffs' : 'Thursday Night Football Kickoff'}</li>
                  <li>👁️ <strong>Pick Reveal:</strong> Picks revealed to all players at game kickoff</li>
                  <li>👻 <strong>Unmade Picks:</strong> Automatic Ghost Elimination (No late pick waivers)</li>
                </ul>
              </div>

              {/* Action Buttons: Join or Reject */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={handleDeclineSubmit}
                  disabled={submittingRegistration}
                  style={{ backgroundColor: '#334155', color: '#f87171', border: '1px solid #7f1d1d', padding: '14px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
                >
                  ❌ Decline Invitation
                </button>

                <button
                  type="submit"
                  disabled={submittingRegistration}
                  style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
                >
                  {submittingRegistration ? 'Registering...' : '✅ Accept & Join Pool'}
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // VIEW 4: AUTHENTICATED APP DASHBOARD (3-PORTAL ARCHITECTURE)
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', color: '#1e293b', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* ── POOL INVITATION & BUY-IN COMMITMENT MODAL ───────────────────────── */}
      {pendingInvitePools.length > 0 && (() => {
        const invPool = pendingInvitePools[0];
        const buyInCost = invPool.buy_in_cost ?? 100;
        const buyBackCost = invPool.buy_back_cost ?? buyInCost;
        const maxBuybacks = invPool.max_buy_backs ?? 1;
        const buybackMaxWeek = invPool.buy_back_max_week ?? 3;
        let lockRuleStr = 'Start of Thursday Night Football';
        if (invPool.pick_lock_rule === 'saturday_kickoff') lockRuleStr = 'Start of Saturday Games';
        else if (invPool.pick_lock_rule === 'sunday_early') lockRuleStr = 'Sunday 1:00 PM ET';
        else if (invPool.pick_lock_rule === 'individual_kickoff') lockRuleStr = 'Individual Game Kickoffs';

        const buybackSummaryStr = maxBuybacks > 0 
          ? `$${buyBackCost} per re-entry (Max ${maxBuybacks} buy-back${maxBuybacks > 1 ? 's' : ''} through Week ${buybackMaxWeek})`
          : 'No Buy-Backs (Single Elimination)';

        return (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
              border: '1px solid #cbd5e1'
            }}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img src="/logo_full.png" alt="SurvivorPool.app Logo" style={{ height: '70px', borderRadius: '10px', marginBottom: '10px', objectFit: 'contain' }} />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Join {invPool.name || invPool.pool_name || 'Survivor Pool'}
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
                  You have been invited to participate in this pool.
                </p>
              </div>

              {/* Commitment Summary Box */}
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px 18px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '0.78rem', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '10px' }}>
                  📋 Financial & Rule Parameters Summary
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.88rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>💵 Entry Buy-In Fee:</span>
                    <span style={{ color: '#0f172a', fontWeight: 800 }}>${buyInCost}.00</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>🔁 Buy-Back Allowance:</span>
                    <span style={{ color: '#0f172a', fontWeight: 700, textAlign: 'right' }}>{buybackSummaryStr}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: invPool.payment_instructions ? '1px solid #e2e8f0' : 'none', paddingBottom: invPool.payment_instructions ? '6px' : '0' }}>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>🔒 Weekly Lock Deadline:</span>
                    <span style={{ color: '#0f172a', fontWeight: 700 }}>{lockRuleStr}</span>
                  </div>
                  {invPool.payment_instructions && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '2px' }}>
                      <span style={{ color: '#64748b', fontWeight: 600 }}>💳 Payment Method:</span>
                      <span style={{ color: '#0f172a', fontWeight: 700, textAlign: 'right' }}>{invPool.payment_instructions}</span>
                    </div>
                  )}
                </div>
              </div>

              <p style={{ fontSize: '0.82rem', color: '#475569', textAlign: 'center', marginBottom: '22px', lineHeight: 1.4 }}>
                By clicking <strong>Accept & Commit</strong>, you agree to enter this pool and commit to the <strong>${buyInCost}.00</strong> buy-in fee.
              </p>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => handleRespondToInvite(invPool.id, 'decline')}
                  disabled={respondingInvite}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid #fca5a5',
                    backgroundColor: '#fee2e2',
                    color: '#b91c1c',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  🔴 Decline
                </button>
                <button
                  type="button"
                  onClick={() => handleRespondToInvite(invPool.id, 'accept')}
                  disabled={respondingInvite}
                  style={{
                    flex: 1.5,
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#16a34a',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.92rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)'
                  }}
                >
                  {respondingInvite ? 'Processing...' : `🟢 Accept & Commit ($${buyInCost})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* ── Top Navigation Header ────────────────────────────────────────────── */}
      <header style={{
        borderBottom: primaryPortal === 'site_admin' ? '1px solid #7f1d1d' : primaryPortal === 'pool_admin' ? '1px solid #1e293b' : '1px solid #e2e8f0',
        backgroundColor: primaryPortal === 'site_admin' ? '#450a0a' : primaryPortal === 'pool_admin' ? '#0f172a' : '#ffffff',
        color: primaryPortal === 'user' ? '#0f172a' : '#ffffff',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        transition: 'background-color 0.2s, border-color 0.2s'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
          
          {/* Logo Title & Selectable Pool Dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: '1 1 auto' }}>
            <img src="/logo.png" alt="SurvivorPool.app Logo" style={{ height: '36px', borderRadius: '6px', objectFit: 'contain', flexShrink: 0 }} />
            {userPools.length > 1 ? (
              <select
                value={activePool?.id || ''}
                onChange={(e) => {
                  const selected = userPools.find((p) => p.id === e.target.value);
                  if (selected) setActivePool(selected);
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  border: 'none',
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  cursor: 'pointer',
                  outline: 'none',
                  padding: 0,
                  maxWidth: '100%',
                  textOverflow: 'ellipsis'
                }}
              >
                {userPools.map((p) => (
                  <option key={p.id} value={p.id} style={{ backgroundColor: '#ffffff', color: '#0f172a', fontSize: '1rem', fontWeight: 600 }}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.02em', color: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {activePool ? activePool.name : 'SurvivorPool.app'}
              </span>
            )}

            {/* Pool Type Badge (Visible to Administrators Only) */}
            {(primaryPortal === 'pool_admin' || primaryPortal === 'site_admin') && (
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                padding: '3px 8px',
                borderRadius: '12px',
                backgroundColor: (activePool?.pool_type === 'simulation' || activePool?.id === 'pool_2026_test') ? '#0284c7' : '#16a34a',
                color: '#ffffff',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                {(activePool?.pool_type === 'simulation' || activePool?.id === 'pool_2026_test') ? '🧪 SIMULATION' : '🏆 LIVE'}
              </span>
            )}

            {/* Admin Badges (No indicator for standard User Portal) */}
            {primaryPortal === 'pool_admin' && (
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                padding: '3px 8px',
                borderRadius: '12px',
                backgroundColor: '#1e293b',
                color: '#93c5fd',
                border: '1px solid #3b82f6',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                ⚙️ Pool Admin
              </span>
            )}

            {primaryPortal === 'site_admin' && (
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                padding: '3px 8px',
                borderRadius: '12px',
                backgroundColor: '#7f1d1d',
                color: '#fca5a5',
                border: '1px solid #ef4444',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}>
                🛡️ Site Admin
              </span>
            )}
          </div>

            {/* Top Right Actions & Hamburger Button (Always Pinned Top Right) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', flexShrink: 0, marginLeft: 'auto' }}>
            
            {(primaryPortal === 'pool_admin' || primaryPortal === 'site_admin') && (
              <button
                className="hide-on-mobile"
                onClick={() => setShowCreatePoolModal(true)}
                title="Create a new Survivor Pool (Live or Simulation)"
                style={{
                  backgroundColor: '#1e293b',
                  color: '#38bdf8',
                  border: '1px solid #0284c7',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <span>➕ Create Pool</span>
              </button>
            )}
            
            {/* ☰ Hamburger Trigger Icon Button (Only visible to Pool/Site Admins who have portal options) */}
            {(isPoolAdmin || isSiteAdmin) && (
              <button
                onClick={() => setHamburgerOpen(!hamburgerOpen)}
                aria-label="Toggle Navigation Menu"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hamburgerOpen ? (primaryPortal === 'site_admin' ? '#ef4444' : primaryPortal === 'pool_admin' ? '#2563eb' : '#2563eb') : (primaryPortal === 'site_admin' ? '#7f1d1d' : primaryPortal === 'pool_admin' ? '#1e293b' : '#f8fafc'),
                  color: '#ffffff',
                  border: primaryPortal === 'site_admin' ? '1px solid #991b1b' : primaryPortal === 'pool_admin' ? '1px solid #334155' : '1px solid #cbd5e1',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                {hamburgerOpen ? <X size={20} /> : <Menu size={20} color={primaryPortal === 'user' && !hamburgerOpen ? '#0f172a' : '#ffffff'} />}
              </button>
            )}

            {/* ☰ Hamburger Dropdown Menu Drawer */}
            {hamburgerOpen && (
              <div style={{
                position: 'absolute',
                top: '50px',
                right: 0,
                width: '300px',
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '14px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                padding: '16px',
                zIndex: 100
              }}>
                {/* User Profile Summary */}
                <div style={{ paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>{formatDisplayName(authUser.name, authUser.email)}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{authUser.email}</div>
                  <div style={{ fontSize: '0.7rem', color: isSiteAdmin ? '#2563eb' : '#16a34a', fontWeight: 700, marginTop: '4px' }}>
                    {isSiteAdmin ? '⭐ Site Administrator' : isPoolAdmin ? '⚙️ Pool Administrator' : 'Pool Member'}
                  </div>
                </div>

                {/* Primary Site / Portal Switcher Section */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.05em' }}>
                    Switch Primary Portal:
                  </div>

                  <button
                    onClick={() => { setPrimaryPortal('user'); setHamburgerOpen(false); }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      marginBottom: '6px',
                      border: 'none',
                      backgroundColor: primaryPortal === 'user' ? '#eff6ff' : '#f8fafc',
                      color: primaryPortal === 'user' ? '#1d4ed8' : '#334155',
                      fontWeight: primaryPortal === 'user' ? 700 : 600,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <User size={16} color="#16a34a" />
                    <span>Go to User Portal</span>
                  </button>

                  {isPoolAdmin && (
                    <button
                      onClick={() => { setPrimaryPortal('pool_admin'); setHamburgerOpen(false); }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        border: 'none',
                        backgroundColor: primaryPortal === 'pool_admin' ? '#eff6ff' : '#f8fafc',
                        color: primaryPortal === 'pool_admin' ? '#1d4ed8' : '#334155',
                        fontWeight: primaryPortal === 'pool_admin' ? 700 : 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <Settings size={16} color="#2563eb" />
                      <span>Go to Pool Admin Page</span>
                    </button>
                  )}

                  {isSiteAdmin && (
                    <button
                      onClick={() => { setPrimaryPortal('site_admin'); setHamburgerOpen(false); }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        border: 'none',
                        backgroundColor: primaryPortal === 'site_admin' ? '#fef2f2' : '#f8fafc',
                        color: primaryPortal === 'site_admin' ? '#b91c1c' : '#334155',
                        fontWeight: primaryPortal === 'site_admin' ? 700 : 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <Shield size={16} color="#dc2626" />
                      <span>Go to Site Admin Page</span>
                    </button>
                  )}
                  {(isPoolAdmin || isSiteAdmin) && (
                    <button
                      onClick={() => { setShowCreatePoolModal(true); setHamburgerOpen(false); }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        marginBottom: '6px',
                        border: '1px solid #0284c7',
                        backgroundColor: '#eff6ff',
                        color: '#0284c7',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} color="#0284c7" />
                      <span>➕ Create New Pool</span>
                    </button>
                  )}
                </div>

                {/* Logout Action */}
                <div style={{ paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #fca5a5',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    <LogOut size={16} />
                    <span>Log Out</span>
                  </button>
                </div>

              </div>
            )}

          </div>

        </div>

        {/* Sub-Pages Ribbon Bar */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', gap: '8px', overflowX: 'auto' }}>
          
          {/* Sub-Pages for USER PORTAL */}
          {primaryPortal === 'user' && activePool && (
            <>
              <button
                onClick={() => setUserSubTab('pick_selection')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: userSubTab === 'pick_selection' ? '#2563eb' : '#64748b',
                  borderBottom: userSubTab === 'pick_selection' ? '2px solid #2563eb' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Calendar size={16} />
                <span>Pick Selection</span>
              </button>

              <button
                onClick={() => setUserSubTab('group_picks')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: userSubTab === 'group_picks' ? '#2563eb' : '#64748b',
                  borderBottom: userSubTab === 'group_picks' ? '2px solid #2563eb' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Grid size={16} />
                <span>Group Picks & Standings</span>
              </button>

              <button
                onClick={() => setUserSubTab('user_settings')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: userSubTab === 'user_settings' ? '#2563eb' : '#64748b',
                  borderBottom: userSubTab === 'user_settings' ? '2px solid #2563eb' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Sliders size={16} />
                <span>Settings</span>
              </button>
            </>
          )}

          {/* Sub-Pages for POOL ADMIN PORTAL */}
          {primaryPortal === 'pool_admin' && effectiveActivePool && (
            <>
              <button
                onClick={() => setPoolAdminSubTab('rules_setup')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: poolAdminSubTab === 'rules_setup' ? '#60a5fa' : '#94a3b8',
                  borderBottom: poolAdminSubTab === 'rules_setup' ? '2px solid #3b82f6' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Settings size={16} />
                <span>Season Rules & Setup</span>
              </button>

              <button
                onClick={() => setPoolAdminSubTab('payment_ledger')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: poolAdminSubTab === 'payment_ledger' ? '#60a5fa' : '#94a3b8',
                  borderBottom: poolAdminSubTab === 'payment_ledger' ? '2px solid #3b82f6' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <CreditCard size={16} />
                <span>Payment & Buy-Back Ledger</span>
              </button>

              <button
                onClick={() => setPoolAdminSubTab('schedule_control')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: poolAdminSubTab === 'schedule_control' ? '#60a5fa' : '#94a3b8',
                  borderBottom: poolAdminSubTab === 'schedule_control' ? '2px solid #3b82f6' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Calendar size={16} />
                <span>Schedule Management</span>
              </button>
            </>
          )}

          {/* Sub-Pages for SITE ADMIN PORTAL */}
          {primaryPortal === 'site_admin' && (
            <>
              <button
                onClick={() => setSiteAdminSubTab('user_directory')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: siteAdminSubTab === 'user_directory' ? '#fca5a5' : '#f87171',
                  borderBottom: siteAdminSubTab === 'user_directory' ? '2px solid #ef4444' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Users size={16} />
                <span>User Directory & Allowlist</span>
              </button>

              <button
                onClick={() => setSiteAdminSubTab('pool_management')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: siteAdminSubTab === 'pool_management' ? '#fca5a5' : '#f87171',
                  borderBottom: siteAdminSubTab === 'pool_management' ? '2px solid #ef4444' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Layers size={16} />
                <span>Pool Directory</span>
              </button>

              <button
                onClick={() => setSiteAdminSubTab('schedule_engine')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: siteAdminSubTab === 'schedule_engine' ? '#fca5a5' : '#f87171',
                  borderBottom: siteAdminSubTab === 'schedule_engine' ? '2px solid #ef4444' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Calendar size={16} />
                <span>NFL Schedule Engine</span>
              </button>

              <button
                onClick={() => setSiteAdminSubTab('db_explorer')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: siteAdminSubTab === 'db_explorer' ? '#fca5a5' : '#f87171',
                  borderBottom: siteAdminSubTab === 'db_explorer' ? '2px solid #ef4444' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Database size={16} />
                <span>Database Explorer & SQL</span>
              </button>

              <button
                onClick={() => setSiteAdminSubTab('diagnostics')}
                style={{
                  padding: '12px 18px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  color: siteAdminSubTab === 'diagnostics' ? '#fca5a5' : '#f87171',
                  borderBottom: siteAdminSubTab === 'diagnostics' ? '2px solid #ef4444' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Activity size={16} />
                <span>System Diagnostics</span>
              </button>
            </>
          )}

        </div>
      </header>

      {/* ── Main Content Area ────────────────────────────────────────────────── */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px', backgroundColor: '#ffffff' }}>
        
        {/* Toast Notification */}
        {toast && (
          <div style={{
            marginBottom: '20px',
            padding: '14px 18px',
            borderRadius: '10px',
            backgroundColor: toast.type === 'success' ? '#f0fdf4' : toast.type === 'info' ? '#eff6ff' : '#fef2f2',
            border: toast.type === 'success' ? '1px solid #86efac' : toast.type === 'info' ? '1px solid #bfdbfe' : '1px solid #fca5a5',
            color: toast.type === 'success' ? '#15803d' : toast.type === 'info' ? '#1e40af' : '#991b1b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.9rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
              <span>{toast.text}</span>
            </div>
            <button onClick={() => setToast(null)} style={{ backgroundColor: 'transparent', color: 'inherit', border: 'none', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        )}

        {/* 🏆 EMPTY POOLS STATE (Hardened Admin vs Participant Distinction) */}
        {!effectiveActivePool && userPools.length === 0 && primaryPortal !== 'site_admin' && (
          <div style={{
            backgroundColor: '#f8fafc',
            border: '2px dashed #cbd5e1',
            borderRadius: '16px',
            padding: '48px 24px',
            textAlign: 'center',
            marginTop: '32px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏆</div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', margin: '0 0 8px 0' }}>
              No Active Pools Found
            </h2>
            <p style={{ fontSize: '0.92rem', color: '#64748b', maxWidth: '480px', margin: '0 auto 24px auto', lineHeight: 1.5 }}>
              {(isPoolAdmin || isSiteAdmin)
                ? 'You are not currently a member of any active Survivor Pools. Create a new pool to begin or check back when you receive an invitation.'
                : 'You are not currently participating in any active Survivor Pools. Check back when your Pool Admin sends you an invitation to join a new pool.'}
            </p>

            {(isPoolAdmin || isSiteAdmin) ? (
              <button
                onClick={() => setShowCreatePoolModal(true)}
                style={{
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>➕ Create New Pool Now</span>
              </button>
            ) : (
              <button
                onClick={() => checkAuth()}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#2563eb',
                  border: '1px solid #bfdbfe',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw size={16} />
                <span>Refresh Dashboard</span>
              </button>
            )}
          </div>
        )}

        {/* 📩 PENDING POOL INVITATION BANNER */}
        {activePool?.invite_status === 'pending' && (
          <div style={{
            backgroundColor: '#eff6ff',
            border: '2px solid #3b82f6',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '24px',
            boxShadow: '0 6px 18px rgba(59, 130, 246, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📩 Pool Invitation: {activePool.name}</span>
                <span style={{ fontSize: '0.7rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
                  INVITATION PENDING
                </span>
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.4 }}>
                You have been invited to join <strong>{activePool.name}</strong> as a participant. Would you like to accept this invitation and enter the pool?
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleRespondPoolInvite(activePool.id, 'decline')}
                style={{ backgroundColor: '#ffffff', color: '#dc2626', border: '1px solid #fca5a5', padding: '10px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}
              >
                ❌ Decline Invitation
              </button>
              <button
                onClick={() => handleRespondPoolInvite(activePool.id, 'accept')}
                style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer', boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)' }}
              >
                ✅ Accept & Enter Pool
              </button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════ */}
        {/* PORTAL 1: USER PORTAL                                                 */}
        {/* ══════════════════════════════════════════════════════════════════════ */}
        {primaryPortal === 'user' && activePool && (
          <div>

            {/* ⚠️ Top-of-Screen Payment Pending Alert Banner */}
            {(() => {
              const currentUserPay = (userPayments || []).find((p) => (p?.user_id && authUser?.id && p.user_id === authUser.id) || (p?.email && authUser?.email && p.email.toLowerCase() === authUser.email.toLowerCase()));

              const buyInCost = gridData?.poolConfig?.buyInCost ?? adminSettings?.buy_in_cost ?? 100;
              const buyBackCost = gridData?.poolConfig?.buyBackCost ?? adminSettings?.buy_back_cost ?? 100;
              const myGridRow = (gridData?.grid || []).find((g) => (g?.user?.id && authUser?.id && g.user.id === authUser.id) || (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()));
              const myBuybacksCount = myGridRow?.buybacksUsed ?? (currentUserPay?.buy_backs_used || 0);
              const totalOwed = buyInCost + (myBuybacksCount * buyBackCost);
              
              const buyInPaid = Number(currentUserPay?.buy_in_paid) === 1 || (typeof currentUserPay?.total_paid === 'number' && currentUserPay.total_paid >= buyInCost);
              const totalPaid = typeof currentUserPay?.total_paid === 'number' && currentUserPay.total_paid > 0 ? currentUserPay.total_paid : (buyInPaid ? buyInCost : 0);
              
              // Structural Rule: Unpaid if buyInPaid is false OR totalPaid < totalOwed (when buyInCost > 0)
              const isUnpaid = (!buyInPaid || totalPaid < totalOwed) && buyInCost > 0;

              if (!isUnpaid) return null;

              return (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fca5a5',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px',
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.06)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <AlertCircle size={20} color="#dc2626" />
                    <div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#991b1b' }}>
                        ⚠️ Buy-In Payment Pending (${totalOwed - totalPaid} Due)
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#b91c1c', marginTop: '2px' }}>
                        {(gridData?.poolConfig?.paymentInstructions || adminSettings?.payment_instructions) ? (
                          <span>
                            <strong>Payment Instructions from Administrator:</strong> {gridData?.poolConfig?.paymentInstructions || adminSettings?.payment_instructions}
                          </span>
                        ) : (
                          <span>Please submit payment to your pool administrator via an approved method.</span>
                        )}
                        {currentUserPay?.notes && currentUserPay.notes !== 'Registered Participant' && (
                          <span style={{ fontWeight: 700, marginLeft: '6px', backgroundColor: '#fee2e2', padding: '1px 6px', borderRadius: '4px' }}>
                            My Note: "{currentUserPay.notes}"
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setPaymentNoteInput(currentUserPay?.notes === 'Registered Participant' ? '' : (currentUserPay?.notes || ''));
                      setShowPaymentNoteModal(true);
                    }}
                    style={{
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 2px 6px rgba(220, 38, 38, 0.2)'
                    }}
                  >
                    <DollarSign size={14} /> Provide Payment Note / Handle
                  </button>
                </div>
              );
            })()}
            
            {/* SUB-PAGE 1: PICK SELECTION */}
            {userSubTab === 'pick_selection' && (
              <div>
                {/* 1. Unlaunched Draft Pool Banner (Top Priority Position Above Schedule) */}
                {settingsLoaded && adminSettings.pool_status !== 'active' && (
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <Rocket size={22} color="#b45309" />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#92400e' }}>
                        🚀 Pool Setup in Progress
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#78350f', marginTop: '2px' }}>
                        The administrator has not launched the 2026 season schedule yet. Rules & settings are open for review, but pick selection will open upon launch.
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Week Header Bar with Live Countdown Timer */}
                {(() => {
                  const currentPoolWeek = gridData?.currentWeek || 1;
                  const isPastWeek = activeWeek < currentPoolWeek;
                  const isFutureWeek = activeWeek > currentPoolWeek;
                  const isRollingMode = adminSettings.pick_opening_rule === 'weekly_rolling';
                  const isPoolSetupMode = adminSettings.pool_status !== 'active';

                  let statusSubtitle = activeWeek === currentPoolWeek ? 'Current Active Pool Week' : isPastWeek ? 'Past Week Results' : 'Upcoming Schedule';
                  let countdownText = '';
                  let statusColor = '#2563eb';

                  if (isPoolSetupMode) {
                    countdownText = '';
                  } else if (isPastWeek) {
                    countdownText = '🏁 Week Concluded & Locked';
                    statusColor = '#64748b';
                  } else if (isFutureWeek && isRollingMode) {
                    countdownText = `⏳ Pick Window Closed (Opens Tuesday of Week ${activeWeek} at 3:00 AM ET)`;
                    statusColor = '#d97706';
                  } else if (isFutureWeek) {
                    countdownText = '🔓 Early Pick Selection Available';
                    statusColor = '#2563eb';
                  } else {
                    const lockRule = adminSettings.pick_lock_rule || 'thursday_kickoff';
                    const activeGames = games.filter((g) => (g.week || currentPoolWeek) === activeWeek);
                    const sortedGames = activeGames
                      .map((g) => ({
                        game: g,
                        kickoffMs: g.kickoffDate ? new Date(g.kickoffDate).getTime() : (g.date ? new Date(g.date).getTime() : 0)
                      }))
                      .filter((g) => g.kickoffMs > 0)
                      .sort((a, b) => a.kickoffMs - b.kickoffMs);

                    if (lockRule === 'individual_kickoff') {
                      const unstarted = sortedGames.filter((g) => g.kickoffMs > currentTime);
                      if (unstarted.length === 0) {
                        countdownText = `🔒 All Picks Locked for Week ${activeWeek}`;
                        statusColor = '#475569';
                      } else {
                        const nextGame = unstarted[0];
                        const isFirstGame = unstarted.length === sortedGames.length;
                        const diff = Math.max(0, nextGame.kickoffMs - currentTime);
                        const timerStr = formatCountdownTimer(diff);
                        countdownText = (isFirstGame ? '⏳ First Game Lock (TNF) In: ' : '⏳ Next Game Lock In: ') + timerStr;
                        statusColor = '#2563eb';
                      }
                    } else {
                      let targetDeadlineMs = 0;
                      let ruleName = 'Kickoff';

                      if (lockRule === 'thursday_kickoff') {
                        targetDeadlineMs = sortedGames[0]?.kickoffMs || 0;
                        ruleName = 'Thursday Kickoff';
                      } else if (lockRule === 'saturday_kickoff') {
                        const satGame = sortedGames.find((g) => new Date(g.kickoffMs).getDay() === 6);
                        const sunGame = sortedGames.find((g) => new Date(g.kickoffMs).getDay() === 0);
                        const targetGame = satGame || sunGame || sortedGames[0];
                        targetDeadlineMs = targetGame?.kickoffMs || 0;
                        ruleName = satGame ? 'Saturday Kickoff' : 'Sunday Kickoff';
                      } else if (lockRule === 'sunday_early') {
                        const sunGame = sortedGames.find((g) => new Date(g.kickoffMs).getDay() === 0) || sortedGames[0];
                        targetDeadlineMs = sunGame?.kickoffMs || 0;
                        ruleName = 'Sunday 1:00 PM ET';
                      }

                      if (!targetDeadlineMs) {
                        countdownText = '🟢 Picks Open';
                        statusColor = '#2563eb';
                      } else {
                        const diff = targetDeadlineMs - currentTime;
                        if (diff <= 0) {
                          countdownText = `🔒 Pick Window Locked (${ruleName} Deadline Passed)`;
                          statusColor = '#475569';
                        } else {
                          const timerStr = formatCountdownTimer(diff);
                          countdownText = `⏳ Pick Lock Deadline In: ${timerStr} (${ruleName})`;
                          statusColor = '#16a34a';
                        }
                      }
                    }
                  }

                  return (
                    <div style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #cbd5e1',
                      padding: '14px 20px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Calendar size={20} color="#2563eb" />
                        <div>
                          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                            NFL Schedule — Week {activeWeek} of 18
                          </h3>
                          <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: '3px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span>{statusSubtitle}</span>
                            {countdownText && (
                              <>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <span style={{ color: statusColor === '#475569' ? '#475569' : statusColor, fontWeight: 700 }}>{countdownText}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {(activePool?.role_in_pool === 'pool_admin' || authUser?.global_role === 'site_admin') && (
                        <span style={{ fontSize: '0.78rem', color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '6px', fontWeight: 700 }}>
                          Schedule Source: {espnSource === 'pool_d1' ? 'D1 Snapshot' : 'Live ESPN Scoreboard'}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* 2. Pre-Pool Week Banner */}
                {adminSettings.season_start_week && activeWeek < adminSettings.season_start_week && (
                  <div style={{
                    backgroundColor: '#eff6ff',
                    border: '1px solid #93c5fd',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <Calendar size={22} color="#1d4ed8" />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#1e40af' }}>
                        ⏸️ Pre-Pool Period (Week {activeWeek})
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#1d4ed8', marginTop: '2px' }}>
                        Pool scoring & eliminations begin in Week {adminSettings.season_start_week}. Weeks 1 through {adminSettings.season_start_week - 1} are marked as pre-pool weeks.
                      </div>
                    </div>
                  </div>
                )}

                {(() => {
                  const userGrid = (gridData?.grid || []).find((g) => 
                    (g?.user?.id && authUser?.id && g.user.id === authUser.id) ||
                    (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) ||
                    (g?.user?.name && authUser?.name && g.user.name.toLowerCase() === authUser.name.toLowerCase())
                  );
                  const isEliminated = userGrid ? !userGrid.isAlive : false;
                  const isGhostEliminated = isEliminated && userGrid && !userGrid.weeklyPicks[userGrid.eliminatedWeek!];
                  const canBuyback = isEliminated && !isGhostEliminated && (adminSettings.max_buy_backs || 1) > 0 && activeWeek <= (adminSettings.buy_back_max_week || 3);
                  const currentUserPay = (userPayments || []).find((p) => (p?.email && authUser?.email && p.email.toLowerCase() === authUser.email.toLowerCase()) || (p?.user_id && authUser?.id && p.user_id === authUser.id));
                  const isPendingBuyback = (adminBuybacks || []).some((b) => (b?.email && authUser?.email && b.email.toLowerCase() === authUser.email.toLowerCase()) || (b?.user_id && authUser?.email && b.user_id === authUser.email));

                  if (isGhostEliminated) {
                    return (
                      <div style={{
                        backgroundColor: '#fef2f2',
                        border: '2px solid #ef4444',
                        borderRadius: '14px',
                        padding: '20px 24px',
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        boxShadow: '0 4px 14px rgba(239, 68, 68, 0.15)'
                      }}>
                        <div style={{ fontSize: '2.2rem' }}>👻</div>
                        <div>
                          <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#991b1b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>Eliminated in Week {userGrid?.eliminatedWeek} — No Pick Submitted</span>
                          </h4>
                          <p style={{ fontSize: '0.85rem', color: '#b91c1c', marginTop: '4px', lineHeight: 1.4, fontWeight: 500, margin: 0 }}>
                            You were eliminated for failing to submit a team selection prior to deadline. Per pool rules, no-pick eliminations are ineligible for buy-back re-entry.
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return canBuyback ? (
                    <div style={{
                      backgroundColor: '#fef3c7',
                      border: '2px solid #f59e0b',
                      borderRadius: '14px',
                      padding: '20px 24px',
                      marginBottom: '24px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '16px',
                      boxShadow: '0 4px 14px rgba(245, 158, 11, 0.2)'
                    }}>
                      <div>
                        <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <RefreshCw size={20} color="#d97706" />
                          <span>🔁 Survivor Pool Buy-Back Opportunity (${adminSettings.buy_back_cost})</span>
                        </h4>
                        <p style={{ fontSize: '0.85rem', color: '#78350f', marginTop: '4px', lineHeight: 1.4, fontWeight: 500 }}>
                          You were eliminated in Week {userGrid?.eliminatedWeek}. Pool rules permit buy-backs through Week {adminSettings.buy_back_max_week}. {isPendingBuyback ? 'Your buy-back request is pending admin approval. Lock in a Provisional Pick below.' : 'Requesting re-entry places you in pending payment status until approved by your Pool Admin.'}
                        </p>
                      </div>

                      <button
                        onClick={() => handleRequestBuyback()}
                        disabled={requestingBuyback || isPendingBuyback}
                        style={{
                          backgroundColor: isPendingBuyback ? '#16a34a' : '#d97706',
                          color: '#ffffff',
                          padding: '12px 24px',
                          borderRadius: '10px',
                          fontWeight: 800,
                          fontSize: '0.92rem',
                          border: 'none',
                          cursor: requestingBuyback || isPendingBuyback ? 'default' : 'pointer',
                          boxShadow: '0 4px 12px rgba(217, 119, 6, 0.35)',
                          transition: 'all 0.15s'
                        }}
                      >
                        {isPendingBuyback ? '⏳ Buy-Back Requested (Pending Admin Confirmation)' : `Request Buy-Back ($${adminSettings.buy_back_cost})`}
                      </button>
                    </div>
                  ) : null;
                })()}

                {/* Calculate Featured Locked Game for Top Promotion & Hero Block */}
                {(() => {
                  const currentPickTeam = usedTeamsMap[activeWeek];
                  const currentPickAbbrev = currentPickTeam ? getTeamAbbrev(currentPickTeam) : '';

                  const featuredLockedGame = games.find((g) => {
                    if (!currentPickAbbrev) return false;
                    const hAbbrev = getTeamAbbrev(g.homeTeam?.abbrev || g.homeTeam?.name || '');
                    const aAbbrev = getTeamAbbrev(g.awayTeam?.abbrev || g.awayTeam?.name || '');
                    const matchesPick = hAbbrev === currentPickAbbrev || aAbbrev === currentPickAbbrev;
                    if (!matchesPick) return false;

                    const statusStr = (g.status || '').toLowerCase().trim();
                    const isFinal = statusStr === 'final' || statusStr === 'status_final' || statusStr === 'post';
                    const isInProgress = !isFinal && (statusStr === 'in' || statusStr === 'in_progress' || statusStr === 'status_in_progress');
                    const kickoffMs = g.kickoffDate ? new Date(g.kickoffDate).getTime() : (g.date ? new Date(g.date).getTime() : 0);
                    const isKickoffPassed = kickoffMs > 0 && Date.now() >= kickoffMs;

                    return isFinal || isInProgress || isKickoffPassed;
                  });

                  const hasPick = !!currentPickTeam;
                  const isPickLocked = !!featuredLockedGame;
                  const teamAbbrev = hasPick ? currentPickAbbrev : '';
                  const teamTheme = hasPick ? (NFL_TEAM_THEMES[teamAbbrev] || { primary: '#2563eb', bgTint: '#eff6ff', border: '#3b82f6', text: '#1e40af' }) : null;
                  const teamLogoUrl = hasPick ? `https://a.espncdn.com/i/teamlogos/nfl/500/scoreboard/${teamAbbrev.toLowerCase()}.png` : '';

                  const cardBg = hasPick ? teamTheme!.bgTint : '#fffbeb';
                  const cardBorder = hasPick ? `2px solid ${teamTheme!.border}` : '1px solid #fcd34d';
                  const labelColor = hasPick ? teamTheme!.text : '#b45309';
                  const titleColor = hasPick ? teamTheme!.primary : '#d97706';
                  const iconColor = hasPick ? teamTheme!.border : '#d97706';

                  const displayGames = games.filter((g) => {
                    if (!g.homeTeam?.name || !g.awayTeam?.name) return false;
                    if (featuredLockedGame && g.id === featuredLockedGame.id) return false; // Filter out featured locked matchup!
                    return true;
                  });

                  // Maintain initial page load order (3-Tier Sort) without in-session layout jumping
                  displayGames.sort((a, b) => {
                    const idA = String(a.id);
                    const idB = String(b.id);
                    const indexA = cardOrderIds.indexOf(idA);
                    const indexB = cardOrderIds.indexOf(idB);
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return 0;
                  });

                  const userGridRow = (gridData?.grid || []).find((g) => 
                    (g?.user?.id && authUser?.id && g.user.id === authUser.id) ||
                    (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) ||
                    (g?.user?.name && authUser?.name && g.user.name.toLowerCase() === authUser.name.toLowerCase())
                  );
                  const isUserAlive = userGridRow ? userGridRow.isAlive : true;
                  const userEliminatedWeek = userGridRow?.eliminatedWeek || 0;
                  const isUserPendingBuyback = (adminBuybacks || []).some((b) => (b?.email && authUser?.email && b.email.toLowerCase() === authUser.email.toLowerCase()) || (b?.user_id && authUser?.email && b.user_id === authUser.email));
                  const canShowHeroCard = isUserAlive || (userEliminatedWeek > 0 && activeWeek <= userEliminatedWeek) || isUserPendingBuyback;

                  return (
                    <div>
                      {/* HERO BLOCK & TEAMS USED (Only shown when pool is launched/active) */}
                      {adminSettings.pool_status === 'active' && (
                        <>
                          {/* HERO BLOCK: Pick Status Card & Featured Locked Matchup Top Banner */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                            
                            {/* Pick Status Top Card (Only shown if user is active or making a provisional pick) */}
                            {canShowHeroCard && (
                              (loadingGrid || !gridData) ? (
                                <div style={{
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '12px',
                                  border: '1px solid #e2e8f0',
                                  padding: '18px 20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, width: '100%' }}>
                                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: '#cbd5e1', flexShrink: 0, opacity: 0.6 }} />
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                                        Week {activeWeek} Pick Status ({formatDisplayName(authUser.name, authUser.email)}):
                                      </div>
                                      <div style={{ fontSize: '1.18rem', fontWeight: 800, marginTop: '2px', color: '#94a3b8' }}>
                                        Loading Pick Status...
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div style={{
                                  backgroundColor: cardBg,
                                  borderRadius: '12px',
                                  border: cardBorder,
                                  padding: '18px 20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  boxShadow: hasPick ? `0 4px 14px ${teamTheme!.border}25` : '0 2px 4px rgba(0,0,0,0.02)',
                                  transition: 'all 0.2s'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                                    {hasPick && (
                                      <img
                                        src={teamLogoUrl}
                                        alt={currentPickTeam}
                                        style={{ width: '42px', height: '42px', objectFit: 'contain', flexShrink: 0 }}
                                      />
                                    )}
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: '0.78rem', color: labelColor, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                                        Week {activeWeek} Pick Status ({formatDisplayName(authUser.name, authUser.email)}):
                                      </div>
                                      <div style={{ fontSize: '1.18rem', fontWeight: 800, marginTop: '2px', color: titleColor, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <span>{hasPick ? (isPickLocked ? `Locked Pick: ${currentPickTeam}` : `Selected Pick: ${currentPickTeam}`) : 'No Pick Selected Yet'}</span>
                                        <span>{isPickLocked ? '🔒' : hasPick ? '🔓' : '⚠️'}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ flexShrink: 0, marginLeft: '12px' }}>
                                    {isPickLocked ? (
                                      <CheckCircle2 size={32} color={iconColor} />
                                    ) : hasPick ? (
                                      <CheckCircle2 size={32} color="#2563eb" />
                                    ) : (
                                      <Clock size={32} color={iconColor} />
                                    )}
                                  </div>
                                </div>
                              )
                            )}

                            {/* Featured Top Matchup Card (Tinted with Selected Team Brand Colors) */}
                            {featuredLockedGame && (() => {
                              const fg = featuredLockedGame;
                              const fHomeName = fg.homeTeam?.name || 'TBD Team';
                              const fAwayName = fg.awayTeam?.name || 'TBD Team';
                              const fHomeAbbrev = getTeamAbbrev(fg.homeTeam?.abbrev || fHomeName);
                              const fAwayAbbrev = getTeamAbbrev(fg.awayTeam?.abbrev || fAwayName);

                              const fIsHomePick = currentPickAbbrev === fHomeAbbrev;
                              const fIsAwayPick = currentPickAbbrev === fAwayAbbrev;

                              const featuredTheme = teamTheme || { primary: '#2563eb', bgTint: '#eff6ff', border: '#3b82f6', text: '#1e40af' };

                              const fStatusStr = (fg.status || '').toLowerCase().trim();
                              const fIsFinal = fStatusStr === 'final' || fStatusStr === 'status_final' || fStatusStr === 'post';
                              const fIsInProgress = !fIsFinal && (fStatusStr === 'in' || fStatusStr === 'in_progress' || fStatusStr === 'status_in_progress');

                              const fHomeScoreNum = parseInt(String(fg.homeTeam?.score ?? fg.homeScore ?? -1), 10);
                              const fAwayScoreNum = parseInt(String(fg.awayTeam?.score ?? fg.awayScore ?? -1), 10);

                              const fHomeWinner = fIsFinal && fHomeScoreNum > fAwayScoreNum;
                              const fAwayWinner = fIsFinal && fAwayScoreNum > fHomeScoreNum;

                              return (
                                <div style={{
                                  backgroundColor: featuredTheme.bgTint,
                                  borderRadius: '12px',
                                  border: `2px solid ${featuredTheme.border}`,
                                  padding: '14px 16px',
                                  boxShadow: `0 4px 14px ${featuredTheme.border}30`,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px',
                                  transition: 'all 0.2s'
                                }}>
                                  {/* Clean Header Bar (1-Line Compact Layout) */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: `1px solid ${featuredTheme.border}33`, fontSize: '0.73rem', gap: '8px' }}>
                                    <div style={{ color: featuredTheme.text, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <span>{fg.date ? formatKickoffWithTimeZone(fg.date, userTimeZone) : fg.kickoffFormatted}</span>
                                    </div>
                                    <div style={{ fontSize: '0.71rem', color: featuredTheme.text, fontWeight: 500, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>
                                      📍 {shortenVenueName(fg.venue)}{getWeatherDisplayCompact(fg.weatherDisplay, fg.venue) ? ` (${getWeatherDisplayCompact(fg.weatherDisplay, fg.venue)})` : ''}
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {/* Away Team Row */}
                                    <div style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      padding: '8px 12px', borderRadius: '8px',
                                      border: fIsAwayPick ? `1px solid ${featuredTheme.border}` : '1px solid #cbd5e1',
                                      borderLeft: fIsAwayPick ? `4px solid ${featuredTheme.primary}` : '1px solid #cbd5e1',
                                      backgroundColor: fIsAwayPick ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                                      color: fIsAwayPick ? featuredTheme.primary : '#475569',
                                      boxShadow: fIsAwayPick ? '0 2px 6px rgba(0,0,0,0.04)' : 'none'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <img src={fg.awayTeam?.logo || `https://a.espncdn.com/i/teamlogos/nfl/500/scoreboard/${fAwayAbbrev.toLowerCase()}.png`} alt={fAwayName} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                        <span style={{ fontWeight: 800, fontSize: '0.88rem' }}>{fAwayAbbrev}</span>
                                        <span style={{ fontSize: '0.72rem', color: fIsAwayPick ? featuredTheme.primary : '#64748b', fontWeight: fIsAwayPick ? 600 : 400 }}>
                                          ({fg.awayTeam?.record || '0-0'}) Away
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {fIsFinal ? (
                                          <span style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e293b' }}>
                                            <span>{fAwayScoreNum >= 0 ? fAwayScoreNum : ''}</span>
                                            {fAwayWinner ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />}
                                            {fIsAwayPick && (
                                              <span style={{ fontSize: '0.7rem', backgroundColor: featuredTheme.primary, color: '#ffffff', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Lock size={11} color="#ffffff" /> YOUR PICK
                                              </span>
                                            )}
                                          </span>
                                        ) : fIsInProgress ? (
                                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>{fAwayScoreNum >= 0 ? fAwayScoreNum : ''}</span>
                                            <span style={{ fontSize: '0.7rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 4px', borderRadius: '3px' }}>LIVE</span>
                                            {fIsAwayPick && (
                                              <span style={{ fontSize: '0.7rem', backgroundColor: featuredTheme.primary, color: '#ffffff', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Lock size={11} color="#ffffff" /> YOUR PICK
                                              </span>
                                            )}
                                          </span>
                                        ) : fIsAwayPick ? (
                                          <span style={{ fontSize: '0.72rem', backgroundColor: featuredTheme.primary, color: '#fff', padding: '3px 9px', borderRadius: '5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Lock size={12} color="#ffffff" /> YOUR PICK
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>

                                    {/* Home Team Row */}
                                    <div style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      padding: '8px 12px', borderRadius: '8px',
                                      border: fIsHomePick ? `1px solid ${featuredTheme.border}` : '1px solid #cbd5e1',
                                      borderLeft: fIsHomePick ? `4px solid ${featuredTheme.primary}` : '1px solid #cbd5e1',
                                      backgroundColor: fIsHomePick ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                                      color: fIsHomePick ? featuredTheme.primary : '#475569',
                                      boxShadow: fIsHomePick ? '0 2px 6px rgba(0,0,0,0.04)' : 'none'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <img src={fg.homeTeam?.logo || `https://a.espncdn.com/i/teamlogos/nfl/500/scoreboard/${fHomeAbbrev.toLowerCase()}.png`} alt={fHomeName} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                        <span style={{ fontWeight: 800, fontSize: '0.88rem' }}>{fHomeAbbrev}</span>
                                        <span style={{ fontSize: '0.72rem', color: fIsHomePick ? featuredTheme.primary : '#64748b', fontWeight: fIsHomePick ? 600 : 400 }}>
                                          ({fg.homeTeam?.record || '0-0'}) Home
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {fIsFinal ? (
                                          <span style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e293b' }}>
                                            <span>{fHomeScoreNum >= 0 ? fHomeScoreNum : ''}</span>
                                            {fHomeWinner ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />}
                                            {fIsHomePick && (
                                              <span style={{ fontSize: '0.7rem', backgroundColor: featuredTheme.primary, color: '#ffffff', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Lock size={11} color="#ffffff" /> YOUR PICK
                                              </span>
                                            )}
                                          </span>
                                        ) : fIsInProgress ? (
                                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>{fHomeScoreNum >= 0 ? fHomeScoreNum : ''}</span>
                                            <span style={{ fontSize: '0.7rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 4px', borderRadius: '3px' }}>LIVE</span>
                                            {fIsHomePick && (
                                              <span style={{ fontSize: '0.7rem', backgroundColor: featuredTheme.primary, color: '#ffffff', padding: '2px 8px', borderRadius: '4px', fontWeight: 700, marginLeft: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Lock size={11} color="#ffffff" /> YOUR PICK
                                              </span>
                                            )}
                                          </span>
                                        ) : fIsHomePick ? (
                                          <span style={{ fontSize: '0.72rem', backgroundColor: featuredTheme.primary, color: '#fff', padding: '3px 9px', borderRadius: '5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Lock size={12} color="#ffffff" /> YOUR PICK
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Featured Matchup Bottom Banner Bar (2-Column Split Footer Layout) */}
                                  <div style={{
                                    marginTop: '2px',
                                    paddingTop: '6px',
                                    borderTop: `1px solid ${featuredTheme.border}33`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '6px'
                                  }}>
                                    {/* Left: Game State Text */}
                                    <div style={{
                                      fontSize: '0.72rem',
                                      color: fIsInProgress ? '#1d4ed8' : featuredTheme.primary,
                                      fontWeight: 700,
                                      letterSpacing: '0.02em',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}>
                                      {(() => {
                                        if (fIsFinal) {
                                          return (fg.statusDetail || '').toLowerCase().includes('final') ? (fg.statusDetail || 'Final') : 'Final';
                                        }
                                        if (fIsInProgress) {
                                          const rawDetail = (fg.statusDetail || '').trim();
                                          const isDateOrTime = rawDetail.toLowerCase().includes('at ') || rawDetail.includes(',') || rawDetail.toLowerCase().includes('scheduled') || rawDetail.toLowerCase().includes('pm') || rawDetail.toLowerCase().includes('am');
                                          return (rawDetail && !isDateOrTime) ? rawDetail : 'Live';
                                        }
                                        const oddsText = (fg.oddsDetails || '').trim();
                                        if (oddsText && oddsText.toUpperCase() !== 'OFF' && oddsText.toUpperCase() !== 'N/A' && oddsText.toUpperCase() !== 'NONE') {
                                          return `Line: ${oddsText}`;
                                        }
                                        return 'Locked';
                                      })()}
                                    </div>

                                    {/* Right: Featured Matchup Pill */}
                                    <span style={{
                                      fontSize: '0.70rem',
                                      backgroundColor: '#ffffff',
                                      border: `1px solid ${featuredTheme.border}`,
                                      color: featuredTheme.primary,
                                      padding: '2px 8px',
                                      borderRadius: '6px',
                                      fontWeight: 800,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                                    }}>
                                      ⭐ YOUR FEATURED MATCHUP
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Used Teams List (Survivor Restriction Check - Only shown starting W2 or Wn+1 from pool start week) */}
                          {activeWeek > ((activePool as any)?.season_start_week || adminSettings?.season_start_week || 1) && (
                            <div style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              padding: '16px 20px',
                              marginBottom: '24px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}>
                              <div style={{ fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '8px' }}>
                                Teams Used Previously (Cannot Pick Again):
                              </div>
                              {Object.keys(usedTeamsMap).length === 0 ? (
                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>No teams used yet. All 32 NFL teams available!</div>
                              ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {Object.entries(usedTeamsMap).map(([wStr, team]) => {
                                    const w = parseInt(wStr, 10);
                                    const myUserGrid = (gridData?.grid || []).find((g) => 
                                      (g?.user?.id && authUser?.id && g.user.id === authUser.id) ||
                                      (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) ||
                                      (g?.user?.name && authUser?.name && g.user.name.toLowerCase() === authUser.name.toLowerCase())
                                    );
                                    const pickObj = myUserGrid?.weeklyPicks?.[w];
                                    const status = pickObj?.status || 'pending';
                                    const isBuybackUsed = pickObj?.buybackUsed || (adminBuybacks || []).some((b) => (b?.email === authUser?.email || b?.user_id === authUser?.email) && b.week === w && b.status === 'approved');

                                    const isWin = status === 'win';
                                    const isLoss = status === 'loss';

                                    const bgColor = isWin ? '#dcfce7' : (isLoss && isBuybackUsed) ? '#fef3c7' : isLoss ? '#fee2e2' : '#eff6ff';
                                    const borderColor = isWin ? '#86efac' : (isLoss && isBuybackUsed) ? '#fde047' : isLoss ? '#fca5a5' : '#93c5fd';
                                    const textColor = isWin ? '#15803d' : (isLoss && isBuybackUsed) ? '#b45309' : isLoss ? '#b91c1c' : '#1d4ed8';

                                    return (
                                      <span
                                        key={wStr}
                                        style={{
                                          backgroundColor: bgColor,
                                          border: `1px solid ${borderColor}`,
                                          borderRadius: '6px',
                                          padding: '4px 10px',
                                          fontSize: '0.78rem',
                                          color: textColor,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          fontWeight: 700
                                        }}>
                                        <Lock size={12} color={textColor} />
                                        <span>W{wStr}: {getTeamAbbrev(team)}</span>
                                        {isWin && <CheckCircle2 size={12} color="#15803d" />}
                                        {isLoss && isBuybackUsed && (
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#b45309', fontWeight: 800 }} title="Buy-Back Approved (Re-entered Pool)">
                                            <XCircle size={12} color="#b91c1c" />
                                            <span>🔁</span>
                                          </span>
                                        )}
                                        {isLoss && !isBuybackUsed && <XCircle size={12} color="#b91c1c" />}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Main Matchups Grid Section Title */}
                      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a' }}>
                            <Trophy size={18} color="#2563eb" />
                            <span>{featuredLockedGame ? `Other Week ${activeWeek} NFL Matchups` : `Week ${activeWeek} NFL Matchups & Betting Spreads`}</span>
                          </h2>
                          {featuredLockedGame && (
                            <p style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>
                              Your locked pick matchup is featured in the top header banner above.
                            </p>
                          )}
                        </div>
                        {loadingGames && (
                          <span style={{ fontSize: '0.8rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <RefreshCw size={14} className="spin" /> Loading schedule...
                          </span>
                        )}
                      </div>

                      {/* Filtered Main Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
                        {displayGames.map((g) => {
                          const currentUserPay = (userPayments || []).find((p) => (p.email && authUser?.email && p.email.toLowerCase() === authUser.email.toLowerCase()) || (p.user_id && authUser?.id && p.user_id === authUser.id));
                          const buyInCost = adminSettings.buy_in_cost || 100;
                          const buyBackCost = adminSettings.buy_back_cost || 100;
                          const totalOwed = currentUserPay ? (buyInCost + ((currentUserPay.buy_backs_used || 0) * buyBackCost)) : buyInCost;
                          const totalPaid = currentUserPay ? (typeof currentUserPay.total_paid === 'number' ? currentUserPay.total_paid : (currentUserPay.buy_in_paid ? buyInCost : 0)) : 0;
                          const isUnpaid = !!currentUserPay && totalPaid < totalOwed && currentUserPay.buy_in_paid === 0;

                          const homeName = g.homeTeam?.name || 'TBD Team';
                          const awayName = g.awayTeam?.name || 'TBD Team';
                          const homeAbbrev = getTeamAbbrev(g.homeTeam?.abbrev || homeName);
                          const awayAbbrev = getTeamAbbrev(g.awayTeam?.abbrev || awayName);

                          const homeUsedInOtherWeek = Object.entries(usedTeamsMap).find(([wStr, t]) => getTeamAbbrev(t) === homeAbbrev && parseInt(wStr, 10) !== activeWeek);
                          const awayUsedInOtherWeek = Object.entries(usedTeamsMap).find(([wStr, t]) => getTeamAbbrev(t) === awayAbbrev && parseInt(wStr, 10) !== activeWeek);
                          const isHomeCurrentPick = !!usedTeamsMap[activeWeek] && getTeamAbbrev(usedTeamsMap[activeWeek]) === homeAbbrev;
                          const isAwayCurrentPick = !!usedTeamsMap[activeWeek] && getTeamAbbrev(usedTeamsMap[activeWeek]) === awayAbbrev;

                          const statusStr = (g.status || '').toLowerCase().trim();
                          const isFinal = statusStr === 'final' || statusStr === 'status_final' || statusStr === 'post';
                          const isInProgress = !isFinal && (statusStr === 'in' || statusStr === 'in_progress' || statusStr === 'status_in_progress');
                          const isScheduled = !isFinal && !isInProgress;

                          const currentPoolWeek = gridData?.currentWeek || 1;
                          const isFutureRollingLocked = adminSettings.pick_opening_rule === 'weekly_rolling' && activeWeek > currentPoolWeek;

                          const kickoffMs = g.kickoffDate ? new Date(g.kickoffDate).getTime() : (g.date ? new Date(g.date).getTime() : 0);
                          const isKickoffPassed = kickoffMs > 0 && Date.now() >= kickoffMs;
                          const isLocked = isFinal || isInProgress || isKickoffPassed;
                          const isPoolSetupMode = settingsLoaded && adminSettings.pool_status !== 'active';

                          const homeScoreNum = parseInt(String(g.homeTeam?.score ?? g.homeScore ?? -1), 10);
                          const awayScoreNum = parseInt(String(g.awayTeam?.score ?? g.awayScore ?? -1), 10);

                          const homeIsWinner = isFinal && homeScoreNum > awayScoreNum;
                          const awayIsWinner = isFinal && awayScoreNum > homeScoreNum;

                          const oddsText = (g.oddsDetails || '').trim();
                          const hasOdds = isScheduled && oddsText && oddsText.toUpperCase() !== 'OFF' && oddsText.toUpperCase() !== 'N/A' && oddsText.toUpperCase() !== 'NONE';

                          const weatherEmoji = isScheduled ? getWeatherEmoji(g.weatherDisplay, g.venue) : '';

                          const cardBg = isInProgress ? '#eff6ff' : isFinal ? '#f8fafc' : '#ffffff';
                          const cardBorder = isInProgress ? '1px solid #bfdbfe' : isFinal ? '1px solid #e2e8f0' : '1px solid #cbd5e1';
                          const cardBorderLeft = isInProgress ? '4px solid #2563eb' : isFinal ? '3px solid #cbd5e1' : '1px solid #cbd5e1';
                          const cardShadow = isInProgress ? '0 2px 10px rgba(37, 99, 235, 0.08)' : isFinal ? 'none' : '0 2px 8px rgba(0,0,0,0.03)';

                          return (
                            <div
                              key={g.id}
                              style={{
                                backgroundColor: cardBg,
                                borderRadius: '12px',
                                border: cardBorder,
                                borderLeft: cardBorderLeft,
                                padding: '14px 16px',
                                boxShadow: cardShadow,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                transition: 'all 0.2s'
                              }}
                            >
                              {/* Game Header Meta Bar (1-Line Compact Layout) */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9', fontSize: '0.73rem', gap: '8px' }}>
                                <div style={{ color: '#475569', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>{g.date ? formatKickoffWithTimeZone(g.date, userTimeZone) : g.kickoffFormatted}</span>
                                </div>

                                <div style={{ fontSize: '0.71rem', color: '#64748b', fontWeight: 500, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>
                                  📍 {shortenVenueName(g.venue)}{getWeatherDisplayCompact(g.weatherDisplay, g.venue) ? ` (${getWeatherDisplayCompact(g.weatherDisplay, g.venue)})` : ''}
                                </div>
                              </div>

                              {/* Compact Team Selection Rows */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                
                                {/* Away Team Selection Row */}
                                <button
                                  onClick={() => !isPoolSetupMode && !isLocked && !isPickLocked && !awayUsedInOtherWeek && !isFutureRollingLocked && handleMakePick(awayAbbrev)}
                                  disabled={isPoolSetupMode || isLocked || isPickLocked || submittingPick || !!awayUsedInOtherWeek || isFutureRollingLocked}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    border: isAwayCurrentPick ? '2px solid #2563eb' : (isFinal && awayIsWinner ? '1.5px solid #bbf7d0' : '1px solid #cbd5e1'),
                                    backgroundColor: isAwayCurrentPick ? '#eff6ff' : (isFinal ? (awayIsWinner ? '#ffffff' : 'rgba(255, 255, 255, 0.65)') : '#ffffff'),
                                    color: isAwayCurrentPick ? '#1e40af' : (isFinal && !awayIsWinner ? '#64748b' : '#0f172a'),
                                    opacity: isFinal && !awayIsWinner ? 0.8 : 1,
                                    cursor: isPoolSetupMode || isLocked || isPickLocked || awayUsedInOtherWeek || isFutureRollingLocked ? 'default' : 'pointer',
                                    transition: 'all 0.15s'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <img src={g.awayTeam?.logo || `https://a.espncdn.com/i/teamlogos/nfl/500/scoreboard/${awayAbbrev.toLowerCase()}.png`} alt={awayName} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                    <span style={{ fontWeight: 800, fontSize: '0.88rem' }}>{awayAbbrev}</span>
                                    <span style={{ fontSize: '0.72rem', color: isAwayCurrentPick ? '#2563eb' : '#64748b', fontWeight: isAwayCurrentPick ? 600 : 400 }}>
                                      ({g.awayTeam?.record || '0-0'}) Away
                                    </span>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {isFinal ? (
                                      <span style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e293b' }}>
                                        <span>{awayScoreNum !== null ? awayScoreNum : ''}</span>
                                        {awayIsWinner ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />}
                                      </span>
                                    ) : isInProgress ? (
                                      <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>{awayScoreNum !== null ? awayScoreNum : ''}</span>
                                        <span style={{ fontSize: '0.7rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 4px', borderRadius: '3px' }}>LIVE</span>
                                      </span>
                                    ) : isAwayCurrentPick ? (
                                      <span style={{ fontSize: '0.75rem', backgroundColor: '#2563eb', color: '#fff', padding: '3px 9px', borderRadius: '5px', fontWeight: 700 }}>
                                        SELECTED ✅
                                      </span>
                                    ) : awayUsedInOtherWeek ? (
                                      <span style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Lock size={12} /> Used W{awayUsedInOtherWeek[0]}
                                      </span>
                                    ) : isFutureRollingLocked ? (
                                      <span style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Lock size={12} /> Window Closed
                                      </span>
                                    ) : isPoolSetupMode || isLocked || isPickLocked ? (
                                      <span style={{ fontSize: '0.85rem' }} title="Pick locked">🔒</span>
                                    ) : (
                                      <span style={{ fontSize: '0.76rem', color: '#2563eb', fontWeight: 700 }}>Pick Team</span>
                                    )}
                                  </div>
                                </button>

                                {/* Home Team Selection Row */}
                                <button
                                  onClick={() => !isPoolSetupMode && !isLocked && !isPickLocked && !homeUsedInOtherWeek && !isFutureRollingLocked && handleMakePick(homeAbbrev)}
                                  disabled={isPoolSetupMode || isLocked || isPickLocked || submittingPick || !!homeUsedInOtherWeek || isFutureRollingLocked}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    border: isHomeCurrentPick ? '2px solid #2563eb' : (isFinal && homeIsWinner ? '1.5px solid #bbf7d0' : '1px solid #cbd5e1'),
                                    backgroundColor: isHomeCurrentPick ? '#eff6ff' : (isFinal ? (homeIsWinner ? '#ffffff' : 'rgba(255, 255, 255, 0.65)') : '#ffffff'),
                                    color: isHomeCurrentPick ? '#1e40af' : (isFinal && !homeIsWinner ? '#64748b' : '#0f172a'),
                                    opacity: isFinal && !homeIsWinner ? 0.8 : 1,
                                    cursor: isPoolSetupMode || isLocked || isPickLocked || homeUsedInOtherWeek || isFutureRollingLocked ? 'default' : 'pointer',
                                    transition: 'all 0.15s'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <img src={g.homeTeam?.logo || `https://a.espncdn.com/i/teamlogos/nfl/500/scoreboard/${homeAbbrev.toLowerCase()}.png`} alt={homeName} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                    <span style={{ fontWeight: 800, fontSize: '0.88rem' }}>{homeAbbrev}</span>
                                    <span style={{ fontSize: '0.72rem', color: isHomeCurrentPick ? '#2563eb' : '#64748b', fontWeight: isHomeCurrentPick ? 600 : 400 }}>
                                      ({g.homeTeam?.record || '0-0'}) Home
                                    </span>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {isFinal ? (
                                      <span style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px', color: '#1e293b' }}>
                                        <span>{homeScoreNum !== null ? homeScoreNum : ''}</span>
                                        {homeIsWinner ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />}
                                      </span>
                                    ) : isInProgress ? (
                                      <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>{homeScoreNum !== null ? homeScoreNum : ''}</span>
                                        <span style={{ fontSize: '0.7rem', backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 4px', borderRadius: '3px' }}>LIVE</span>
                                      </span>
                                    ) : isHomeCurrentPick ? (
                                      <span style={{ fontSize: '0.75rem', backgroundColor: '#2563eb', color: '#fff', padding: '3px 9px', borderRadius: '5px', fontWeight: 700 }}>
                                        SELECTED ✅
                                      </span>
                                    ) : homeUsedInOtherWeek ? (
                                      <span style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Lock size={12} /> Used W{homeUsedInOtherWeek[0]}
                                      </span>
                                    ) : isFutureRollingLocked ? (
                                      <span style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Lock size={12} /> Window Closed
                                      </span>
                                    ) : isPoolSetupMode || isLocked || isPickLocked ? (
                                      <span style={{ fontSize: '0.85rem' }} title="Pick locked">🔒</span>
                                    ) : (
                                      <span style={{ fontSize: '0.76rem', color: '#2563eb', fontWeight: 700 }}>Pick Team</span>
                                    )}
                                  </div>
                                </button>
                              </div>

                              {/* Bottom Card Footer Bar (Scheduled Line, Live Quarter/Detail, or Final) */}
                              {(() => {
                                let footerText = '';
                                let footerColor = '#64748b';

                                if (isFinal) {
                                  footerText = (g.statusDetail || '').toLowerCase().includes('final') ? (g.statusDetail || 'Final') : 'Final';
                                  footerColor = '#64748b';
                                } else if (isInProgress) {
                                  const rawDetail = (g.statusDetail || '').trim();
                                  const isDateOrTime = rawDetail.toLowerCase().includes('at ') || rawDetail.includes(',') || rawDetail.toLowerCase().includes('scheduled') || rawDetail.toLowerCase().includes('pm') || rawDetail.toLowerCase().includes('am');
                                  footerText = (rawDetail && !isDateOrTime) ? rawDetail : 'Live';
                                  footerColor = '#1d4ed8';
                                } else if (isScheduled && hasOdds) {
                                  footerText = `Line: ${oddsText}`;
                                  footerColor = '#64748b';
                                }

                                if (!footerText) return null;

                                return (
                                  <div style={{
                                    marginTop: '2px',
                                    paddingTop: '6px',
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.72rem',
                                    color: footerColor,
                                    fontWeight: 600,
                                    letterSpacing: '0.02em'
                                  }}>
                                    <span>{footerText}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>

                      {/* COMPACT MOBILE-FRIENDLY WEEK STEPPER CONTROL */}
                      <div style={{
                        marginTop: '28px',
                        marginBottom: '16px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '12px',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        gap: '8px',
                        width: '100%'
                      }}>
                        <button
                          onClick={() => setActiveWeek((w) => Math.max(1, w - 1))}
                          disabled={activeWeek <= 1}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: activeWeek <= 1 ? '#f8fafc' : '#ffffff',
                            color: activeWeek <= 1 ? '#cbd5e1' : '#1e293b',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            cursor: activeWeek <= 1 ? 'default' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            transition: 'all 0.15s'
                          }}
                        >
                          <ChevronLeft size={16} />
                          <span>Prev</span>
                        </button>

                        <div style={{ textAlign: 'center', minWidth: 0, flex: '1 1 auto' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%', justifyContent: 'center' }}>
                            <Calendar size={16} color="#2563eb" style={{ flexShrink: 0 }} />
                            <select
                              value={activeWeek}
                              onChange={(e) => setActiveWeek(parseInt(e.target.value, 10))}
                              style={{
                                fontSize: '0.98rem',
                                fontWeight: 800,
                                color: '#0f172a',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: '2px 0',
                                textAlign: 'center',
                                appearance: 'auto'
                              }}
                            >
                              {Array.from({ length: 18 }, (_, i) => i + 1).map((wNum) => (
                                <option key={wNum} value={wNum}>
                                  Week {wNum} of 18
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '1px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {activeWeek === gridData?.currentWeek ? 'Current Active Week' : activeWeek < (gridData?.currentWeek || 1) ? 'Past Week Results' : 'Upcoming Schedule'}
                          </div>
                        </div>

                        <button
                          onClick={() => setActiveWeek((w) => Math.min(18, w + 1))}
                          disabled={activeWeek >= 18}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: activeWeek >= 18 ? '#f8fafc' : '#ffffff',
                            color: activeWeek >= 18 ? '#cbd5e1' : '#1e293b',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            cursor: activeWeek >= 18 ? 'default' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            transition: 'all 0.15s'
                          }}
                        >
                          <span>Next</span>
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* SUB-PAGE 2: GROUP PICKS & STANDINGS */}
            {userSubTab === 'group_picks' && (
              <div>
                {/* Standings & Scoreboard Visibility Lock Gate (Hidden before kickoff for regular users) */}
                {authUser.global_role !== 'site_admin' && !isPoolAdmin && activeWeek === 1 && games.length > 0 && !games.some((g) => g.status === 'in_progress' || g.status === 'final') ? (
                  <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #cbd5e1', padding: '48px 24px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', maxWidth: '640px', margin: '30px auto' }}>
                    <Lock size={52} color="#2563eb" style={{ marginBottom: '16px' }} />
                    <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a' }}>🔒 Standings & Group Picks Hidden</h3>
                    <p style={{ fontSize: '0.92rem', color: '#64748b', marginTop: '8px', lineHeight: 1.5 }}>
                      Group picks and pool standings are currently hidden and will be revealed to all pool participants automatically based on pool rules (<strong>{adminSettings.pick_reveal_rule === 'game_kickoff' ? 'At Game Kickoff' : adminSettings.pick_reveal_rule}</strong>).
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Admin Real-Time Pick Monitor Info Banner */}
                    {(isPoolAdmin || authUser.global_role === 'site_admin') && (
                      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '12px', padding: '12px 18px', marginBottom: '20px', fontSize: '0.85rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Eye size={18} color="#2563eb" />
                        <div>
                          <strong>ℹ️ Admin Real-Time Pick Monitor:</strong> You are viewing member picks in real time. Player visibility is configured to reveal picks at: <strong>{adminSettings.pick_reveal_rule === 'game_kickoff' ? 'Game Kickoff' : adminSettings.pick_reveal_rule}</strong>.
                        </div>
                      </div>
                    )}
                {/* Top KPI Header Cards (3 Combined Cards) */}
                {(() => {
                  const totalBuybacksCountAll = (gridData?.grid || []).reduce((acc, g) => acc + (g?.buybacksUsed || (g?.buybackUsedWeek ? 1 : 0)), 0) || 0;
                  const currentWkNum = gridData?.currentWeek || activeWeek || 1;
                  const currentWkElimCount = (gridData?.grid || []).filter((g) => g?.eliminatedWeek === currentWkNum).length || 0;

                  // Hybrid Smart Switch (Option 3): Show Week N-1 until Week N records its first elimination
                  const targetWkNum = currentWkElimCount > 0 ? currentWkNum : Math.max(1, currentWkNum - 1);
                  const targetWkElimCount = (gridData?.grid || []).filter((g) => g?.eliminatedWeek === targetWkNum).length || 0;
                  const targetWkElimRate = gridData?.usersCount ? Math.round((targetWkElimCount / gridData.usersCount) * 100) : 0;
                  const overallElimRate = gridData?.usersCount ? Math.round(((gridData?.eliminatedCount || 0) / gridData.usersCount) * 100) : 0;

                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                      {/* Card 1: Total Pool Value */}
                      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <DollarSign size={15} color="#2563eb" />
                          <span>Total Pool Value</span>
                        </div>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>
                            ${totalPotValue.toLocaleString()}
                          </div>
                          <span style={{ fontSize: '0.8rem', color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: '6px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Users size={12} /> {gridData?.usersCount || 0} Players
                          </span>
                        </div>
                      </div>

                      {/* Card 2: Player Status */}
                      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckCircle2 size={15} color="#16a34a" />
                          <span>Player Status</span>
                        </div>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a' }}>
                            <span style={{ color: '#16a34a' }}>{gridData?.aliveCount || 0} Alive</span> <span style={{ color: '#94a3b8' }}>/</span> <span style={{ color: '#dc2626' }}>{gridData?.eliminatedCount || 0} Out</span>
                          </div>
                          <span style={{ fontSize: '0.8rem', color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: '6px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            🔁 {totalBuybacksCountAll} Buy-back{totalBuybacksCountAll === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>

                      {/* Card 3: Elimination Rate */}
                      <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '0.78rem', color: '#dc2626', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <AlertCircle size={15} color="#dc2626" />
                          <span>Elimination Rate</span>
                        </div>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#dc2626' }}>
                            {overallElimRate}% <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>Overall</span>
                          </div>
                          {targetWkNum > 0 && (
                            <span style={{ fontSize: '0.8rem', color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '3px 8px', borderRadius: '6px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              W{targetWkNum}: {targetWkElimRate}% ({targetWkElimCount} Out)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* View Switcher & Filter Bar */}
                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  
                  {/* View Selector Buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#ffffff', padding: '4px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    <button
                      onClick={() => setStandingsViewMode('summary')}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        border: 'none',
                        backgroundColor: standingsViewMode === 'summary' ? '#2563eb' : 'transparent',
                        color: standingsViewMode === 'summary' ? '#ffffff' : '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.15s'
                      }}
                    >
                      <ListFilter size={14} />
                      <span>Summary View</span>
                    </button>

                    <button
                      onClick={() => setStandingsViewMode('detailed')}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        border: 'none',
                        backgroundColor: standingsViewMode === 'detailed' ? '#2563eb' : 'transparent',
                        color: standingsViewMode === 'detailed' ? '#ffffff' : '#64748b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.15s'
                      }}
                    >
                      <Table size={14} />
                      <span>Detailed Season Matrix</span>
                    </button>
                  </div>

                  {/* Status Filter Buttons & Search */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Filter size={14} color="#2563eb" />
                      <button
                        onClick={() => setGridFilterStatus('all')}
                        style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, border: 'none', backgroundColor: gridFilterStatus === 'all' ? '#2563eb' : '#ffffff', color: gridFilterStatus === 'all' ? '#fff' : '#475569', cursor: 'pointer' }}
                      >
                        All ({gridData?.usersCount || 0})
                      </button>
                      <button
                        onClick={() => setGridFilterStatus('alive')}
                        style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, border: 'none', backgroundColor: gridFilterStatus === 'alive' ? '#16a34a' : '#ffffff', color: gridFilterStatus === 'alive' ? '#fff' : '#475569', cursor: 'pointer' }}
                      >
                        🟢 Alive ({gridData?.aliveCount || 0})
                      </button>
                      <button
                        onClick={() => setGridFilterStatus('eliminated')}
                        style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, border: 'none', backgroundColor: gridFilterStatus === 'eliminated' ? '#dc2626' : '#ffffff', color: gridFilterStatus === 'eliminated' ? '#fff' : '#475569', cursor: 'pointer' }}
                      >
                        🔴 Eliminated ({gridData?.eliminatedCount || 0})
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#ffffff', padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      <Search size={14} color="#64748b" />
                      <input
                        type="text"
                        placeholder="Search player..."
                        value={gridSearch}
                        onChange={(e) => setGridSearch(e.target.value)}
                        style={{ backgroundColor: 'transparent', border: 'none', color: '#0f172a', fontSize: '0.8rem', outline: 'none' }}
                      />
                    </div>
                  </div>

                </div>

                {/* SUMMARY VIEW */}
                {standingsViewMode === 'summary' && (() => {
                  const curPoolWk = gridData?.currentWeek || activeWeek || 1;
                  const lockRule = adminSettings?.pick_lock_rule || 'thursday_kickoff';
                  const wkGames = (allSchedulesMap[curPoolWk] || games || []).filter((g: any) => (g.week || curPoolWk) === curPoolWk);
                  const sortedGames = wkGames
                    .map((g: any) => ({
                      kickoffMs: g.kickoffDate ? new Date(g.kickoffDate).getTime() : (g.date ? new Date(g.date).getTime() : 0)
                    }))
                    .filter((g: any) => g.kickoffMs > 0)
                    .sort((a: any, b: any) => a.kickoffMs - b.kickoffMs);

                  let isCurWkLocked = false;
                  if (sortedGames.length > 0) {
                    const firstKickoff = sortedGames[0].kickoffMs;
                    const now = Date.now();
                    if (lockRule === 'individual_kickoff' || lockRule === 'thursday_kickoff') {
                      isCurWkLocked = now >= firstKickoff;
                    } else if (lockRule === 'saturday_kickoff') {
                      const satGame = sortedGames.find((g: any) => new Date(g.kickoffMs).getDay() === 6);
                      isCurWkLocked = now >= (satGame?.kickoffMs || firstKickoff);
                    } else if (lockRule === 'sunday_early') {
                      const sunGame = sortedGames.find((g: any) => new Date(g.kickoffMs).getDay() === 0);
                      isCurWkLocked = now >= (sunGame?.kickoffMs || firstKickoff);
                    }
                  }

                  const summaryPickWeek = curPoolWk <= 1 ? 1 : (isCurWkLocked ? curPoolWk : curPoolWk - 1);

                  return (
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.86rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569', fontSize: '0.78rem' }}>
                            <th style={{ padding: '12px 14px', width: '32%' }}>Participant</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center', width: '22%', whiteSpace: 'nowrap' }}>W{summaryPickWeek} Pick</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center', width: '12%', whiteSpace: 'nowrap' }}>Status</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center', width: '14%', whiteSpace: 'nowrap' }}>Wk Eliminated</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center', width: '10%', whiteSpace: 'nowrap' }}>Buy-Backs</th>
                            <th style={{ padding: '12px 14px', textAlign: 'right', width: '10%', whiteSpace: 'nowrap' }}>Record</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredGrid.map((row) => {
                            const userPay = userPayments.find((p) => p.user_id === row.user.id || p.email === row.user.email);
                            const curWk = gridData?.currentWeek || activeWeek || 1;
                            const isGraceWkBuyback = row.isAlive && row.buybackUsedWeek && curWk === row.buybackUsedWeek + 1;
                            const isGhostElim = !row.isAlive && row.eliminatedWeek && !row.weeklyPicks[row.eliminatedWeek];

                            const curWkPick = row.weeklyPicks?.[summaryPickWeek];
                            const isUnmadeGhostCurWk = !curWkPick && row.eliminatedWeek === summaryPickWeek;
                            const teamAbbrev = curWkPick ? getTeamAbbrev(curWkPick.team) : isUnmadeGhostCurWk ? '👻 NO PICK' : '—';

                            let statusEmoji = '🟢';
                            let statusBg = '#dcfce7';
                            let statusBorder = '#86efac';
                            let statusTooltip = 'Alive';

                            if (row.isAlive) {
                              if (isGraceWkBuyback) {
                                statusEmoji = '🔁';
                                statusBg = '#fef3c7';
                                statusBorder = '#fde047';
                                statusTooltip = `Buyback Re-Entry (Grace W${curWk})`;
                              } else {
                                statusEmoji = '🟢';
                                statusBg = '#dcfce7';
                                statusBorder = '#86efac';
                                statusTooltip = 'Alive';
                              }
                            } else {
                              if (isGhostElim) {
                                statusEmoji = '👻';
                                statusBg = '#fee2e2';
                                statusBorder = '#fca5a5';
                                statusTooltip = `Eliminated via Ghost Pick (W${row.eliminatedWeek})`;
                              } else {
                                statusEmoji = '🔴';
                                statusBg = '#fee2e2';
                                statusBorder = '#fca5a5';
                                statusTooltip = `Eliminated (W${row.eliminatedWeek})`;
                              }
                            }

                            const buybacksCount = row.buybacksUsed ?? (userPay?.buy_backs_used || (row.buybackUsedWeek ? 1 : 0));

                            return (
                              <tr key={row.user.email || row.user.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '12px 14px', fontWeight: 600, color: row.isAlive ? '#0f172a' : '#64748b' }}>
                                  <div style={{ textDecoration: row.isAlive ? 'none' : 'line-through' }}>{formatDisplayName(row.user.name, row.user.email)}</div>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{row.user.email}</div>
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                  {curWkPick ? (
                                    <div
                                      title={`${curWkPick.team} (${curWkPick.status})${curWkPick.buybackUsed ? ' • 🔄 Buy-Back Used' : ''}`}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '0.76rem',
                                        fontWeight: 800,
                                        backgroundColor: curWkPick.status === 'win' ? '#dcfce7' : curWkPick.status === 'loss' ? (curWkPick.buybackUsed ? '#fef3c7' : '#fee2e2') : '#eff6ff',
                                        color: curWkPick.status === 'win' ? '#15803d' : curWkPick.status === 'loss' ? (curWkPick.buybackUsed ? '#b45309' : '#b91c1c') : '#1d4ed8',
                                        border: curWkPick.status === 'win' ? '1px solid #86efac' : curWkPick.status === 'loss' ? (curWkPick.buybackUsed ? '1px solid #fde68a' : '1px solid #fca5a5') : '1px solid #93c5fd',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <span>{teamAbbrev}</span>
                                      {curWkPick.status === 'win' && <Check size={11} />}
                                      {curWkPick.status === 'loss' && <XCircle size={11} />}
                                      {curWkPick.buybackUsed && <span title="Buy-Back Used">🔄</span>}
                                    </div>
                                  ) : isUnmadeGhostCurWk ? (
                                    <span style={{ fontSize: '0.76rem', fontWeight: 800, color: '#dc2626', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', padding: '3px 8px', borderRadius: '6px' }}>
                                      👻 NO PICK
                                    </span>
                                  ) : (
                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'center', whiteSpace: 'nowrap', fontSize: '1.15rem' }}>
                                  <span title={statusTooltip} style={{ cursor: 'help' }}>
                                    {statusEmoji}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, color: row.eliminatedWeek ? '#dc2626' : '#64748b', whiteSpace: 'nowrap' }}>
                                  {row.eliminatedWeek ? `W${row.eliminatedWeek}` : <span style={{ color: '#16a34a' }}>Active</span>}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>
                                  {buybacksCount}
                                </td>
                                <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                  {row.record}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* DETAILED SEASON MATRIX VIEW */}
                {standingsViewMode === 'detailed' && (
                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                          <th style={{ padding: '12px 16px', minWidth: '170px', position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 10 }}>
                            Participant
                          </th>
                          <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '65px' }}>Status</th>
                          <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '55px' }}>Record</th>
                          <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '60px' }}>Buy-Back</th>
                          {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                            <th key={w} style={{ padding: '12px 8px', textAlign: 'center', minWidth: '60px' }}>
                              W{w}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredGrid.map((row) => {
                          const userPay = userPayments.find((p) => p.user_id === row.user.id || p.email === row.user.email);
                          return (
                            <tr key={row.user.email || row.user.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              
                              {/* Name Column */}
                              <td style={{ padding: '12px 16px', fontWeight: 600, color: row.isAlive ? '#0f172a' : '#64748b', position: 'sticky', left: 0, backgroundColor: '#ffffff', zIndex: 5, borderRight: '1px solid #e2e8f0' }}>
                                <div style={{ textDecoration: row.isAlive ? 'none' : 'line-through' }}>{formatDisplayName(row.user.name, row.user.email)}</div>
                                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{row.user.email}</div>
                              </td>

                              {/* Status Column (Emoji Only) */}
                              <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: '1.1rem' }}>
                                {(() => {
                                  const curWk = gridData?.currentWeek || activeWeek || 1;
                                  const isGraceWkBuyback = row.isAlive && row.buybackUsedWeek && curWk === row.buybackUsedWeek + 1;
                                  const isGhostElim = !row.isAlive && row.eliminatedWeek && !row.weeklyPicks[row.eliminatedWeek];
                                  let emoji = '🟢';
                                  let tip = 'Alive';
                                  if (row.isAlive) {
                                    if (isGraceWkBuyback) { emoji = '🔁'; tip = `Buyback Re-Entry (Grace W${curWk})`; }
                                  } else {
                                    if (isGhostElim) { emoji = '👻'; tip = `Eliminated via Ghost Pick (W${row.eliminatedWeek})`; }
                                    else { emoji = '🔴'; tip = `Eliminated (W${row.eliminatedWeek})`; }
                                  }
                                  return <span title={tip} style={{ cursor: 'help' }}>{emoji}</span>;
                                })()}
                              </td>

                              {/* Record Column */}
                              <td style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#334155', fontSize: '0.78rem' }}>
                                {row.record}
                              </td>

                              {/* Buy-Backs Used Column */}
                              <td style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700, color: '#334155', fontSize: '0.78rem' }}>
                                {row.buybacksUsed ?? (userPay?.buy_backs_used || (row.buybackUsedWeek ? 1 : 0))}
                              </td>

                              {/* Weekly Picks Columns (1 to 18) with 3-Letter Team Abbrev */}
                              {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => {
                                const pick = row.weeklyPicks[w];
                                const isUnmadeGhost = !pick && row.eliminatedWeek === w;
                                const teamAbbrev = pick ? getTeamAbbrev(pick.team) : isUnmadeGhost ? '👻 NO PICK' : '—';
                                return (
                                  <td key={w} style={{ padding: '8px 6px', textAlign: 'center' }}>
                                    {pick ? (
                                      <div
                                        title={`${pick.team} (${pick.status})${pick.buybackUsed ? ' • 🔄 Buy-Back Used' : ''}`}
                                        style={{
                                          padding: '4px 6px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 800,
                                          backgroundColor: pick.status === 'win' ? '#dcfce7' : pick.status === 'loss' ? (pick.buybackUsed ? '#fef3c7' : '#fee2e2') : '#eff6ff',
                                          color: pick.status === 'win' ? '#15803d' : pick.status === 'loss' ? (pick.buybackUsed ? '#b45309' : '#b91c1c') : '#1d4ed8',
                                          border: pick.status === 'win' ? '1px solid #86efac' : pick.status === 'loss' ? (pick.buybackUsed ? '1px solid #fde68a' : '1px solid #fca5a5') : '1px solid #93c5fd',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '3px'
                                        }}
                                      >
                                        <span>{teamAbbrev}</span>
                                        {pick.status === 'win' && <Check size={10} />}
                                        {pick.status === 'loss' && <XCircle size={10} />}
                                        {pick.buybackUsed && <span title="Buy-Back Used">🔄</span>}
                                      </div>
                                    ) : isUnmadeGhost ? (
                                      <div
                                        title="Ghost Elimination: No Pick Submitted"
                                        style={{
                                          fontWeight: 800,
                                          backgroundColor: '#fef2f2',
                                          color: '#dc2626',
                                          border: '1px solid #fca5a5',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '3px'
                                        }}
                                      >
                                        <span>👻 NO PICK</span>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#cbd5e1', fontSize: '0.78rem' }}>—</span>
                                    )}
                                  </td>
                                );
                              })}

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                  </>
                )}
              </div>
            )}

            {/* SUB-PAGE 3: USER SETTINGS & PROFILE */}
            {userSubTab === 'user_settings' && (
              <div style={{ maxWidth: '680px', margin: '0 auto', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
                  <Sliders size={24} color="#2563eb" />
                  <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>User Profile & Preferences</h2>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>Manage personal settings, time zone, and notification preferences</p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                  {/* Card 1: Account & Locked Standings Display Name */}
                  <div style={{ backgroundColor: '#f8fafc', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Users size={15} color="#2563eb" />
                      <span>Account Identity</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', backgroundColor: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Signed-In User</div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginTop: '1px' }}>{authUser.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{authUser.email}</div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>Standings Display Name</div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e40af', marginTop: '2px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span>{formatDisplayName(authUser.name, authUser.email)}</span>
                          <span
                            title="Player names are locked during the active season to preserve competitor identification across group standings. Contact your Pool Administrator to request a display name correction."
                            style={{ fontSize: '0.72rem', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, cursor: 'help' }}
                          >
                            🔒 Locked
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: My Financial Ledger (Personal Payment Status) */}
                  {(() => {
                    const myPayRecord = (userPayments || []).find((p) => (p?.email && authUser?.email && p.email.toLowerCase() === authUser.email.toLowerCase()) || (p?.user_id && authUser?.id && p.user_id === authUser.id));
                    const myGridRow = (gridData?.grid || []).find((g) => (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) || (g?.user?.id && authUser?.id && g.user.id === authUser.id));
                    const buyInCost = gridData?.poolConfig?.buyInCost ?? adminSettings?.buy_in_cost ?? 10;
                    const buyBackCost = gridData?.poolConfig?.buyBackCost ?? adminSettings?.buy_back_cost ?? 10;

                    const myBuybacksCount = myGridRow?.buybacksUsed ?? (myPayRecord?.buy_backs_used || 0);
                    const totalOwed = buyInCost + (myBuybacksCount * buyBackCost);
                    const totalPaid = myPayRecord ? (myPayRecord.total_paid || ((myPayRecord.buy_in_paid ? buyInCost : 0) + (myPayRecord.buy_backs_paid ? myBuybacksCount * buyBackCost : 0))) : 0;
                    const isFullyPaid = totalPaid >= totalOwed;

                    return (
                      <div style={{ backgroundColor: '#f8fafc', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <DollarSign size={15} color="#2563eb" />
                            <span>My Financial Ledger</span>
                          </div>
                          <div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '3px 10px', borderRadius: '6px', backgroundColor: isFullyPaid ? '#dcfce7' : '#fef3c7', color: isFullyPaid ? '#15803d' : '#b45309', border: `1px solid ${isFullyPaid ? '#86efac' : '#fde047'}`, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                              {isFullyPaid ? '🟢 Account Current' : '⚠️ Payment Pending'}
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ flex: '1 1 0', minWidth: 0, backgroundColor: '#ffffff', padding: '10px 8px', borderRadius: '8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Buy-In</div>
                            <div style={{ fontSize: '0.98rem', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>${buyInCost}</div>
                            <div style={{ fontSize: '0.68rem', color: myPayRecord?.buy_in_paid ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                              {myPayRecord?.buy_in_paid ? '✓ Paid' : 'Unpaid'}
                            </div>
                          </div>

                          <div style={{ flex: '1 1 0', minWidth: 0, backgroundColor: '#ffffff', padding: '10px 8px', borderRadius: '8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Buy-Backs ({myBuybacksCount})</div>
                            <div style={{ fontSize: '0.98rem', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>${myBuybacksCount * buyBackCost}</div>
                            <div style={{ fontSize: '0.68rem', color: myBuybacksCount === 0 || myPayRecord?.buy_backs_paid ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                              {myBuybacksCount === 0 ? 'N/A' : myPayRecord?.buy_backs_paid ? '✓ Paid' : 'Pending'}
                            </div>
                          </div>

                          <div style={{ flex: '1 1 0', minWidth: 0, backgroundColor: '#ffffff', padding: '10px 8px', borderRadius: '8px', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Total Balance</div>
                            <div style={{ fontSize: '0.98rem', fontWeight: 800, color: isFullyPaid ? '#16a34a' : '#dc2626', marginTop: '2px' }}>
                              ${totalPaid}/${totalOwed}
                            </div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>
                              {isFullyPaid ? 'Settled' : `$${totalOwed - totalPaid} Due`}
                            </div>
                          </div>
                        </div>

                        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                          <div style={{ fontSize: '0.78rem', color: '#475569' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>Payment Note / Handle: </span>
                            {myPayRecord?.notes && myPayRecord.notes !== 'Registered Participant' ? (
                              <span style={{ fontWeight: 600, color: '#16a34a' }}>"{myPayRecord.notes}"</span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>None provided yet</span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setPaymentNoteInput(myPayRecord?.notes === 'Registered Participant' ? '' : (myPayRecord?.notes || ''));
                              setShowPaymentNoteModal(true);
                            }}
                            style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {myPayRecord?.notes && myPayRecord.notes !== 'Registered Participant' ? '✏️ Edit Memo' : '➕ Add Payment Memo'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Card 3: Buy-Back Allowance & Rules Summary */}
                  {(() => {
                    const myGridRow = (gridData?.grid || []).find((g) => (g?.user?.email && authUser?.email && g.user.email.toLowerCase() === authUser.email.toLowerCase()) || (g?.user?.id && authUser?.id && g.user.id === authUser.id));
                    const maxBuybacks = gridData?.poolConfig?.maxBuybacks ?? adminSettings?.max_buy_backs ?? 1;
                    const maxWeek = gridData?.poolConfig?.buybackMaxWeek ?? adminSettings?.buy_back_max_week ?? 3;
                    const usedCount = myGridRow?.buybacksUsed ?? 0;
                    const curWk = gridData?.currentWeek || activeWeek || 1;

                    const isPending = (adminBuybacks || []).some((b) => (b?.email && authUser?.email && b.email.toLowerCase() === authUser.email.toLowerCase()) || (b?.user_id && authUser?.email && b.user_id === authUser.email) || (b?.user_id && authUser?.id && b.user_id === authUser.id) && b.status === 'pending');
                    const isEliminated = myGridRow ? !myGridRow.isAlive : false;
                    const elimWk = myGridRow?.eliminatedWeek;
                    const isWithinWkLimit = elimWk ? elimWk <= maxWeek : curWk <= maxWeek;
                    const isWithinCountLimit = usedCount < maxBuybacks;
                    const canBuyback = isEliminated && isWithinCountLimit && isWithinWkLimit && !isPending;

                    let badgeText = 'Active in Pool';
                    let badgeEmoji = '🟢';
                    let badgeBg = '#dcfce7';
                    let badgeColor = '#15803d';
                    let badgeBorder = '#86efac';

                    if (isEliminated) {
                      if (isPending) {
                        badgeText = 'Buy back approval pending';
                        badgeEmoji = '⏳';
                        badgeBg = '#eff6ff';
                        badgeColor = '#1d4ed8';
                        badgeBorder = '#93c5fd';
                      } else if (canBuyback) {
                        badgeText = 'Eligible for Buy Back';
                        badgeEmoji = '🔁';
                        badgeBg = '#fef3c7';
                        badgeColor = '#b45309';
                        badgeBorder = '#fde047';
                      } else if (usedCount >= maxBuybacks) {
                        badgeText = 'Limit reached';
                        badgeEmoji = '🛑';
                        badgeBg = '#f1f5f9';
                        badgeColor = '#475569';
                        badgeBorder = '#cbd5e1';
                      } else {
                        badgeText = 'Window closed';
                        badgeEmoji = '🔒';
                        badgeBg = '#f1f5f9';
                        badgeColor = '#64748b';
                        badgeBorder = '#cbd5e1';
                      }
                    }

                    return (
                      <div style={{ backgroundColor: '#f8fafc', padding: '18px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>🔁</span>
                          <span>Buy-Back Allowance & Status</span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', backgroundColor: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                          <div>
                            <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>
                              {usedCount} of {maxBuybacks} Buy-Back{maxBuybacks === 1 ? '' : 's'} Used
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                              Buy-backs permitted through Week {maxWeek}
                            </div>
                          </div>

                          <span style={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '6px',
                            backgroundColor: badgeBg,
                            color: badgeColor,
                            border: `1px solid ${badgeBorder}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span>{badgeEmoji}</span>
                            <span>{badgeText}</span>
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Card 4: Preferred Time Zone & Notification Preferences */}
                  <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Globe size={15} color="#2563eb" />
                      <span>Time Zone & Email Notifications</span>
                    </div>

                    <div style={{ marginBottom: '18px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>
                        Preferred Time Zone
                      </label>
                      <select
                        value={userTimeZone}
                        onChange={(e) => setUserTimeZone(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: '#ffffff',
                          color: '#0f172a',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="America/New_York">Eastern Time (ET) — America/New_York</option>
                        <option value="America/Chicago">Central Time (CT) — America/Chicago</option>
                        <option value="America/Denver">Mountain Time (MT) — America/Denver</option>
                        <option value="America/Phoenix">Arizona Time (MST - No DST) — America/Phoenix</option>
                        <option value="America/Los_Angeles">Pacific Time (PT) — America/Los_Angeles</option>
                        <option value="America/Anchorage">Alaska Time (AKST) — America/Anchorage</option>
                        <option value="Pacific/Honolulu">Hawaii Time (HST) — Pacific/Honolulu</option>
                        <option value="UTC">Coordinated Universal Time — UTC</option>
                      </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Bell size={16} color="#2563eb" />
                        <span>Email Alert Preferences</span>
                      </div>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                        <input
                          type="checkbox"
                          checked={userReminderOptIn}
                          onChange={(e) => setUserReminderOptIn(e.target.checked)}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                        />
                        <span>Weekly pick reminders (sent 24h & 4h before lock deadline if pick is unmade)</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                        <input
                          type="checkbox"
                          checked={userBuybackNotifyOptIn}
                          onChange={(e) => setUserBuybackNotifyOptIn(e.target.checked)}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                        />
                        <span>Buy-back status updates (notifications when buy-back requests are approved or rejected)</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                        <input
                          type="checkbox"
                          checked={userRevealNotifyOptIn}
                          onChange={(e) => setUserRevealNotifyOptIn(e.target.checked)}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb' }}
                        />
                        <span>Weekly picks reveal alerts (notifications when group picks unlock at kickoff)</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveUserSettings}
                    disabled={savingUserSettings}
                    style={{
                      width: '100%',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      padding: '12px',
                      borderRadius: '10px',
                      fontWeight: 700,
                      fontSize: '0.92rem',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Save size={18} />
                    {savingUserSettings ? 'Saving Preferences...' : 'Save Preferences'}
                  </button>

                </div>
              </div>
            )}

          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════ */}
        {/* PORTAL 2: POOL ADMIN PORTAL                                          */}
        {/* ══════════════════════════════════════════════════════════════════════ */}
        {primaryPortal === 'pool_admin' && isPoolAdmin && effectiveActivePool && (
          <div>
            
            {/* SUB-PAGE 1: SEASON RULES & SETUP */}
            {poolAdminSubTab === 'rules_setup' && (
              <div>
                {/* 🎮 GAME DAY TIMELINE SIMULATION CONTROL CENTER */}
                {(activePool?.pool_type === 'simulation' || activePool?.id === 'pool_2026_test') && (
                  /* Sequential Time-Travel Simulation Engine Panel */
                  <div style={{
                    marginTop: '24px',
                    padding: '24px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid #0284c7',
                    color: '#f8fafc',
                    boxShadow: '0 12px 32px rgba(2, 132, 199, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                  }}>
                    {/* Header & Badges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span>🎮 Sequential Time-Travel Simulation Engine</span>
                          <span style={{ fontSize: '0.72rem', backgroundColor: '#0284c7', color: '#ffffff', padding: '3px 10px', borderRadius: '12px', fontWeight: 800 }}>
                            WEEKS 1 – 18 ACTIVE
                          </span>
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.84rem', color: '#94a3b8', lineHeight: 1.4 }}>
                          Scrub through time sequentially across all 18 weeks. Step through the 4 milestone states of each week to test pick locks, live scores, ghost picks, and eliminations.
                        </p>
                      </div>

                      {/* Mode Switcher Pills */}
                      <div style={{ display: 'flex', backgroundColor: '#0f172a', padding: '4px', borderRadius: '10px', border: '1px solid #334155' }}>
                        <button
                          onClick={() => { setSimEngineMode('scripted'); handleSimulateState(undefined, simTimelineIndex, 'scripted'); }}
                          style={{
                            padding: '6px 12px', borderRadius: '8px', border: 'none', fontSize: '0.78rem', fontWeight: 800, cursor: 'pointer',
                            backgroundColor: simEngineMode === 'scripted' ? '#2563eb' : 'transparent',
                            color: simEngineMode === 'scripted' ? '#ffffff' : '#94a3b8'
                          }}
                        >
                          📜 Deterministic Deck
                        </button>
                        <button
                          onClick={() => { setSimEngineMode('random'); handleSimulateState(undefined, simTimelineIndex, 'random'); }}
                          style={{
                            padding: '6px 12px', borderRadius: '8px', border: 'none', fontSize: '0.78rem', fontWeight: 800, cursor: 'pointer',
                            backgroundColor: simEngineMode === 'random' ? '#d97706' : 'transparent',
                            color: simEngineMode === 'random' ? '#ffffff' : '#94a3b8'
                          }}
                        >
                          🎲 Random Upsets
                        </button>
                      </div>
                    </div>

                    {/* Current Timeline State Display */}
                    {(() => {
                      const curWeek = Math.floor(simTimelineIndex / 4) + 1;
                      const subStep = simTimelineIndex % 4;
                      const subTitles = [
                        { label: '📅 Wed Pre-Pick', color: '#38bdf8', desc: 'Pre-Pick Window Open (All Games Scheduled)' },
                        { label: '🏉 Fri Post-TNF', color: '#f59e0b', desc: 'TNF Final Recorded • Thursday Picks Hard Locked' },
                        { label: '⚡ Sun Live In-Progress', color: '#ef4444', desc: 'Sunday Games Live • Sunday Pick Lockouts Enforced' },
                        { label: '🏆 Tue Week Concluded', color: '#22c55e', desc: 'All Games Final • Standings Settled & Ghosts Assigned' }
                      ];
                      const curSub = subTitles[subStep];

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div style={{
                            backgroundColor: '#0284c715', border: '1px solid #0284c740', borderRadius: '12px', padding: '16px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
                          }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                CURRENT SIMULATED TIMELINE POSITION
                              </div>
                              <div style={{ fontSize: '1.25rem', fontWeight: 900, color: curSub.color, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>Week {curWeek}:</span>
                                <span>{curSub.label}</span>
                              </div>
                              <div style={{ fontSize: '0.82rem', color: '#cbd5e1', marginTop: '2px' }}>
                                {curSub.desc}
                              </div>
                            </div>

                            {/* Stepper Navigation Buttons */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  const prevIdx = Math.max(0, simTimelineIndex - 1);
                                  setSimTimelineIndex(prevIdx);
                                  handleSimulateState(undefined, prevIdx);
                                }}
                                disabled={isSimulating || simTimelineIndex <= 0}
                                style={{
                                  backgroundColor: '#1e293b', color: '#ffffff', border: '1px solid #334155',
                                  padding: '10px 16px', borderRadius: '10px', fontWeight: 800, fontSize: '0.88rem',
                                  cursor: simTimelineIndex <= 0 ? 'not-allowed' : 'pointer', opacity: simTimelineIndex <= 0 ? 0.5 : 1
                                }}
                              >
                                ◀ Step Back
                              </button>
                              <button
                                onClick={() => {
                                  const nextIdx = Math.min(71, simTimelineIndex + 1);
                                  setSimTimelineIndex(nextIdx);
                                  handleSimulateState(undefined, nextIdx);
                                }}
                                disabled={isSimulating || simTimelineIndex >= 71}
                                style={{
                                  backgroundColor: '#2563eb', color: '#ffffff', border: 'none',
                                  padding: '10px 18px', borderRadius: '10px', fontWeight: 800, fontSize: '0.88rem',
                                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)', cursor: simTimelineIndex >= 71 ? 'not-allowed' : 'pointer', opacity: simTimelineIndex >= 71 ? 0.5 : 1
                                }}
                              >
                                Step Forward ▶
                              </button>
                              <button
                                onClick={() => {
                                  setSimTimelineIndex(0);
                                  handleSimulateState('wed_prepick', 0);
                                }}
                                disabled={isSimulating}
                                style={{ backgroundColor: '#334155', color: '#94a3b8', border: 'none', padding: '10px 14px', borderRadius: '10px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
                              >
                                🔄 Reset W1
                              </button>
                            </div>
                          </div>

                          {/* 4-Step Milestone Node Indicator for Current Week */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                            {subTitles.map((st, sIdx) => {
                              const nodeIdx = (curWeek - 1) * 4 + sIdx;
                              const isActive = subStep === sIdx;
                              const isPast = subStep > sIdx;

                              return (
                                <div
                                  key={sIdx}
                                  onClick={() => {
                                    setSimTimelineIndex(nodeIdx);
                                    handleSimulateState(undefined, nodeIdx);
                                  }}
                                  style={{
                                    backgroundColor: isActive ? '#1e293b' : '#0f172a',
                                    border: isActive ? `2px solid ${st.color}` : isPast ? '1px solid #334155' : '1px solid #1e293b',
                                    borderRadius: '10px', padding: '10px', cursor: 'pointer', textAlign: 'center',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <div style={{ fontSize: '0.75rem', fontWeight: 800, color: isActive ? st.color : (isPast ? '#94a3b8' : '#64748b') }}>
                                    {isActive ? '● ACTIVE' : isPast ? '✓ DONE' : `STEP ${sIdx + 1}`}
                                  </div>
                                  <div style={{ fontSize: '0.82rem', fontWeight: 800, color: isActive ? '#ffffff' : '#cbd5e1', marginTop: '2px' }}>
                                    {st.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* 18-Week Master Scrubbing Timeline Slider */}
                          <div style={{ backgroundColor: '#0f172a', borderRadius: '12px', padding: '16px', border: '1px solid #334155' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#94a3b8', fontWeight: 700, marginBottom: '8px' }}>
                              <span>🗓️ Full 18-Week Season Scrubbing Slider</span>
                              <span style={{ color: '#38bdf8' }}>Step {simTimelineIndex + 1} of 72</span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={71}
                              value={simTimelineIndex}
                              onChange={(e) => {
                                const newIdx = Number(e.target.value);
                                setSimTimelineIndex(newIdx);
                                handleSimulateState(undefined, newIdx);
                              }}
                              style={{ width: '100%', accentColor: '#38bdf8', cursor: 'pointer', height: '8px' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b', marginTop: '6px', fontWeight: 700 }}>
                              <span>W1 Wed</span>
                              <span>W5</span>
                              <span>W10</span>
                              <span>W15</span>
                              <span>W18 Tue</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {simMessage && (
                      <div style={{ padding: '10px 14px', backgroundColor: '#0f172a', border: '1px solid #0284c7', borderRadius: '8px', fontSize: '0.84rem', fontWeight: 700, color: '#38bdf8' }}>
                        {simMessage}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                
                {(() => {
                  const isSettingsLockedEffective = isSettingsLocked && adminSettings.pool_status === 'active';
                  return (
                    <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: 0 }}>
                      <span>{activePool?.name || 'Pool Settings'}</span>
                      {!settingsLoaded ? null : adminSettings.pool_status !== 'active' ? (
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', padding: '3px 10px', borderRadius: '12px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          🚀 Status: DRAFT (Unlaunched)
                        </span>
                      ) : isSettingsLockedEffective ? (
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac', padding: '3px 10px', borderRadius: '12px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          🔒 Status: ACTIVE (Settings Locked)
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', padding: '3px 10px', borderRadius: '12px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          ✏️ Status: ACTIVE (Editing Unlocked)
                        </span>
                      )}
                    </h2>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '4px', margin: '4px 0 0 0' }}>
                      Configure pool name, lock times, buy-back rules, pick visibility, and email preferences.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', width: 'auto', maxWidth: '100%' }}>
                    <button
                      onClick={() => {
                        if (isSettingsLockedEffective) {
                          setIsSettingsLocked(false);
                        } else if (adminSettings.pool_status === 'active') {
                          handleSaveSettings(false);
                        } else {
                          handleSaveSettings(false);
                        }
                      }}
                      disabled={savingAdminSettings}
                      style={isSettingsLockedEffective ? {
                        backgroundColor: '#ffffff',
                        color: '#475569',
                        border: '1px solid #cbd5e1',
                        padding: '9px 16px',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      } : {
                        backgroundColor: (adminSettings.pool_status === 'active' ? '#16a34a' : '#2563eb'),
                        color: '#ffffff',
                        padding: '10px 18px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                        cursor: 'pointer',
                        border: 'none'
                      }}
                    >
                      {isSettingsLockedEffective ? <Unlock size={15} color="#475569" /> : <Save size={16} />}
                      <span>
                        {isSettingsLockedEffective
                          ? '🔓 Unlock Settings to Edit'
                          : (savingAdminSettings
                              ? 'Saving Settings...'
                              : (adminSettings.pool_status === 'active' ? '💾 Save & Lock Pool Settings' : '💾 Save Pool Settings'))}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Quick Pool Rule Presets Bar (Shown only before pool launch) */}
                {settingsLoaded && adminSettings.pool_status !== 'active' && (
                  <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e40af', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Zap size={16} color="#2563eb" />
                      <span>Quick Pool Rule Presets</span>
                    </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {/* Preset 1: Strict Traditional */}
                    <button
                      disabled={isSettingsLockedEffective}
                      onClick={() => {
                        setAdminSettings({
                          ...adminSettings,
                          season_start_week: 1,
                          registration_lock_week: 1,
                          pick_opening_rule: 'weekly_rolling',
                          pick_lock_rule: 'thursday_kickoff',
                          unmade_pick_policy: 'policy_a_ghost',
                          pick_reveal_rule: 'pick_lock',
                          pick_reuse_rule: 'single',
                          buy_back_scope: 'losses_only'
                        });
                      }}
                      style={{ flex: '1 1 0', minWidth: '180px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #93c5fd', backgroundColor: '#ffffff', textAlign: 'left', cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                    >
                      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>🛡️ Strict Traditional</div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '2px' }}>W1 Hard Lock • Strict Ghost Elim (No Buy-Backs on Unmade Picks)</div>
                    </button>

                    {/* Preset 2: Flexible / Late Join */}
                    <button
                      disabled={isSettingsLockedEffective}
                      onClick={() => {
                        setAdminSettings({
                          ...adminSettings,
                          registration_lock_week: 2,
                          unmade_pick_policy: 'policy_b_buyback',
                          buy_back_scope: 'losses_and_unmade'
                        });
                      }}
                      style={{ flex: '1 1 0', minWidth: '180px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #93c5fd', backgroundColor: '#ffffff', textAlign: 'left', cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                    >
                      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>🎈 Flexible / Late-Join</div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '2px' }}>W2/W3 Late Entry • Buy-Backs Allowed for Missed Picks & Late Joiners</div>
                    </button>

                    {/* Preset 3: Auto-Favorite Safety Net */}
                    <button
                      disabled={isSettingsLockedEffective}
                      onClick={() => {
                        setAdminSettings({
                          ...adminSettings,
                          registration_lock_week: 1,
                          unmade_pick_policy: 'policy_b_favorite',
                          buy_back_scope: 'losses_only'
                        });
                      }}
                      style={{ flex: '1 1 0', minWidth: '180px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #93c5fd', backgroundColor: '#ffffff', textAlign: 'left', cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                    >
                      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>🤖 Auto-Favorite Safety Net</div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '2px' }}>W1 Hard Lock • Auto-assigns largest favorite on missed deadline (Burns team)</div>
                    </button>
                  </div>
                </div>
              )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                  
                  {/* Card 0 (Top Card): Pool Name Identification Card */}
                  <div style={{ gridColumn: '1 / -1', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tag size={18} color="#2563eb" />
                        <span>Pool Identification & Name</span>
                      </div>
                      <span style={{ fontSize: '0.78rem', color: (adminSettings.pool_name || adminSettings.name || activePool?.name || '').length > 45 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                        {(adminSettings.pool_name || adminSettings.name || activePool?.name || '').length} / 50 characters
                      </span>
                    </div>

                    <div>
                      <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '6px', fontSize: '0.85rem' }}>
                        Pool Name (Display Name across all dashboards & emails):
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        disabled={isSettingsLockedEffective}
                        placeholder="e.g. SurvivorPool.app Championship 2026"
                        value={adminSettings.pool_name || adminSettings.name || activePool?.name || ''}
                        onChange={(e) => {
                          const val = e.target.value.slice(0, 50);
                          setAdminSettings({ ...adminSettings, pool_name: val, name: val });
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff',
                          color: isSettingsLockedEffective ? '#64748b' : '#0f172a',
                          fontWeight: 700,
                          fontSize: '1rem',
                          cursor: isSettingsLockedEffective ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>
                  </div>

                  {/* Card 1: Deadlines & Lock Rules */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                      <Lock size={16} color="#2563eb" />
                      <span>Pick Locking & Registration Rules</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.85rem' }}>
                      {/* 0. Pool Start Week */}
                      <div>
                        <label
                          title="The first NFL week where game results count for pool standings and eliminations."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Pool Start Week (First Scoring Week):</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.season_start_week || 1}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, season_start_week: parseInt(e.target.value, 10) })}
                          title="Select the first NFL week where game results count"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value={1}>Week 1 (Standard Season Launch)</option>
                          <option value={2}>Week 2</option>
                          <option value={3}>Week 3</option>
                          <option value={4}>Week 4</option>
                          <option value={5}>Week 5 (Mid-Season Revival Pool)</option>
                          <option value={9}>Week 9 (Second Half Pool)</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '6px', lineHeight: 1.35 }}>
                          {(adminSettings.season_start_week || 1) > 1
                            ? `💡 Pool scoring & eliminations begin in Week ${adminSettings.season_start_week}. Weeks 1 through ${adminSettings.season_start_week - 1} are marked as ⏸️ Pre-Pool.`
                            : '💡 Pool scoring & eliminations begin in Week 1.'}
                        </div>
                      </div>

                      {/* 1. Registration Lock Week */}
                      <div>
                        <label
                          title="The week after which no new participants may join the pool."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Registration Lock Week (Close Pool):</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.registration_lock_week}
                          onChange={(e: any) => handleRegLockWeekChange(parseInt(e.target.value, 10))}
                          title="Select the final week open for new pool registrations"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value={1} title="Registration closes permanently after Week 1 kickoff.">Week 1 Kickoff (Hard Lock)</option>
                          <option value={2} title="Registration closes after Week 2 kickoff. Enables Late Join Buy-Back Catch-Up.">Week 2 Kickoff (Late Join Allowed)</option>
                          <option value={3} title="Registration closes after Week 3 kickoff. Enables Late Join Buy-Back Catch-Up.">Week 3 Kickoff (Late Join Allowed)</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '6px', lineHeight: 1.35 }}>
                          {adminSettings.registration_lock_week > 1
                            ? `💡 Late Registration Open through Week ${adminSettings.registration_lock_week}. Mutual Exclusivity Guard automatically permits buy-backs for missed picks and late joiners.`
                            : '💡 Registration closes permanently after Week 1 kickoff.'}
                        </div>
                      </div>

                      {/* 2. Pick Opening Window */}
                      <div>
                        <label
                          title="Controls when players are permitted to select picks for upcoming weeks."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Pick Opening Window:</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.pick_opening_rule}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, pick_opening_rule: e.target.value })}
                          title="Select weekly rolling opening or advance multi-week picking"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="weekly_rolling" title="Picks for Week N open on Tuesday at 3:00 AM ET (immediately after Monday Night Football ends for Week N-1).">Weekly Rolling Window (Default)</option>
                          <option value="advance_multi_week" title="Members can set or edit placeholder picks for any of the 18 weeks in advance prior to each game's lock deadline.">Advance Multi-Week</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '6px', lineHeight: 1.35 }}>
                          {adminSettings.pick_opening_rule === 'weekly_rolling'
                            ? '💡 Picks for Week N open on Tuesday at 3:00 AM ET (immediately after Monday Night Football ends for Week N-1).'
                            : '💡 Members can set or edit placeholder picks for any of the 18 weeks in advance prior to each game\'s lock deadline.'}
                        </div>
                      </div>

                      {/* 3. Weekly Pick Lock Deadline Rule */}
                      <div>
                        <label
                          title="Determines when participant picks lock for the week. Thursday games always lock individually at Thursday kickoff regardless of main deadline."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Weekly Pick Lock Deadline Rule:</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.pick_lock_rule}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, pick_lock_rule: e.target.value })}
                          title="Choose when picks lock for pool participants"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="thursday_kickoff" title="Locks all games for the entire week as soon as Thursday Night Football kicks off.">Start of Thursday Night Football (Default)</option>
                          <option value="saturday_kickoff" title="Locks all games for the week as soon as Saturday games start.">Start of Saturday Games</option>
                          <option value="sunday_early" title="Locks all games for the week on Sunday at 1:00 PM ET.">Start of Sunday Early Games (1:00 PM ET)</option>
                          <option value="individual_kickoff" title="Each game locks independently at its specific kickoff timestamp. Prevents picking Thursday winners on Friday or Sunday winners on Monday.">Individual Game Kickoffs</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '6px', lineHeight: 1.35 }}>
                          {adminSettings.pick_lock_rule === 'thursday_kickoff' && '💡 Locks all picks for the week as soon as Thursday Night Football kicks off.'}
                          {adminSettings.pick_lock_rule === 'saturday_kickoff' && '💡 Locks all picks for the week as soon as Saturday games start.'}
                          {adminSettings.pick_lock_rule === 'sunday_early' && '💡 Locks all picks for the week on Sunday at 1:00 PM ET.'}
                          {adminSettings.pick_lock_rule === 'individual_kickoff' && '💡 Each game locks independently at its specific kickoff timestamp. Thursday games lock Thursday night, Sunday games lock Sunday, etc.'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Unmade Pick & Ghost Elimination Policies */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                      <AlertCircle size={16} color="#dc2626" />
                      <span>Unmade Pick & Ghost Elimination Policy</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.85rem' }}>
                      <div>
                        <label
                          title="Determines the automated penalty or assignment rule when an active participant fails to submit a pick before the deadline."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Unmade Pick Policy (Forgotten Picks):</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.unmade_pick_policy}
                          onChange={(e: any) => handleUnmadePolicyChange(e.target.value)}
                          title="Select the automated rule for unmade picks"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="policy_a_ghost" title="Hard Elimination: Marked with a 👻 NO PICK Badge. Buy-backs are strictly prohibited for unmade picks. Requires Week 1 Hard Lock.">👻 Hard Elimination (No Buy-Back)</option>
                          <option value="policy_b_buyback" title="Forgiving Elimination: Marked with a 👻 NO PICK badge, but user IS permitted to use a Buy-Back to re-enter.">👻 Forgiving Elimination (Buy-Back Allowed)</option>
                          <option value="policy_b_favorite" title="Auto-assigns the team with the largest point-spread favorite remaining in that player's unused team list. (Burns team for season!)">📈 Auto-Pick Favorite (Burns Team)</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '8px', lineHeight: 1.4 }}>
                          {adminSettings.unmade_pick_policy === 'policy_a_ghost' && '💡 Hard Elimination: 👻 NO PICK Badge assigned. Buy-backs strictly prohibited. Enforces Week 1 Hard Registration Lock.'}
                          {adminSettings.unmade_pick_policy === 'policy_b_buyback' && '💡 Forgiving Elimination: 👻 NO PICK badge assigned, but player IS permitted to use a Buy-Back to re-enter.'}
                          {adminSettings.unmade_pick_policy === 'policy_b_favorite' && '💡 Safety Net: Auto-assigns the week\'s largest point-spread favorite. Player stays alive, but permanently burns that team for the season!'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Buy-In & Buy-Back Financial Settings */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                      <CreditCard size={16} color="#16a34a" />
                      <span>Buy-In & Buy-Back Pricing</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem' }}>
                      <div>
                        <label
                          title="The monetary entry fee required from each participant to enter the pool."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Entry Buy-In ($):</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <input
                          type="number"
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.buy_in_cost}
                          onChange={(e) => setAdminSettings({ ...adminSettings, buy_in_cost: parseFloat(e.target.value) || 0 })}
                          title="Set entry fee per participant"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'text' }}
                        />
                      </div>

                      <div>
                        <label
                          title="The cost to buy back into the pool after an early-season elimination."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Buy-Back Fee ($):</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <input
                          type="number"
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.buy_back_cost}
                          onChange={(e) => setAdminSettings({ ...adminSettings, buy_back_cost: parseFloat(e.target.value) || 0 })}
                          title="Set cost per buy-back"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'text' }}
                        />
                      </div>

                      <div>
                        <label
                          title="Maximum number of buy-backs an eliminated participant can purchase."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Max Allowed:</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.max_buy_backs}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, max_buy_backs: parseInt(e.target.value, 10) })}
                          title="Select maximum buy-backs per participant"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value={0}>0 (No Buy-backs)</option>
                          <option value={1}>1 Buy-back</option>
                          <option value={2}>2 Buy-backs</option>
                          <option value={3}>3 Buy-backs</option>
                        </select>
                      </div>

                      <div>
                        <label
                          title="Latest week in which buy-backs are permitted (e.g. Week 3)."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Cutoff Week:</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.buy_back_max_week}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, buy_back_max_week: parseInt(e.target.value, 10) })}
                          title="Select latest week for buy-back eligibility"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value={1}>Through Week 1</option>
                          <option value={2}>Through Week 2</option>
                          <option value={3}>Through Week 3</option>
                          <option value={4}>Through Week 4</option>
                          <option value={6}>Through Week 6</option>
                        </select>
                      </div>

                      {/* Commissioner Payment Instructions / Notes */}
                      <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
                        <label
                          title="Payment instructions or notes for pool buy-ins and buy-backs (e.g. Venmo @Commissioner, Zelle 555-0199, cash in person)."
                          style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', cursor: 'help' }}
                        >
                          <span>Commissioner Payment Notes / Instructions:</span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>ℹ️</span>
                        </label>
                        <input
                          type="text"
                          disabled={isSettingsLockedEffective}
                          placeholder="e.g. Venmo @Commissioner, Zelle 555-0199, or cash prior to Week 1 Kickoff."
                          value={adminSettings.payment_instructions || ''}
                          onChange={(e) => setAdminSettings({ ...adminSettings, payment_instructions: e.target.value })}
                          title="Set payment instructions for participants"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 500, cursor: isSettingsLockedEffective ? 'not-allowed' : 'text' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Pick Visibility & Team Reuse */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                      <Eye size={16} color="#d97706" />
                      <span>Pick Reveal & Team Reuse Rules</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.85rem' }}>
                      <div>
                        <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                          When to Reveal Picks to Pool:
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.pick_reveal_rule}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, pick_reveal_rule: e.target.value })}
                          title="Choose when member picks are made visible on the group matrix"
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="pick_lock">At Pick Lock (Recommended & Default)</option>
                          <option value="as_submitted">As Submitted</option>
                          <option value="game_kickoff">On Individual Kickoffs</option>
                        </select>

                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '6px', lineHeight: 1.35 }}>
                          {adminSettings.pick_reveal_rule === 'pick_lock' && '💡 All picks for the week are revealed simultaneously to all participants at the lock deadline.'}
                          {adminSettings.pick_reveal_rule === 'as_submitted' && '💡 Picks are visible on the public matrix as soon as a participant submits them.'}
                          {adminSettings.pick_reveal_rule === 'game_kickoff' && '💡 A participant\'s pick is revealed only as their chosen game kicks off.'}
                        </div>
                      </div>

                      <div>
                        <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                          Team Reuse Policy:
                        </label>
                        <select
                          disabled={isSettingsLockedEffective}
                          value={adminSettings.pick_reuse_rule}
                          onChange={(e: any) => setAdminSettings({ ...adminSettings, pick_reuse_rule: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="single">Single Pick (Use team only 1 time per season)</option>
                          <option value="double">Double Pick (Can pick team up to 2 times)</option>
                          <option value="double_late_season">Split Season (Single Pick W1-14, Double Pick W15-18)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Email Notifications */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '18px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a' }}>
                      <Bell size={16} color="#2563eb" />
                      <span>Email Reminder Policy</span>
                    </div>

                    <div style={{ fontSize: '0.85rem' }}>
                      <label style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Automated Pick Reminder Preference:
                      </label>
                      <select
                        disabled={isSettingsLockedEffective}
                        value={adminSettings.reminder_email_rule}
                        onChange={(e: any) => setAdminSettings({ ...adminSettings, reminder_email_rule: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: isSettingsLockedEffective ? '#f1f5f9' : '#ffffff', color: isSettingsLockedEffective ? '#64748b' : '#0f172a', fontWeight: 600, cursor: isSettingsLockedEffective ? 'not-allowed' : 'pointer' }}
                      >
                        <option value="user_choice">Allow users to select opt-in/opt-out on registration</option>
                        <option value="all_on">Mandatory ON (Send to all active participants)</option>
                        <option value="all_off">Disabled (OFF globally)</option>
                      </select>

                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '10px', lineHeight: 1.4 }}>
                        Reminders are automatically dispatched 24h & 4h prior to weekly pick lock deadlines.
                      </div>
                    </div>
                  </div>

                </div>

                {/* Launch Pool & Full 18-Week Schedule Fetch Banner (Moved to Bottom of Settings Page) */}
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '12px', padding: '20px', marginTop: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={20} color="#2563eb" />
                      <span>Season Setup & Pool Launch ({adminSettings.season_year} Season)</span>
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: '#1e40af', marginTop: '2px', lineHeight: 1.4 }}>
                      Launching the pool pulls the entire 18-week NFL schedule into D1 upfront and opens member registrations.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={() => {
                        if (isSettingsLockedEffective) {
                          setIsSettingsLocked(false);
                        } else if (adminSettings.pool_status === 'active') {
                          handleSaveSettings(false);
                        } else {
                          setShowPoolLaunchModal(true);
                        }
                      }}
                      disabled={launchingPool || savingAdminSettings}
                      style={isSettingsLockedEffective ? {
                        backgroundColor: '#ffffff',
                        color: '#475569',
                        border: '1px solid #cbd5e1',
                        padding: '10px 18px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        fontSize: '0.86rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      } : {
                        backgroundColor: (adminSettings.pool_status === 'active' ? '#16a34a' : '#2563eb'),
                        color: '#ffffff',
                        padding: '12px 20px',
                        borderRadius: '10px',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                        cursor: 'pointer'
                      }}
                    >
                      {isSettingsLockedEffective ? (
                        <Unlock size={16} color="#475569" />
                      ) : (
                        adminSettings.pool_status === 'active' ? <Save size={18} /> : <Rocket size={18} />
                      )}
                      <span>
                        {isSettingsLockedEffective
                          ? '🔓 Unlock Settings to Edit'
                          : (launchingPool || savingAdminSettings
                              ? 'Processing...'
                              : (adminSettings.pool_status === 'active' ? '💾 Save & Lock Pool Settings' : '🚀 Save Settings & Launch Pool'))}
                      </span>
                    </button>
                  </div>
                </div>

                {/* ⚠️ DANGER ZONE: PERMANENT POOL DELETION */}
                {(primaryPortal === 'pool_admin' || primaryPortal === 'site_admin') && activePool && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: '12px',
                    padding: '20px',
                    marginTop: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle size={18} color="#dc2626" />
                        <span>Danger Zone: Delete Survivor Pool</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#b91c1c', marginTop: '4px', lineHeight: 1.4 }}>
                        Permanently delete <strong>"{activePool.name}"</strong> and erase all associated rosters, picks, and standings. This action cannot be undone.
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setDeleteFailsafeChecked(false);
                        setShowDeletePoolModal(true);
                      }}
                      title="Permanently Delete this Pool"
                      style={{
                        backgroundColor: '#dc2626',
                        color: '#ffffff',
                        border: 'none',
                        padding: '10px 18px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Trash2 size={16} />
                      <span>🗑️ Permanently Delete Pool</span>
                    </button>
                  </div>
                )}

                    </>
                  );
                })()}

              </div>
            </div>
          )}

            {/* Rule Change Summary Email Modal */}
            {showSettingsChangeModal && (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                <div style={{ backgroundColor: '#ffffff', color: '#0f172a', borderRadius: '16px', border: '1px solid #cbd5e1', maxWidth: '560px', width: '100%', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', color: '#1d4ed8' }}>
                    <Mail size={24} />
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>Send Rule Changes Summary to Members?</h3>
                  </div>
                  <p style={{ fontSize: '0.86rem', color: '#475569', lineHeight: 1.5, marginBottom: '16px' }}>
                    You have updated official pool rules for an active pool with registered participants. Would you like to automatically email a summary of these changes to all enrolled members?
                  </p>
                  {/* Diffs Table */}
                  <div style={{ backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '12px', marginBottom: '20px', maxHeight: '220px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #cbd5e1', textAlign: 'left', color: '#64748b' }}>
                          <th style={{ padding: '6px' }}>Rule Setting</th>
                          <th style={{ padding: '6px' }}>Previous</th>
                          <th style={{ padding: '6px' }}>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settingsDiffs.map((d, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 6px', fontWeight: 700 }}>{d.field}</td>
                            <td style={{ padding: '8px 6px', color: '#dc2626', textDecoration: 'line-through' }}>{d.oldVal}</td>
                            <td style={{ padding: '8px 6px', color: '#16a34a', fontWeight: 700 }}>{d.newVal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Action Buttons: Go Back, Save Without Emailing, Send Email & Lock */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
                    <button
                      onClick={() => setShowSettingsChangeModal(false)}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                    >
                      ← Go Back to Edit
                    </button>
                    <button
                      onClick={() => { setShowSettingsChangeModal(false); handleSaveSettings(true); }}
                      style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', color: '#334155', fontWeight: 700, cursor: 'pointer' }}
                    >
                      💾 Save Without Emailing
                    </button>
                    <button
                      onClick={handleSendDiffEmailAndSave}
                      disabled={sendingDiffEmail}
                      style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#2563eb', color: '#ffffff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Mail size={16} />
                      <span>{sendingDiffEmail ? 'Sending Emails...' : '✉️ Send Email & Lock Settings'}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-PAGE 2: FINANCIAL LEDGER & USER PAYMENTS */}
            {poolAdminSubTab === 'payment_ledger' && (
              <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                
                {/* Pending Buy-Back Requests Queue (Prominently Placed at Very Top of Ledger Tab) */}
                {adminBuybacks.filter((b) => b.status === 'pending').length > 0 && (
                  <div style={{ backgroundColor: '#fffbeb', border: '2px solid #f59e0b', borderRadius: '14px', padding: '20px', marginBottom: '24px', boxShadow: '0 4px 14px rgba(245, 158, 11, 0.15)' }}>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                      <RefreshCw size={18} color="#d97706" />
                      <span>📥 Action Required: Pending Buy-Back Requests ({adminBuybacks.filter((b) => b.status === 'pending').length})</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {adminBuybacks.filter((b) => b.status === 'pending').map((ub) => (
                        <div key={ub.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff', border: '1px solid #fde68a', padding: '14px 18px', borderRadius: '10px', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{ub.name || ub.user_id}</span>
                              <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 400 }}>({ub.email})</span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#d97706', fontWeight: 600, marginTop: '2px' }}>
                              Requested Week {ub.week} Buy-Back (${ub.amount}) • {new Date(ub.created_at).toLocaleString()}
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={() => handleApproveBuyback(ub.id)}
                              style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)' }}
                            >
                              <CheckCircle2 size={14} /> Mark Paid & Approve (${ub.amount})
                            </button>
                            <button
                              onClick={() => handleRejectBuyback(ub.id)}
                              style={{ backgroundColor: '#ef4444', color: '#ffffff', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <XCircle size={14} /> Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 📩 Batch Player Invitation Card (Collapses/disappears after registration lock week) */}
                {activeWeek <= adminSettings.registration_lock_week && (
                  <div style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <h4 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Send size={18} color="#2563eb" />
                        <span>📩 Invite Players to Pool</span>
                      </h4>
                      <span style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 700, backgroundColor: '#dcfce7', padding: '3px 8px', borderRadius: '12px', border: '1px solid #86efac' }}>
                        📅 Registration Open (Closes Week {adminSettings.registration_lock_week} Kickoff)
                      </span>
                    </div>

                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '10px' }}>
                      Paste participant email addresses below (separated by commas, semicolons, or newlines) to dispatch magic link invitation emails.
                    </p>

                    <textarea
                      rows={3}
                      value={inviteInputText}
                      onChange={(e) => setInviteInputText(e.target.value)}
                      placeholder="john@example.com, sarah@example.com, mike@example.com"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none', marginBottom: '10px' }}
                    />

                    <button
                      onClick={handleSendBatchInvites}
                      disabled={sendingInvites || !inviteInputText.trim()}
                      style={{
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        border: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <Send size={14} />
                      <span>{sendingInvites ? 'Sending Pool Invites...' : 'Send Pool Invites'}</span>
                    </button>

                    {/* Generated Invites Table with Resend, Copy Link, Delete Actions (Collapsible UX) */}
                    {invitedList.length > 0 && (
                      <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>Recent Pool Invitations ({invitedList.length})</span>
                          </div>
                          <button
                            onClick={() => setInvitesCollapsed(!invitesCollapsed)}
                            style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                          >
                            {invitesCollapsed ? '▼ Show Invitations' : '▲ Collapse List'}
                          </button>
                        </div>

                        {!invitesCollapsed && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {invitedList.map((inv) => (
                              <div key={inv.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '8px 12px', borderRadius: '6px', fontSize: '0.8rem', flexWrap: 'wrap', gap: '8px' }}>
                                <div>
                                  <strong>{inv.name}</strong> ({inv.email})
                                  <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: inv.emailSent ? '#16a34a' : '#d97706', fontWeight: 700 }}>
                                    {inv.emailSent ? '✓ Sent via Cloudflare Email' : '⚠️ Pending Delivery'}
                                  </span>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <button
                                    onClick={() => handleResendInvite(inv.email)}
                                    style={{ backgroundColor: '#eff6ff', border: '1px solid #93c5fd', color: '#1d4ed8', padding: '5px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                  >
                                    🔁 Resend Invite
                                  </button>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(inv.magicLink);
                                      setToast({ type: 'success', text: `Copied 7-day registration link for ${inv.email}!` });
                                    }}
                                    style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', padding: '5px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                  >
                                    📋 Copy Magic Link
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (confirm(`Are you sure you want to delete pending invitation for ${inv.email}?`)) {
                                        handleDeletePendingInvite(inv.email);
                                      }
                                    }}
                                    style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', padding: '5px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                  >
                                    🗑️ Delete Pending
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Admin Financial Overview Header (Compact Equal Flex Strip) */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '130px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <DollarSign size={13} color="#2563eb" />
                      <span>Purse</span>
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                      ${totalPotValue.toLocaleString()}
                    </div>
                  </div>

                  <div style={{ flex: '1 1 0', minWidth: '130px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#16a34a', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <CheckCircle2 size={13} color="#16a34a" />
                      <span>Collected</span>
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>
                      ${totalPotCollected.toLocaleString()}
                    </div>
                  </div>

                  <div style={{ flex: '1 1 0', minWidth: '130px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#dc2626', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <AlertCircle size={13} color="#dc2626" />
                      <span>Unpaid</span>
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#dc2626', marginTop: '2px' }}>
                      ${totalUnpaidBalance.toLocaleString()}
                    </div>
                  </div>

                  <div style={{ flex: '1 1 0', minWidth: '130px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#2563eb', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <Users size={13} color="#2563eb" />
                      <span>Buy-Backs</span>
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2563eb', marginTop: '2px' }}>
                      {totalBuyBacksCount}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={18} color="#16a34a" />
                      <span>Participant Payment & Buy-Back Ledger ({activePool?.name})</span>
                    </h3>
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                      Track buy-in payments, buy-back counts, payment settlement, and reminder preferences per member.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={() => handleSendNudge()}
                      disabled={sendingNudge}
                      style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fca5a5',
                        color: '#991b1b',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Mail size={15} color="#991b1b" />
                      <span>{sendingNudge ? 'Sending Reminders...' : '📩 Nudge All Unpaid Players'}</span>
                    </button>

                    <button
                      onClick={handleExportLedgerCSV}
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        color: '#334155',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Download size={15} color="#2563eb" />
                      <span>Export Ledger CSV</span>
                    </button>
                  </div>
                </div>

                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569', fontSize: '0.78rem' }}>
                        <th style={{ padding: '12px 14px', minWidth: '180px', whiteSpace: 'nowrap' }}>Member</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center', width: '65px', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '130px', whiteSpace: 'nowrap' }}>Buy-In Status</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '120px', whiteSpace: 'nowrap' }}>Buy-Backs Used</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '120px', whiteSpace: 'nowrap' }}>Buy-Back Paid</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '110px', whiteSpace: 'nowrap' }}>Email Alert</th>
                        <th style={{ padding: '12px 10px', textAlign: 'right', minWidth: '110px', whiteSpace: 'nowrap' }}>Total Balance</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', minWidth: '110px', whiteSpace: 'nowrap' }}>Settlement</th>
                        <th style={{ padding: '12px 14px', minWidth: '220px', whiteSpace: 'nowrap' }}>Notes / Payment Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userPayments.map((p) => {
                        const uGrid = (gridData?.grid || []).find((g) => (g?.user?.email && p.email && g.user.email.toLowerCase() === p.email.toLowerCase()) || (g?.user?.id && p.user_id && g.user.id === p.user_id));
                        const usedCount = uGrid?.buybacksUsed ?? (p.buy_backs_used || 0);
                        const isGhost = uGrid ? (!uGrid.isAlive && uGrid.eliminatedWeek && !uGrid.weeklyPicks[uGrid.eliminatedWeek]) : false;
                        const curWk = gridData?.currentWeek || activeWeek || 1;
                        const isGraceWkBuyback = uGrid ? (uGrid.isAlive && uGrid.buybackUsedWeek && curWk === uGrid.buybackUsedWeek + 1) : false;

                        let statusEmoji = '🟢';
                        let statusTooltip = 'Alive';
                        if (uGrid) {
                          if (uGrid.isAlive) {
                            if (isGraceWkBuyback) {
                              statusEmoji = '🔁';
                              statusTooltip = `Buyback Re-Entry (Grace W${curWk})`;
                            } else {
                              statusEmoji = '🟢';
                              statusTooltip = 'Alive';
                            }
                          } else {
                            if (isGhost) {
                              statusEmoji = '👻';
                              statusTooltip = `Eliminated via Ghost Pick (W${uGrid.eliminatedWeek})`;
                            } else {
                              statusEmoji = '🔴';
                              statusTooltip = `Eliminated (W${uGrid.eliminatedWeek})`;
                            }
                          }
                        }

                        const buyInCost = adminSettings.buy_in_cost || 10;
                        const buyBackCost = adminSettings.buy_back_cost || 10;
                        const totalOwed = buyInCost + (usedCount * buyBackCost);
                        const totalPaid = (p.buy_in_paid ? buyInCost : 0) + (p.buy_backs_paid ? usedCount * buyBackCost : 0);
                        const isFullyPaid = totalPaid >= totalOwed && totalOwed > 0;

                        const pendingBuybackObj = adminBuybacks.find((b) => (b.user_id === p.user_id || b.email === p.email) && b.status === 'pending');
                        const hasPendingBuyback = !!pendingBuybackObj;
                        const displayNotes = p.notes === 'buyback_requested' ? '' : (p.notes || '');

                        return (
                          <tr key={p.user_id} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: hasPendingBuyback ? '#fffbeb' : '#ffffff' }}>
                            {/* Member Column */}
                            <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0f172a' }}>
                              <div>
                                <span>{formatDisplayName(p.name, p.email)}</span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.name && p.name !== formatDisplayName(p.name, p.email) ? `${p.name} • ` : ''}{p.email}</div>
                            </td>

                            {/* Standardized Status Column (Matching Group Picks & Standings Table) */}
                            <td style={{ padding: '12px 8px', textAlign: 'center', whiteSpace: 'nowrap', fontSize: '1.15rem' }}>
                              <span title={statusTooltip} style={{ cursor: 'help' }}>
                                {statusEmoji}
                              </span>
                            </td>

                            {/* Buy-In Status Column */}
                            <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button
                                onClick={() => handleUpdateUserPayment({ ...p, buy_in_paid: p.buy_in_paid === 1 ? 0 : 1 })}
                                style={{
                                  padding: '5px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  border: 'none',
                                  backgroundColor: p.buy_in_paid === 1 ? '#dcfce7' : '#fee2e2',
                                  color: p.buy_in_paid === 1 ? '#15803d' : '#b91c1c',
                                  cursor: 'pointer'
                                }}
                              >
                                {p.buy_in_paid === 1 ? `PAID ($${buyInCost}) ✅` : 'UNPAID ❌'}
                              </button>
                            </td>

                            {/* Buy-Backs Used Column (Automated Numeric Integer) */}
                            <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px' }}>
                                {usedCount}
                              </span>
                            </td>

                            {/* Buy-Back Paid Column */}
                            <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {hasPendingBuyback ? (
                                <span style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '4px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.72rem', display: 'inline-block' }}>
                                  ⏳ W{pendingBuybackObj.week} Pending (${pendingBuybackObj.amount})
                                </span>
                              ) : usedCount > 0 ? (
                                <button
                                  onClick={() => handleUpdateUserPayment({ ...p, buy_backs_paid: p.buy_backs_paid === 1 ? 0 : 1 })}
                                  style={{
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    border: 'none',
                                    backgroundColor: p.buy_backs_paid === 1 ? '#dcfce7' : '#fee2e2',
                                    color: p.buy_backs_paid === 1 ? '#15803d' : '#b91c1c',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {p.buy_backs_paid === 1 ? 'PAID ✅' : 'UNPAID ❌'}
                                </button>
                              ) : (
                                <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>N/A</span>
                              )}
                            </td>

                            {/* Email Alert Preference Column */}
                            <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button
                                onClick={() => handleUpdateUserPayment({ ...p, reminder_opt_in: p.reminder_opt_in === 1 ? 0 : 1 })}
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  border: '1px solid #cbd5e1',
                                  backgroundColor: p.reminder_opt_in === 1 ? '#eff6ff' : '#f8fafc',
                                  color: p.reminder_opt_in === 1 ? '#1d4ed8' : '#64748b',
                                  cursor: 'pointer'
                                }}
                              >
                                {p.reminder_opt_in === 1 ? '🔔 Opted In' : '🔕 Opted Out'}
                              </button>
                            </td>

                            {/* Total Owed / Balance Column */}
                            <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 700, color: isFullyPaid ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>
                              ${totalPaid} / ${totalOwed}
                            </td>

                            {/* 1-Click Settlement Column */}
                            <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {isFullyPaid ? (
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#15803d', backgroundColor: '#dcfce7', padding: '3px 8px', borderRadius: '6px', border: '1px solid #86efac' }}>
                                  🟢 Settled
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleSettleAccount(p)}
                                  style={{ fontSize: '0.72rem', fontWeight: 800, color: '#ffffff', backgroundColor: '#16a34a', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)' }}
                                >
                                  ⚡ Settle (${totalOwed - totalPaid} Due)
                                </button>
                              )}
                            </td>

                            {/* Notes / Payment Method Column */}
                            <td style={{ padding: '12px 14px' }}>
                              <input
                                type="text"
                                value={displayNotes}
                                placeholder="e.g. Venmo, Zelle, Cash..."
                                onChange={(e) => setUserPayments((prev) => prev.map((item) => (item.user_id === p.user_id ? { ...item, notes: e.target.value } : item)))}
                                onBlur={(e) => handleUpdateUserPayment({ ...p, notes: e.target.value })}
                                style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.78rem' }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

              </div>
            )}

            {/* SUB-PAGE 3: SCHEDULE MANAGEMENT (READ-ONLY FOR POOL ADMINS) */}
            {poolAdminSubTab === 'schedule_control' && (
              <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={20} color="#2563eb" />
                      <span>NFL Schedule & Lock Deadlines (Week {activeWeek})</span>
                    </h2>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>
                      Review official 2026 NFL game matchups and lock times for your pool. Global schedule updates and ESPN syncs are managed by Site Administrators.
                    </p>
                  </div>

                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 700 }}>
                    <ShieldCheck size={14} color="#2563eb" />
                    <span>Official NFL Schedule Active • Managed by Site Admin</span>
                  </div>
                </div>

                {/* 18-Week Date & Time Schedule & Lock Deadlines Table (Pool Admin Preview) */}
                <div style={{ marginTop: '16px', overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569', fontSize: '0.78rem' }}>
                        <th style={{ padding: '12px 14px', width: '10%', whiteSpace: 'nowrap' }}>Week</th>
                        <th style={{ padding: '12px 14px', width: '25%', whiteSpace: 'nowrap' }}>Pick Opening Window</th>
                        <th style={{ padding: '12px 14px', width: '33%', whiteSpace: 'nowrap' }}>Pick Lock Deadline</th>
                        <th style={{ padding: '12px 14px', width: '20%', whiteSpace: 'nowrap' }}>NFL Week Span</th>
                        <th style={{ padding: '12px 14px', width: '12%', textAlign: 'center', whiteSpace: 'nowrap' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => {
                        const isCurrent = w === activeWeek;
                        const isPast = w < activeWeek;

                        // Base Thursday Kickoff Date for Week w (Week 1 = Sep 10, 2026 8:15 PM ET)
                        const baseThuMs = new Date(2026, 8, 10, 20, 15, 0).getTime() + (w - 1) * 7 * 24 * 60 * 60 * 1000;
                        const thuDateObj = new Date(baseThuMs);
                        const thuFormatted = thuDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + ' • 8:15 PM ET';

                        // Tuesday opening (2 days prior to Thursday kickoff at 3:00 AM ET)
                        const openMs = baseThuMs - (2 * 24 * 60 * 60 * 1000) - (17 * 60 * 60 * 1000 + 15 * 60 * 1000);
                        const openDateObj = new Date(openMs);
                        const openFormatted = adminSettings.pick_opening_rule === 'advance_multi_week'
                          ? 'Open Full Season (Pool Launch)'
                          : (w === 1 ? 'Pool Launch' : openDateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ' • 3:00 AM ET');

                        // Monday Night Football Date (4 days after Thursday kickoff)
                        const monMs = baseThuMs + (4 * 24 * 60 * 60 * 1000);
                        const monDateObj = new Date(monMs);

                        let lockDisplay = thuFormatted;
                        if (adminSettings.pick_lock_rule === 'individual_kickoff') {
                          lockDisplay = `Rolling Kickoffs (Thu ${thuDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – Mon ${monDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
                        } else if (adminSettings.pick_lock_rule === 'sunday_early') {
                          lockDisplay = `Sunday Early Kickoffs • ${thuDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        }

                        return (
                          <tr key={w} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: isCurrent ? '#f0f9ff' : '#ffffff' }}>
                            <td style={{ padding: '12px 14px', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                              Week {w} {isCurrent && <span style={{ fontSize: '0.7rem', backgroundColor: '#2563eb', color: '#fff', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}>Active</span>}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#334155', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {openFormatted}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {lockDisplay}
                            </td>
                            <td style={{ padding: '12px 14px', color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              Thu {thuDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ➔ Mon {monDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <span style={{
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                padding: '3px 10px',
                                borderRadius: '12px',
                                backgroundColor: isPast ? '#f1f5f9' : isCurrent ? '#dcfce7' : '#eff6ff',
                                color: isPast ? '#64748b' : isCurrent ? '#15803d' : '#1d4ed8',
                                border: `1px solid ${isPast ? '#cbd5e1' : isCurrent ? '#86efac' : '#93c5fd'}`
                              }}>
                                {isPast ? '🔒 Finalized' : isCurrent ? '🟢 Open for Picks' : '⏳ Upcoming'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════ */}
        {/* PORTAL 3: SITE ADMIN PORTAL                                          */}
        {/* ══════════════════════════════════════════════════════════════════════ */}
        {primaryPortal === 'site_admin' && isSiteAdmin && (
          <div>
            
            {/* SUB-PAGE 1: USER DIRECTORY & ALLOWLIST */}
            {siteAdminSubTab === 'user_directory' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
                
                {/* Add Authorized User Card */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <UserPlus size={20} color="#2563eb" />
                    <span>Add Authorized Account</span>
                  </h3>
                  <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '20px', lineHeight: 1.4 }}>
                    Authorized accounts can log in via 60-second magic links and participate in survivor pools.
                  </p>

                  <form onSubmit={handleAddAllowlistUser} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Email Address:
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="member@example.com"
                        value={newAllowlistEmail}
                        onChange={(e) => setNewAllowlistEmail(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Full Name:
                      </label>
                      <input
                        type="text"
                        placeholder="First & Last Name"
                        value={newAllowlistName}
                        onChange={(e) => setNewAllowlistName(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '4px' }}>
                        Global System Role:
                      </label>
                      <select
                        value={newAllowlistRole}
                        onChange={(e: any) => setNewAllowlistRole(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', backgroundColor: '#ffffff', fontWeight: 600 }}
                      >
                        <option value="user">Pool Member (Standard User)</option>
                        <option value="site_admin">⭐ Site Administrator (Full System Access)</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      disabled={addingAllowlist}
                      style={{
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        padding: '12px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <UserPlus size={16} />
                      {addingAllowlist ? 'Adding User...' : 'Add to Allowlist'}
                    </button>
                  </form>
                </div>

                {/* Current Consolidated User Directory Table */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <ShieldCheck size={20} color="#16a34a" />
                    <span>D1 User Directory ({allowlistUsers.length} Authorized Accounts)</span>
                  </h3>

                  <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569', whiteSpace: 'nowrap' }}>
                          <th style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>Name & Email</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>Account Status</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>Global Role</th>
                          <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allowlistUsers.map((u) => (
                          <tr key={u.email} style={{ borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                              <div>{u.name}</div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{u.email}</div>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span style={{
                                backgroundColor: u.account_status === 'invited' ? '#fef3c7' : '#dcfce7',
                                color: u.account_status === 'invited' ? '#b45309' : '#15803d',
                                border: u.account_status === 'invited' ? '1px solid #fde68a' : '1px solid #86efac',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '0.72rem'
                              }}>
                                {u.account_status === 'invited' ? '📩 Invite Pending' : '🟢 Active Account'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span style={{
                                backgroundColor: u.global_role === 'site_admin' ? '#eff6ff' : '#f8fafc',
                                color: u.global_role === 'site_admin' ? '#1d4ed8' : '#475569',
                                border: u.global_role === 'site_admin' ? '1px solid #93c5fd' : '1px solid #cbd5e1',
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '0.72rem'
                              }}>
                                {u.global_role === 'site_admin' ? '⭐ Site Admin' : 'User'}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              <button
                                onClick={() => {
                                  setEditingUserName({ userId: u.email, email: u.email, name: u.name });
                                  setNewNameInput(u.name || '');
                                }}
                                title="Edit Global Display Name"
                                style={{
                                  backgroundColor: '#eff6ff',
                                  color: '#2563eb',
                                  border: '1px solid #bfdbfe',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.78rem',
                                  fontWeight: 700,
                                  cursor: 'pointer'
                                }}
                              >
                                ✏️ Edit Name
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {/* SUB-PAGE 2: POOL DIRECTORY */}
            {siteAdminSubTab === 'pool_management' && (
              <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <Layers size={20} color="#2563eb" />
                  <span>Multi-Pool Directory & Management</span>
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {userPools.map((p) => (
                    <div key={p.id} style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>{p.name}</div>
                        <span style={{ fontSize: '0.72rem', backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                          Season {p.season}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '6px' }}>
                        Pool ID: <code style={{ color: '#2563eb' }}>{p.id}</code>
                      </div>
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>Your Role: {p.role_in_pool}</span>
                        <button
                          onClick={() => { setActivePool(p); setPrimaryPortal('pool_admin'); }}
                          style={{ fontSize: '0.78rem', backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          Manage Pool ➔
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SUB-PAGE: GLOBAL NFL SCHEDULE & SYNC ENGINE */}
            {siteAdminSubTab === 'schedule_engine' && (
              <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={20} color="#dc2626" />
                      <span>Global NFL Schedule & Sync Engine (Week {activeWeek})</span>
                    </h2>
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>
                      Platform-wide controls to synchronize official 2026 NFL regular season schedules, kickoff times, spreads, and venues from ESPN into Cloudflare D1.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => checkDiscrepancies(activeWeek, true)}
                      disabled={checkingDiscrepancies}
                      style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#f8fafc',
                        color: '#334155',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <RefreshCw size={14} className={checkingDiscrepancies ? 'spin' : ''} />
                      <span>Check Discrepancies Daily</span>
                    </button>

                    <button
                      onClick={() => setShowScheduleSyncModal(true)}
                      disabled={syncingSchedule || launchingPool}
                      style={{
                        backgroundColor: discrepancyData?.hasDiscrepancy ? '#d97706' : '#2563eb',
                        color: '#ffffff',
                        padding: '10px 18px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: 'none',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        cursor: 'pointer'
                      }}
                    >
                      <RefreshCw size={16} className={syncingSchedule || launchingPool ? 'spin' : ''} />
                      <span>Sync Full Season Schedule (Weeks 1–18)</span>
                      {discrepancyData && (
                        <span style={{
                          backgroundColor: discrepancyData.hasDiscrepancy ? '#fee2e2' : '#dcfce7',
                          color: discrepancyData.hasDiscrepancy ? '#b91c1c' : '#15803d',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          marginLeft: '4px'
                        }}>
                          {discrepancyData.hasDiscrepancy ? `🟡 ${discrepancyData.count} Discrepancy` : '🟢 Up To Date'}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {discrepancyData && discrepancyData.hasDiscrepancy && (
                  <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '16px', fontSize: '0.85rem' }}>
                    <div style={{ fontWeight: 700, color: '#b45309', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertCircle size={16} />
                      <span>Discrepancies Detected Between Stored Pool Schedule and Official ESPN Schedule:</span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400e', lineHeight: 1.5 }}>
                      {discrepancyData.list.map((d, idx) => (
                        <li key={idx}>{d}</li>
                      ))}
                    </ul>
                    <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#b45309', fontStyle: 'italic' }}>
                      An automated discrepancy alert email has been sent to pool administrators (jasonc1201@gmail.com, johnrbressler@gmail.com).
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SUB-PAGE 3: DATABASE EXPLORER & READ-ONLY SQL CONSOLE */}
            {siteAdminSubTab === 'db_explorer' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* 1. Database Table Inspector Card */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Database size={22} color="#2563eb" />
                      <div>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>Cloudflare D1 Table Inspector</h3>
                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>Select a database table to inspect schema columns, row records, and pagination.</p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <select
                        value={selectedDbTable}
                        onChange={(e) => {
                          setSelectedDbTable(e.target.value);
                          setDbTablePage(1);
                          fetchDbTableData(e.target.value, 1, dbTableLimit);
                        }}
                        style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}
                      >
                        {dbTables.map((t) => (
                          <option key={t.name} value={t.name}>
                            📊 {t.name} ({t.rowCount} rows)
                          </option>
                        ))}
                      </select>

                      <button
                        onClick={() => fetchDbTableData(selectedDbTable, dbTablePage, dbTableLimit)}
                        disabled={loadingTableData}
                        style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 12px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <RefreshCw size={14} className={loadingTableData ? 'spin' : ''} />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>

                  {/* Filter & Search Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <Search size={16} color="#64748b" />
                    <input
                      type="text"
                      placeholder={`Filter records in "${selectedDbTable}"...`}
                      value={dbTableFilter}
                      onChange={(e) => setDbTableFilter(e.target.value)}
                      style={{ border: 'none', backgroundColor: 'transparent', outline: 'none', fontSize: '0.85rem', width: '100%' }}
                    />
                    {dbTableFilter && (
                      <button onClick={() => setDbTableFilter('')} style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>✕ Clear</button>
                    )}
                  </div>

                  {/* Table Rows Table */}
                  <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '420px' }}>
                    {loadingTableData ? (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '0.88rem' }}>
                        <RefreshCw size={24} className="spin" style={{ marginBottom: '8px' }} />
                        <div>Loading records from {selectedDbTable}...</div>
                      </div>
                    ) : (() => {
                      const filteredRows = dbTableData.rows.filter((r) => {
                        if (!dbTableFilter.trim()) return true;
                        const query = dbTableFilter.toLowerCase();
                        return Object.values(r).some((val) => String(val || '').toLowerCase().includes(query));
                      });

                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.78rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                              {dbTableData.columns.map((col) => (
                                <th key={col} style={{ padding: '10px 12px', fontWeight: 700, borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRows.length === 0 ? (
                              <tr>
                                <td colSpan={dbTableData.columns.length || 1} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                                  No records found in table "{selectedDbTable}".
                                </td>
                              </tr>
                            ) : (
                              filteredRows.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                                  {dbTableData.columns.map((col) => (
                                    <td key={col} style={{ padding: '8px 12px', borderRight: '1px solid #f1f5f9', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: typeof row[col] === 'object' ? 'monospace' : 'inherit' }}>
                                      {typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] ?? 'NULL')}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>

                  {/* Pagination Footer */}
                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#64748b' }}>
                    <div>
                      Showing {dbTableData.rows.length} of {dbTableData.totalRows} records in <strong>{selectedDbTable}</strong>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => { const p = Math.max(1, dbTablePage - 1); setDbTablePage(p); fetchDbTableData(selectedDbTable, p, dbTableLimit); }}
                        disabled={dbTablePage <= 1}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: dbTablePage <= 1 ? '#f8fafc' : '#ffffff', cursor: dbTablePage <= 1 ? 'not-allowed' : 'pointer' }}
                      >
                        ◀ Prev
                      </button>
                      <span>Page {dbTablePage}</span>
                      <button
                        onClick={() => { const p = dbTablePage + 1; setDbTablePage(p); fetchDbTableData(selectedDbTable, p, dbTableLimit); }}
                        disabled={dbTablePage * dbTableLimit >= dbTableData.totalRows}
                        style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: dbTablePage * dbTableLimit >= dbTableData.totalRows ? '#f8fafc' : '#ffffff', cursor: dbTablePage * dbTableLimit >= dbTableData.totalRows ? 'not-allowed' : 'pointer' }}
                      >
                        Next ▶
                      </button>
                    </div>
                  </div>
                </div>

                {/* 2. Read-Only SQL Console Card */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Code size={20} color="#2563eb" />
                      <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>Read-Only SQL Query Console</h3>
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', padding: '3px 8px', borderRadius: '12px' }}>
                      🔒 READ-ONLY GATED (SELECT / PRAGMA ONLY)
                    </span>
                  </div>

                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                    Execute arbitrary read-only SQL queries against Cloudflare D1. Write operations (<code>INSERT</code>, <code>UPDATE</code>, <code>DELETE</code>, <code>DROP</code>, etc.) are strictly forbidden and rejected server-side.
                  </p>

                  {/* Sample Query Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', alignSelf: 'center', fontWeight: 600 }}>Quick Queries:</span>
                    {[
                      'SELECT * FROM user_payments LIMIT 50;',
                      'SELECT * FROM nfl_games WHERE week = 1;',
                      'SELECT * FROM picks ORDER BY created_at DESC LIMIT 50;',
                      'SELECT * FROM allowlist;',
                      'PRAGMA table_info("user_payments");',
                    ].map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setSqlQuery(q)}
                        style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '3px 8px', fontSize: '0.72rem', fontFamily: 'monospace', color: '#2563eb', cursor: 'pointer' }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>

                  {/* SQL Textarea Input */}
                  <textarea
                    rows={4}
                    value={sqlQuery}
                    onChange={(e) => setSqlQuery(e.target.value)}
                    placeholder="SELECT * FROM user_payments LIMIT 50;"
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', fontFamily: 'monospace', outline: 'none', backgroundColor: '#0f172a', color: '#38bdf8', marginBottom: '12px' }}
                  />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      onClick={handleExecuteReadonlySql}
                      disabled={executingSql || !sqlQuery.trim()}
                      style={{
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        padding: '10px 18px',
                        borderRadius: '8px',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <RefreshCw size={14} className={executingSql ? 'spin' : ''} />
                      <span>{executingSql ? 'Executing Query...' : '▶️ Run Read-Only Query'}</span>
                    </button>

                    {sqlResult && !sqlResult.error && (
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(sqlResult.rows, null, 2));
                          setToast({ type: 'success', text: 'Copied SQL results JSON to clipboard!' });
                        }}
                        style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                      >
                        📋 Copy Results JSON
                      </button>
                    )}
                  </div>

                  {/* SQL Results Render */}
                  {sqlResult && (
                    <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                      {sqlResult.error ? (
                        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '0.82rem', fontWeight: 600 }}>
                          ⚠️ {sqlResult.error}
                        </div>
                      ) : (
                        <div>
                          <div style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>✓ Query executed successfully!</span>
                            <span style={{ color: '#64748b', fontWeight: 400 }}>({sqlResult.rowCount} rows returned in {sqlResult.durationMs}ms)</span>
                          </div>

                          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', maxHeight: '360px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.78rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                                  {sqlResult.columns.map((col) => (
                                    <th key={col} style={{ padding: '8px 12px', fontWeight: 700, borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sqlResult.rows.map((row, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }}>
                                    {sqlResult.columns.map((col) => (
                                      <td key={col} style={{ padding: '6px 12px', borderRight: '1px solid #f1f5f9', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] ?? 'NULL')}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>

              </div>
            )}

            {/* SUB-PAGE 4: SYSTEM DIAGNOSTICS */}
            {siteAdminSubTab === 'diagnostics' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                
                {/* Cloudflare Infrastructure Health Card */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '1rem', color: '#0f172a' }}>
                      <Activity size={18} color="#2563eb" />
                      <span>Cloudflare Health</span>
                    </div>
                    <button onClick={fetchHealth} disabled={loadingHealth} style={{ backgroundColor: 'transparent', color: '#64748b', border: 'none', cursor: 'pointer' }}>
                      <RefreshCw size={14} className={loadingHealth ? 'spin' : ''} />
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#64748b' }}>Workers API</span>
                      <span style={{ color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle2 size={14} /> Online
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Database size={14} /> Cloudflare D1
                      </span>
                      <span style={{ color: health?.cloudflare.d1_status === 'connected' ? '#16a34a' : '#d97706', fontWeight: 600 }}>
                        {health?.cloudflare.d1_status || 'Checking...'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Mail size={14} /> Email Gateway
                      </span>
                      <span style={{ color: '#0f172a', fontSize: '0.78rem', fontWeight: 500 }}>
                        {health?.cloudflare.email_gateway || 'Development mode'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Test Actions & DB Inspector */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a' }}>
                    <Database size={18} color="#2563eb" />
                    <span>Test Suite & D1 Inspection</span>
                  </h3>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                    <button
                      onClick={handleTestEmail}
                      disabled={emailTestLoading}
                      style={{
                        backgroundColor: '#2563eb',
                        color: '#fff',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <Mail size={16} />
                      Send Pick Reminder Email
                    </button>

                    <button
                      onClick={handleInspectDb}
                      disabled={loadingDb}
                      style={{
                        backgroundColor: '#f8fafc',
                        color: '#0f172a',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: '1px solid #cbd5e1',
                        cursor: 'pointer'
                      }}
                    >
                      <Database size={16} />
                      Inspect D1 Records
                    </button>
                  </div>

                  {emailTestStatus && (
                    <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', padding: '12px', borderRadius: '8px', fontSize: '0.82rem', color: '#15803d', marginBottom: '16px' }}>
                      {emailTestStatus}
                    </div>
                  )}

                  {dbData && (
                    <div style={{ backgroundColor: '#0f172a', borderRadius: '8px', padding: '16px', border: '1px solid #334155' }}>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '10px' }}>
                        D1 Database Status: <strong style={{ color: '#38bdf8' }}>{dbData.database}</strong>
                      </div>
                      <pre style={{ fontSize: '0.78rem', color: '#38bdf8', overflowX: 'auto', fontFamily: 'monospace', maxHeight: '250px' }}>
                        {JSON.stringify(dbData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>

              </div>
            )}

          </div>
        )}

      {/* Admin Edit User Display Name Modal */}
      {editingUserName && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', maxWidth: '440px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✏️ Edit Standings Display Name</span>
              </h3>
              <button onClick={() => setEditingUserName(null)} style={{ backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem', fontWeight: 700 }}>✕</button>
            </div>

            <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '16px' }}>
              Update the standings display name for <strong>{editingUserName.email}</strong>. This name will update across the Group Pick Matrix and Financial Ledger.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '6px' }}>Display Name</label>
              <input
                type="text"
                value={newNameInput}
                onChange={(e) => setNewNameInput(e.target.value)}
                placeholder="Enter new display name..."
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setEditingUserName(null)}
                style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '9px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewName}
                disabled={savingNewName || !newNameInput.trim()}
                style={{ backgroundColor: '#2563eb', border: 'none', color: '#ffffff', padding: '9px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)' }}
              >
                {savingNewName ? 'Saving...' : '💾 Save Display Name'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Delete User Confirmation Modal */}
      {deletingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', maxWidth: '460px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🗑️ Confirm User Deletion</span>
              </h3>
              <button onClick={() => setDeletingUser(null)} style={{ backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem', fontWeight: 700 }}>✕</button>
            </div>

            <p style={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.5, marginBottom: '14px' }}>
              Are you sure you want to permanently delete the user record for <strong>"{deletingUser.name}"</strong> (<code>{deletingUser.email}</code>)?
            </p>

            <p style={{ fontSize: '0.8rem', color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '10px 12px', borderRadius: '8px', marginBottom: '16px', lineHeight: 1.4 }}>
              ⚠️ <strong>Warning:</strong> This will delete their account details, magic links, pool memberships, and picks from the system.
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#334155', fontWeight: 600, cursor: 'pointer', marginBottom: '20px', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={notifyUserOnDelete}
                onChange={(e) => setNotifyUserOnDelete(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#dc2626' }}
              />
              <span>Send notification email to user regarding account deletion</span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                disabled={isDeletingUser}
                onClick={() => setDeletingUser(null)}
                style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '9px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                disabled={isDeletingUser}
                onClick={handleDeleteUserSubmit}
                style={{ backgroundColor: '#dc2626', border: 'none', color: '#ffffff', padding: '9px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.25)' }}
              >
                {isDeletingUser ? 'Deleting...' : '🗑️ Yes, Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE NEW POOL MODAL OVERLAY ────────────────────────────────────── */}
      {showCreatePoolModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            padding: '28px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🏈 Create New Survivor Pool</span>
              </h3>
              <button
                onClick={() => setShowCreatePoolModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreatePoolSubmit}>
              <div style={{ marginBottom: '18px', display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Pool Name:
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Cote Family Survivor 2026"
                    value={newPoolName}
                    onChange={(e) => setNewPoolName(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', fontWeight: 600 }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Season Year:
                  </label>
                  <select
                    value={newPoolSeason}
                    onChange={(e) => setNewPoolSeason(Number(e.target.value))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', fontWeight: 700, backgroundColor: '#ffffff', color: '#0f172a' }}
                  >
                    {[2026, 2027, 2028].map((y) => (
                      <option key={y} value={y}>{y} Season</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                  Pool Mode:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div
                    onClick={() => setNewPoolType('live')}
                    style={{
                      border: `2px solid ${newPoolType === 'live' ? '#16a34a' : '#e2e8f0'}`,
                      backgroundColor: newPoolType === 'live' ? '#f0fdf4' : '#ffffff',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#15803d', fontSize: '0.9rem' }}>🏆 Live Actual</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>Real ESPN Live Syncing & Protected Ledger</div>
                  </div>

                  <div
                    onClick={() => setNewPoolType('simulation')}
                    style={{
                      border: `2px solid ${newPoolType === 'simulation' ? '#0284c7' : '#e2e8f0'}`,
                      backgroundColor: newPoolType === 'simulation' ? '#f0f9ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#0369a1', fontSize: '0.9rem' }}>🧪 Simulation</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>Includes Game Day Simulator & 1-Click Pool Reset</div>
                  </div>
                </div>
              </div>

              {/* 🏆 SELECTABLE BASE RULE PRESETS */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                  Select Base Rule Seed:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  
                  {/* Preset 1: Strict Traditional */}
                  <div
                    onClick={() => applyRulePreset('strict_traditional')}
                    style={{
                      border: `2px solid ${selectedRulePreset === 'strict_traditional' ? '#2563eb' : '#e2e8f0'}`,
                      backgroundColor: selectedRulePreset === 'strict_traditional' ? '#eff6ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '14px 10px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#1d4ed8', fontSize: '0.86rem' }}>🛡️ Strict Traditional</div>
                  </div>

                  {/* Preset 2: Flexible / Late-Join */}
                  <div
                    onClick={() => applyRulePreset('flexible_late')}
                    style={{
                      border: `2px solid ${selectedRulePreset === 'flexible_late' ? '#0284c7' : '#e2e8f0'}`,
                      backgroundColor: selectedRulePreset === 'flexible_late' ? '#f0f9ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '14px 10px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#0369a1', fontSize: '0.86rem' }}>🎈 Flexible / Late-Join</div>
                  </div>

                  {/* Preset 3: Auto-Favorite Safety Net */}
                  <div
                    onClick={() => applyRulePreset('auto_favorite')}
                    style={{
                      border: `2px solid ${selectedRulePreset === 'auto_favorite' ? '#059669' : '#e2e8f0'}`,
                      backgroundColor: selectedRulePreset === 'auto_favorite' ? '#ecfdf5' : '#ffffff',
                      borderRadius: '10px',
                      padding: '14px 10px',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: '#047857', fontSize: '0.86rem' }}>🤖 Auto-Favorite Net</div>
                  </div>
                </div>
                <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '8px', fontStyle: 'italic', textAlign: 'center' }}>
                  💡 Rules can be fully customized in Pool Settings once the pool is created.
                </div>
              </div>

              {/* Commissioner Payment Instructions Field */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Commissioner Payment Instructions & Handles (Optional):
                </label>
                <input
                  type="text"
                  placeholder="e.g. Venmo @YourHandle or Zelle 555-0199"
                  value={newPoolPaymentInstructions}
                  onChange={(e) => setNewPoolPaymentInstructions(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.88rem', fontWeight: 600 }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={addJasonJohn}
                    onChange={(e) => setAddJasonJohn(e.target.checked)}
                  />
                  <span>Automatically add Jason Cote & John Bressler as Pool Admins</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreatePoolModal(false)}
                  style={{ backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingPool}
                  style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  {isCreatingPool ? 'Creating Pool...' : '🚀 Create Pool Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── 🗑️ Double Failsafe Delete Pool Modal ────────────────────────────── */}
      {showDeletePoolModal && activePool && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            maxWidth: '520px',
            width: '100%',
            padding: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            border: '2px solid #ef4444'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#dc2626', marginBottom: '12px' }}>
              <AlertTriangle size={24} color="#dc2626" />
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
                ⚠️ Permanent Pool Deletion Failsafe
              </h2>
            </div>

            <p style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.5, marginBottom: '16px' }}>
              You are about to permanently delete the pool <strong>"{activePool.name}"</strong> (ID: <code style={{ backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{activePool.id}</code>).
            </p>

            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem', color: '#991b1b', lineHeight: 1.5 }}>
                <li>All submitted user picks for this pool will be deleted.</li>
                <li>All member roster assignments and roles for this pool will be wiped.</li>
                <li>All financial payment ledgers and buyback logs will be destroyed.</li>
                <li><strong>This action is permanent and CANNOT be undone.</strong></li>
              </ul>
            </div>

            <form onSubmit={handleDeletePoolSubmit}>
              <div style={{ marginBottom: '24px', backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', cursor: 'pointer', lineHeight: 1.4 }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: '3px', width: '16px', height: '16px' }}
                    checked={deleteFailsafeChecked}
                    onChange={(e) => setDeleteFailsafeChecked(e.target.checked)}
                  />
                  <span>I confirm that I understand this pool will be permanently destroyed and cannot be recovered.</span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeletePoolModal(false);
                    setDeleteFailsafeChecked(false);
                  }}
                  style={{ backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={!deleteFailsafeChecked || isDeletingPool}
                  style={{
                    backgroundColor: !deleteFailsafeChecked ? '#94a3b8' : '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontWeight: 800,
                    cursor: !deleteFailsafeChecked ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: !deleteFailsafeChecked ? 'none' : '0 2px 6px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  <Trash2 size={16} />
                  <span>{isDeletingPool ? 'Deleting Pool...' : '🗑️ Permanently Delete Pool'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 📝 PAYMENT NOTE MODAL */}
      {showPaymentNoteModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            maxWidth: '480px',
            width: '100%',
            padding: '28px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>💳 Submit Payment Memo / Handle</span>
              </h3>
              <button onClick={() => setShowPaymentNoteModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <p style={{ fontSize: '0.86rem', color: '#64748b', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              Provide your Venmo username, Zelle phone number/email, or transaction memo so your Pool Administrator can reconcile and approve your payment.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>
                Payment Handle or Transaction Memo:
              </label>
              <input
                type="text"
                placeholder="e.g. Sent $100 via Venmo @JasonCote or Zelle 555-123-4567"
                value={paymentNoteInput}
                onChange={(e) => setPaymentNoteInput(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', color: '#0f172a' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPaymentNoteModal(false)}
                style={{ backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSavePaymentNote}
                disabled={savingPaymentNote}
                style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}
              >
                {savingPaymentNote ? 'Saving...' : 'Submit Note to Admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 POOL LAUNCH & RULE SUMMARY CONFIRMATION MODAL */}
      {showPoolLaunchModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #cbd5e1',
            maxWidth: '640px', width: '100%', padding: '24px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1d4ed8', marginBottom: '6px' }}>
              <Rocket size={26} color="#2563eb" />
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                Confirm Pool Rules & Launch Pool
              </h2>
            </div>

            <p style={{ fontSize: '0.86rem', color: '#475569', lineHeight: 1.5, margin: '0 0 16px 0' }}>
              Please review the official rule configuration for <strong>"{adminSettings.pool_name || activePool?.name}"</strong> before launching. Once launched into <code>active</code> status, rules lock and registrations open to all members.
            </p>

            {/* Rules Summary Card Grid */}
            <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', fontSize: '0.84rem' }}>
                
                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Pool Name</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>{adminSettings.pool_name || activePool?.name || 'Survivor Pool'}</div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>First Scoring Week</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>Week {adminSettings.season_start_week || 1}</div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Registration Lock</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>
                    {adminSettings.registration_lock_week > 1 ? `Week ${adminSettings.registration_lock_week} (Late Join Open)` : 'Week 1 Kickoff (Hard Lock)'}
                  </div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Pick Lock Rule</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>
                    {adminSettings.pick_lock_rule === 'thursday_kickoff' && 'Start of Thursday Night Football'}
                    {adminSettings.pick_lock_rule === 'saturday_kickoff' && 'Start of Saturday Games'}
                    {adminSettings.pick_lock_rule === 'sunday_early' && 'Start of Sunday Early Games (1 PM ET)'}
                    {adminSettings.pick_lock_rule === 'individual_kickoff' && 'Individual Game Kickoffs'}
                  </div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Pick Opening Window</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>
                    {adminSettings.pick_opening_rule === 'weekly_rolling' ? 'Weekly Rolling Window (Tuesday 3 AM ET)' : 'Advance Multi-Week'}
                  </div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Unmade Pick Policy</div>
                  <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem', marginTop: '2px' }}>
                    {adminSettings.unmade_pick_policy === 'policy_a_ghost' && 'Hard Elimination (No Buy-Back)'}
                    {adminSettings.unmade_pick_policy === 'policy_b_buyback' && 'Forgiving Elimination (Buy-Back Allowed)'}
                    {adminSettings.unmade_pick_policy === 'policy_b_favorite' && 'Auto-Pick Favorite (Burns Team)'}
                  </div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Entry Buy-In Fee</div>
                  <div style={{ fontWeight: 800, color: '#16a34a', fontSize: '0.92rem', marginTop: '2px' }}>${adminSettings.buy_in_cost || 0}</div>
                </div>

                <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Buy-Back Policy</div>
                  <div style={{ fontWeight: 800, color: '#16a34a', fontSize: '0.92rem', marginTop: '2px' }}>
                    {adminSettings.max_buy_backs > 0
                      ? `${adminSettings.max_buy_backs} Max @ $${adminSettings.buy_back_cost} (Through W${adminSettings.buy_back_max_week})`
                      : 'No Buy-Backs Allowed'}
                  </div>
                </div>

              </div>

              {/* Payment Notes line */}
              <div style={{ backgroundColor: '#ffffff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '4px' }}>
                <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>Commissioner Payment Notes / Instructions</div>
                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.86rem', marginTop: '2px' }}>
                  {adminSettings.payment_instructions ? adminSettings.payment_instructions : 'None set (contact commissioner directly)'}
                </div>
              </div>
            </div>

            {/* Broadcast email checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px',
              backgroundColor: syncNotifyEmail ? '#f0fdf4' : '#f8fafc',
              border: syncNotifyEmail ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
              borderRadius: '12px', cursor: 'pointer', marginBottom: '20px', transition: 'all 0.2s'
            }}>
              <input
                type="checkbox"
                checked={syncNotifyEmail}
                onChange={(e) => setSyncNotifyEmail(e.target.checked)}
                style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: '#16a34a', cursor: 'pointer' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: syncNotifyEmail ? '#15803d' : '#334155' }}>
                  Broadcast pool launch email & rule summary to all enrolled members
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                  Sends an automated launch email with official rules and invitation link to registered players.
                </div>
              </div>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowPoolLaunchModal(false)}
                disabled={launchingPool || syncingSchedule}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
              >
                ← Edit Rules
              </button>

              <button
                onClick={() => {
                  setShowPoolLaunchModal(false);
                  handleSyncSchedule(true, syncNotifyEmail);
                }}
                disabled={launchingPool || syncingSchedule}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#2563eb', color: '#ffffff', fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                }}
              >
                <Rocket size={18} />
                <span>{launchingPool ? 'Launching Pool...' : '🚀 Confirm Rules & Launch Pool'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Sync Confirmation & Broadcast Email Modal */}
      {showScheduleSyncModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '28px',
            maxWidth: '500px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', display: 'flex',
            flexDirection: 'column', gap: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: '#eff6ff', color: '#2563eb' }}>
                <RefreshCw size={24} className={syncingSchedule || launchingPool ? 'spin' : ''} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                  Sync Full Season NFL Schedule
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                  Weeks 1 – 18 Regular Season Schedule
                </span>
              </div>
            </div>

            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', fontSize: '0.86rem', color: '#334155', lineHeight: 1.5 }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 700, color: '#0f172a' }}>
                📅 Schedule Update Summary:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li>Overwrites stale/expired games across all 18 weeks with live 2026 ESPN data.</li>
                <li>Updates kickoff timestamps, spreads, venue details, and game statuses.</li>
                <li>Preserves all locked player picks and pool standings.</li>
              </ul>
            </div>

            {/* Email Notification Option (Default Checked) */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px',
              backgroundColor: syncNotifyEmail ? '#f0fdf4' : '#f8fafc',
              border: syncNotifyEmail ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
              borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'
            }}>
              <input
                type="checkbox"
                checked={syncNotifyEmail}
                onChange={(e) => setSyncNotifyEmail(e.target.checked)}
                style={{ marginTop: '3px', width: '18px', height: '18px', accentColor: '#16a34a', cursor: 'pointer' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: syncNotifyEmail ? '#15803d' : '#334155' }}>
                  📩 Send pool-wide email notification to all players
                </div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                  Broadcast an automated email to notify members of updated schedule and lock times. (Recommended)
                </div>
              </div>
            </label>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
              <button
                onClick={() => setShowScheduleSyncModal(false)}
                disabled={syncingSchedule || launchingPool}
                style={{ backgroundColor: '#f1f5f9', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleSyncSchedule(true, syncNotifyEmail)}
                disabled={syncingSchedule || launchingPool}
                style={{
                  backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '10px 20px',
                  borderRadius: '8px', fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                }}
              >
                <RefreshCw size={16} className={syncingSchedule || launchingPool ? 'spin' : ''} />
                <span>{syncingSchedule || launchingPool ? 'Syncing 18 Weeks...' : syncNotifyEmail ? '🚀 Sync Schedule & Send Email' : '🚀 Sync Schedule Only'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      </main>
    </div>
  );
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary caught error]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
          <div style={{ maxWidth: '480px', width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '16px', backgroundColor: '#fef2f2', marginBottom: '16px' }}>
              <AlertCircle size={36} color="#dc2626" />
            </div>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
              Application Render Exception
            </h2>
            <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.5, marginBottom: '20px' }}>
              An unexpected render error occurred: <br />
              <code style={{ color: '#dc2626', backgroundColor: '#fef2f2', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem' }}>
                {this.state.error?.message || 'Unknown render exception'}
              </code>
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ backgroundColor: '#2563eb', color: '#ffffff', padding: '12px 24px', borderRadius: '10px', fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)' }}
            >
              🔄 Reload Application
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
